import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Group,
    Loader,
    Menu,
    Modal,
    MultiSelect,
    NumberInput,
    Paper,
    Popover,
    SegmentedControl,
    Select,
    Stack,
    Switch,
    TagsInput,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
    ArrowCounterClockwise,
    ArrowLeft,
    ArrowRight,
    CalendarPlus,
    Checks,
    CheckCircle,
    ChartLine,
    DotsThreeVertical,
    DownloadSimple,
    EnvelopeSimple,
    Equals,
    GearSix,
    Package,
    PencilSimple,
    Rows,
    ShoppingCart,
    Trash,
    TrendUp,
    UploadSimple,
    WarningCircle,
} from '@phosphor-icons/react';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from 'react';

import ScheduleAlertDialog from '@components/ui/ScheduleAlertDialog/ScheduleAlertDialog';
import type { CreateScheduledAlertInput } from '@api/userAlerts';

import {
    computeDistributionPlan,
    deleteDistributionPlan,
    getChannelConfig,
    getChannelHealth,
    getChannelPastSales,
    getChannelProductMappings,
    getChannelWarehouseOptions,
    getDistributionPlan,
    getDistributionPaths,
    getPlanEmailPreview,
    getPlanShipments,
    listWarehouseDohRules,
    previewDistributionTransfers,
    renameDistributionPlan,
    saveDistributionTransfers,
    sendPlanEmail,
    type PlanSettingsOverride,
    type SaveTransferEdit,
    type PreviewTransferLine,
    type PlanEmailPreview,
    type PlanShipment,
    type ChannelProductMapping,
    type DistributionSkuHealth,
    type DistributionPlanDetail,
    type DistributionTransferRow,
    type DistributionTransferStatus,
    type WarehouseDohRule,
} from '@api/distribution';
import { getB2bPoLines, type B2bPoLine } from '@api/b2bPo';
import { usePlanEdits } from '@hooks/usePlanEdits';
import usePersistentState from '@hooks/usePersistentState';
import DataSheet, {
    type DataSheetColumn,
    type DataSheetFilterConfig,
} from '@components/ui/DataSheet';
import PageHeader from '@components/common/PageHeader';
import { ApprovePlanModal } from './ApprovePlanModal';
import {
    CreateSaleOrdersModal,
    ShipmentStatusBadge,
} from './CreateSaleOrdersModal';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import { useProductFilterParams } from '@hooks/useProductFilterParams';
import { neutralColors, semanticColors } from '@styles/design-system';
import { showError, showSuccess } from '@utils/notifications';

import styles from './ChannelDetailView.module.css';
import { rowSendingValue, rowSendQty, slotEditKey, valueOfQty } from './planRowQty';
import { formatCount, formatMoney } from '@utils/currency';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface PlanComputeParams {
    channelId: string;
    channelName: string;
    channelAbbreviationKey: string;
    demandSource: DemandSource;
    targetDohDays: number | null;
    channelTierTargetDohDays?: Record<string, number> | null;
    isAggregated: boolean;
    /** PO-driven flow: compute this new plan against a specific open PO (demandSource = OPEN_PO). */
    purchaseOrderId?: string | null;
    /** Pre-select these destination FCs in the settings page (e.g. a PO-driven per-FC plan's FC). */
    preselectedWarehouseIds?: string[] | null;
    /** Restrict the compute to these SKUs — the channel-understock alert's deep link. Null = whole channel. */
    productIds?: string[] | null;
    /** Compute immediately with the channel's saved settings instead of stopping on the settings page (alert deep link). */
    autoCompute?: boolean;
    /** Settings pinned by the alert that opened this plan — seed the settings form so the computed plan matches the alert. */
    planSettings?: PlanSettingsOverride | null;
}

interface Props {
    params?: PlanComputeParams;
    existingPlan?: DistributionPlanDetail;
    /** Pass just the planId for immediate navigation — plan view fetches it internally */
    existingPlanId?: string;
    /** The Home card (checklist item id) this plan was opened from, when it was. */
    fromAlertId?: string;
    /** Plan name shown in the loader while fetching by existingPlanId */
    existingPlanName?: string;
    /** Preselect the shipment (sale order code) filter on open — used by the Track table row click. */
    initialShipmentCode?: string;
    /** Tier DoH config from the channel, for existing-plan navigation path */
    channelTierTargetDohDays?: Record<string, number> | null;
    /** Channel-level defaults shown as placeholder hints in plan settings */
    channelDefaultMinSourceStockUnits?: number | null;
    channelDefaultMaxOutboundUtilizationPct?: number | null;
    channelDefaultInboundInTransitDays?: number | null;
    channelDefaultSourceReserveUnits?: number | null;
    channelDefaultMinInternalDohDays?: number | null;
    channelDefaultMilestone1Days?: number | null;
    channelDefaultMilestone2Days?: number | null;
    channelDefaultMaxUnitsPerShipment?: number | null;
    channelDefaultMaxMeasurementPerShipment?: number | null;
    channelMeasurementUnit?: 'weight' | 'volume' | null;
    onBack: () => void;
    onSaveDraft: () => Promise<void>;
    onDelete?: () => void;
}

type ViewState = 'settings' | 'computing' | 'plan' | 'error';
type DemandSource =
    | 'FORECAST'
    | 'PAST_SALES'
    | 'OPEN_PO'
    | 'MAX_OF_FORECAST_PAST_SALES';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DEMAND_SOURCE_OPTIONS: {
    value: DemandSource;
    label: string;
    description: string;
    icon: FC<{ size: number }>;
}[] = [
    {
        value: 'FORECAST',
        label: 'Forecast',
        description: 'Use uploaded forecast',
        icon: TrendUp,
    },
    {
        value: 'PAST_SALES',
        label: 'Past Sales',
        description: 'Rolling average of actuals',
        icon: ChartLine,
    },
    {
        value: 'MAX_OF_FORECAST_PAST_SALES',
        label: 'Max of Forecast / Past Sales',
        description: 'Higher of forecast or DRR-based demand',
        icon: TrendUp,
    },
    {
        value: 'OPEN_PO',
        label: 'Open POs',
        description: 'Pending order quantities',
        icon: ShoppingCart,
    },
];

// ─── Plan-level rule group types ───────────────────────────────────────────────

interface PlanRuleGroup {
    warehouseIds: string[];
    dohMode: 'flat' | 'tier';
    defaultFlat: number | '';
    defaultTierDoh: Record<string, number | ''>;
}

interface PlanRuleGroupOverride {
    flat: number | '';
    /** Per-tier DoH override for this group's stores, for this plan run only (not persisted). */
    tierDoh?: Record<string, number | ''>;
}

function buildPlanRuleGroups(rules: WarehouseDohRule[]): PlanRuleGroup[] {
    const groupMap = new Map<
        string,
        { warehouseIds: string[]; rule: WarehouseDohRule }
    >();
    for (const rule of rules) {
        const tierSorted = rule.tierTargetDohDays
            ? Object.fromEntries(Object.entries(rule.tierTargetDohDays).sort())
            : null;
        const key = JSON.stringify({ t: rule.targetDohDays, tier: tierSorted });
        const entry = groupMap.get(key);
        if (entry) entry.warehouseIds.push(rule.warehouseId);
        else groupMap.set(key, { warehouseIds: [rule.warehouseId], rule });
    }
    return Array.from(groupMap.values()).map((g) => ({
        warehouseIds: g.warehouseIds,
        dohMode: g.rule.tierTargetDohDays ? 'tier' : 'flat',
        defaultFlat: g.rule.targetDohDays ?? '',
        defaultTierDoh: Object.fromEntries(
            Object.entries(g.rule.tierTargetDohDays ?? {}).map(([k, v]) => [
                k,
                (v as number | null) ?? '',
            ]),
        ),
    }));
}

const planRuleGroupKey = (g: PlanRuleGroup) =>
    g.warehouseIds.slice().sort().join(',');

// ─── Section title helper ───────────────────────────────────────────────────────

const PlanSectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text
        style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: neutralColors.textMuted,
            marginBottom: 6,
        }}
    >
        {children}
    </Text>
);

const TRANSFER_STATUS_COLOR: Record<DistributionTransferStatus, string> = {
    PENDING: neutralColors.textMuted,
    APPROVED: semanticColors.positive.primary,
    REJECTED: semanticColors.critical.primary,
};

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

type EmailDraft = PlanEmailPreview & { sending: boolean; sent: boolean };

// ─── Confirm-on-leave modal ────────────────────────────────────────────────────

function openLeaveConfirm(onSaveDraft: () => Promise<void>) {
    modals.open({
        title: 'Leave this plan?',
        size: 'sm',
        children: (
            <Stack gap={16}>
                <Text
                    style={{ fontSize: 14, color: neutralColors.textSecondary }}
                >
                    Your draft will be saved. You can return to it from the
                    Plans tab.
                </Text>
                <Group justify="flex-end" gap={8}>
                    <Button
                        variant="subtle"
                        color="dark"
                        size="sm"
                        onClick={() => modals.closeAll()}
                    >
                        Keep editing
                    </Button>
                    <Button
                        size="sm"
                        color="violet"
                        onClick={async () => {
                            await onSaveDraft();
                            modals.closeAll();
                        }}
                    >
                        Save draft
                    </Button>
                </Group>
            </Stack>
        ),
    });
}

// ─── DRR control panel ────────────────────────────────────────────────────────

/** How the past-sales rate is summed: a rolling "last N days" or an operator-picked date range. */
type DrrWindowMode = 'rolling' | 'range';

const formatDrrDate = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
    });
};

/** Inclusive day count of a yyyy-MM-dd window; null while the range is incomplete. */
const drrWindowDays = (start: string | null, end: string | null): number | null => {
    if (!start || !end) return null;
    const ms = Date.UTC(
        Number(end.slice(0, 4)),
        Number(end.slice(5, 7)) - 1,
        Number(end.slice(8, 10)),
    ) - Date.UTC(
        Number(start.slice(0, 4)),
        Number(start.slice(5, 7)) - 1,
        Number(start.slice(8, 10)),
    );
    return Math.round(ms / 86_400_000) + 1;
};

interface DrrControlSectionProps {
    lookbackDays: number | null;
    windowStart: string | null;
    windowEnd: string | null;
    growthPct: number | null;
    onLookbackChange: (v: number | null) => void;
    onWindowChange: (start: string | null, end: string | null) => void;
    onGrowthChange: (v: number | null) => void;
}

/**
 * The DRR control panel: pick the period the run-rate is measured over (rolling lookback or an
 * explicit date range such as a promo week) and a static growth % layered on top. The effective
 * rate the compute uses is `(1 + growth%) × units sold in period ÷ days in period`.
 */
const DrrControlSection: FC<DrrControlSectionProps> = ({
    lookbackDays,
    windowStart,
    windowEnd,
    growthPct,
    onLookbackChange,
    onWindowChange,
    onGrowthChange,
}) => {
    // A half-picked range (start only) still counts as range mode so the picker stays open.
    const [mode, setMode] = useState<DrrWindowMode>(
        windowStart || windowEnd ? 'range' : 'rolling',
    );
    const days = drrWindowDays(windowStart, windowEnd);
    const periodLabel =
        mode === 'range'
            ? windowStart && windowEnd
                ? `${formatDrrDate(windowStart)} – ${formatDrrDate(windowEnd)} (${days}d)`
                : 'pick a date range'
            : `last ${lookbackDays ?? 30}d`;
    const growthLabel =
        growthPct != null && growthPct !== 0
            ? ` × ${(1 + growthPct / 100).toFixed(2)}`
            : '';

    return (
        <div>
            <PlanSectionTitle>DRR Control</PlanSectionTitle>
            <Text
                style={{
                    fontSize: 11,
                    color: neutralColors.textMuted,
                    marginBottom: 12,
                }}
            >
                Period the daily run rate is measured over, plus an optional
                growth uplift. Effective DRR = (1 + growth%) × sales in period
                ÷ days in period.
            </Text>
            <Stack gap={10}>
                <SegmentedControl
                    size="xs"
                    fullWidth
                    value={mode}
                    onChange={(v) => {
                        const next = v as DrrWindowMode;
                        setMode(next);
                        if (next === 'rolling') onWindowChange(null, null);
                    }}
                    data={[
                        { value: 'rolling', label: 'Last N days' },
                        { value: 'range', label: 'Date range' },
                    ]}
                />
                {mode === 'rolling' ? (
                    <NumberInput
                        label="DRR lookback window"
                        description="Rolling window for calculating daily run rate (average daily sales used as demand signal)"
                        placeholder="30d"
                        value={lookbackDays ?? ''}
                        onChange={(v) =>
                            onLookbackChange(v === '' ? null : Number(v))
                        }
                        min={7}
                        max={365}
                        rightSection={
                            <Text
                                style={{
                                    fontSize: 10,
                                    color: neutralColors.textMuted,
                                    paddingRight: 4,
                                }}
                            >
                                d
                            </Text>
                        }
                        rightSectionWidth={20}
                        hideControls
                        size="sm"
                    />
                ) : (
                    <DatePickerInput
                        type="range"
                        label="DRR sales period"
                        description="Sales are summed over this inclusive range and divided by its day count — e.g. 18 Aug → 25 Aug is an 8-day rate"
                        placeholder="Pick start and end"
                        valueFormat="DD MMM YYYY"
                        value={[windowStart, windowEnd]}
                        onChange={([s, e]) => onWindowChange(s ?? null, e ?? null)}
                        maxDate={new Date()}
                        allowSingleDateInRange
                        clearable
                        size="sm"
                    />
                )}
                <NumberInput
                    label="Growth rate"
                    description="Static uplift applied to the DRR: +10 turns a 20/day rate into 22/day. Negative values shrink it."
                    placeholder="0"
                    value={growthPct ?? ''}
                    onChange={(v) => onGrowthChange(v === '' ? null : Number(v))}
                    min={-100}
                    max={1000}
                    step={5}
                    decimalScale={1}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            %
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <Text
                    style={{
                        fontSize: 11,
                        color: neutralColors.textSecondary,
                        fontFamily: 'monospace',
                    }}
                >
                    DRR = sales({periodLabel}) ÷ days{growthLabel}
                </Text>
            </Stack>
        </div>
    );
};

// ─── Settings form (shared between full-page and modal) ───────────────────────

interface SettingsFormProps {
    channelName: string;
    channelSupportsOpenPo: boolean;
    demandSource: DemandSource;
    /** PO this OPEN_PO plan is scoped to — shown under the demand signal as a trace. */
    purchaseOrderId?: string | null;
    /** SKUs this run is scoped to (alert deep link) — shown under the demand signal as a trace. */
    scopedProductCount?: number | null;
    targetDohDays: number | null;
    milestone1DaysOverride: number | null;
    milestone2DaysOverride: number | null;
    drrLookbackDays: number | null;
    /** Explicit DRR window (yyyy-MM-dd, inclusive). Both null = rolling lookback. */
    drrWindowStart: string | null;
    drrWindowEnd: string | null;
    /** Growth % layered on the DRR. Null = none. */
    drrGrowthPct: number | null;
    channelTierMode: boolean;
    tierDohOverride: Record<string, number | ''>;
    minSourceStockUnitsOverride: number | null;
    maxOutboundUtilizationPctOverride: number | null;
    inboundInTransitDaysOverride: number | null;
    sourceReserveUnitsOverride: number | null;
    minInternalDohDaysOverride: number | null;
    maxUnitsPerShipmentOverride: number | null;
    maxMeasurementPerShipmentOverride: number | null;
    /** Channel-level defaults shown as placeholder hints */
    channelDefaultMinSourceStockUnits?: number | null;
    channelDefaultMaxOutboundUtilizationPct?: number | null;
    channelDefaultInboundInTransitDays?: number | null;
    channelDefaultSourceReserveUnits?: number | null;
    channelDefaultMinInternalDohDays?: number | null;
    channelDefaultMilestone1Days?: number | null;
    channelDefaultMilestone2Days?: number | null;
    channelDefaultMaxUnitsPerShipment?: number | null;
    channelDefaultMaxMeasurementPerShipment?: number | null;
    channelMeasurementUnit?: 'weight' | 'volume' | null;
    onDemandSourceChange: (v: DemandSource) => void;
    onTargetDohChange: (v: number | null) => void;
    onMilestone1Change: (v: number | null) => void;
    onMilestone2Change: (v: number | null) => void;
    onDrrLookbackChange: (v: number | null) => void;
    onDrrWindowChange: (start: string | null, end: string | null) => void;
    onDrrGrowthChange: (v: number | null) => void;
    onMinSourceStockUnitsChange: (v: number | null) => void;
    onMaxOutboundUtilizationPctChange: (v: number | null) => void;
    onInboundInTransitDaysChange: (v: number | null) => void;
    onSourceReserveUnitsChange: (v: number | null) => void;
    onMinInternalDohDaysChange: (v: number | null) => void;
    onMaxUnitsPerShipmentChange: (v: number | null) => void;
    onMaxMeasurementPerShipmentChange: (v: number | null) => void;
    onTierDohOverrideChange: (tier: string, v: number | '') => void;
    onCompute: () => void;
    /** "Schedule" next to Compute: the alert to create (channel + pinned settings + deep link), built by the parent. */
    scheduleAlert?: Omit<CreateScheduledAlertInput, 'frequency' | 'triggerTime' | 'triggerDayOfWeek'> | null;
    computing: boolean;
    isAggregated: boolean;
    planWarehouses: Array<{ warehouseId: string; name: string }>;
    warehouseRules: WarehouseDohRule[];
    selectedPlanWarehouseIds: string[];
    onWarehouseIdsChange: (ids: string[]) => void;
    planWarehouseOptionsLoading: boolean;
    planRuleOverrides: Record<string, PlanRuleGroupOverride>;
    onPlanFlatOverrideChange: (groupKey: string, v: number | '') => void;
    onPlanTierOverrideChange: (
        groupKey: string,
        tier: string,
        v: number | '',
    ) => void;
    /** Aggregated channels only: available paths to select from */
    availablePaths: Array<{
        id: string;
        fromWarehouseName: string;
        leadTimeDays: number;
    }>;
    selectedPathIds: string[];
    onPathIdsChange: (ids: string[]) => void;
    pathsLoading: boolean;
    /** Non-aggregated channels only: eligible source (from) warehouses, universe scoped to the selected FCs. */
    sourceWarehouseOptions: Array<{ warehouseId: string; name: string }>;
    selectedSourceWarehouseIds: string[];
    onSourceWarehouseIdsChange: (ids: string[]) => void;
    /** 'panel' = full-page 2-col, 'modal' = single column */
    layout?: 'panel' | 'modal';
}

const SettingsForm: FC<SettingsFormProps> = ({
    channelName,
    channelSupportsOpenPo,
    demandSource,
    purchaseOrderId,
    scopedProductCount,
    targetDohDays,
    milestone1DaysOverride,
    milestone2DaysOverride,
    drrLookbackDays,
    drrWindowStart,
    drrWindowEnd,
    drrGrowthPct,
    minSourceStockUnitsOverride,
    maxOutboundUtilizationPctOverride,
    inboundInTransitDaysOverride,
    sourceReserveUnitsOverride,
    minInternalDohDaysOverride,
    maxUnitsPerShipmentOverride,
    maxMeasurementPerShipmentOverride,
    channelDefaultMinSourceStockUnits,
    channelDefaultMaxOutboundUtilizationPct,
    channelDefaultInboundInTransitDays,
    channelDefaultSourceReserveUnits,
    channelDefaultMinInternalDohDays,
    channelDefaultMilestone1Days,
    channelDefaultMilestone2Days,
    channelDefaultMaxUnitsPerShipment,
    channelDefaultMaxMeasurementPerShipment,
    channelMeasurementUnit,
    channelTierMode,
    tierDohOverride,
    onDemandSourceChange,
    onTargetDohChange,
    onMilestone1Change,
    onMilestone2Change,
    onDrrLookbackChange,
    onDrrWindowChange,
    onDrrGrowthChange,
    onMinSourceStockUnitsChange,
    onMaxOutboundUtilizationPctChange,
    onInboundInTransitDaysChange,
    onSourceReserveUnitsChange,
    onMinInternalDohDaysChange,
    onMaxUnitsPerShipmentChange,
    onMaxMeasurementPerShipmentChange,
    onTierDohOverrideChange,
    onCompute,
    scheduleAlert,
    computing,
    isAggregated,
    planWarehouses,
    warehouseRules,
    selectedPlanWarehouseIds,
    onWarehouseIdsChange,
    planWarehouseOptionsLoading,
    planRuleOverrides,
    onPlanFlatOverrideChange,
    onPlanTierOverrideChange,
    availablePaths,
    selectedPathIds,
    onPathIdsChange,
    pathsLoading,
    sourceWarehouseOptions,
    selectedSourceWarehouseIds,
    onSourceWarehouseIdsChange,
    layout = 'modal',
}) => {
    const allowedSources: DemandSource[] = channelSupportsOpenPo
        ? ['FORECAST', 'PAST_SALES', 'MAX_OF_FORECAST_PAST_SALES', 'OPEN_PO']
        : ['FORECAST', 'PAST_SALES', 'MAX_OF_FORECAST_PAST_SALES'];

    const allRuleGroups = useMemo(
        () => buildPlanRuleGroups(warehouseRules),
        [warehouseRules],
    );

    const relevantRuleGroups = useMemo(() => {
        if (selectedPlanWarehouseIds.length === 0) return allRuleGroups;
        return allRuleGroups.filter((g) =>
            g.warehouseIds.some((id) => selectedPlanWarehouseIds.includes(id)),
        );
    }, [allRuleGroups, selectedPlanWarehouseIds]);

    const whNameById = useMemo(
        () => new Map(planWarehouses.map((w) => [w.warehouseId, w.name])),
        [planWarehouses],
    );

    // Warehouses selected but not covered by any rule group
    const uncoveredWhs = useMemo(() => {
        if (!isAggregated && selectedPlanWarehouseIds.length > 0) {
            const covered = new Set(
                allRuleGroups.flatMap((g) => g.warehouseIds),
            );
            return selectedPlanWarehouseIds.filter((id) => !covered.has(id));
        }
        return [];
    }, [isAggregated, selectedPlanWarehouseIds, allRuleGroups]);

    // ── Section fragments ──────────────────────────────────────────────────────

    const demandSignalSection = (
        <div>
            <PlanSectionTitle>Demand Signal</PlanSectionTitle>
            <Stack gap={6}>
                {DEMAND_SOURCE_OPTIONS.filter((o) =>
                    allowedSources.includes(o.value),
                ).map((opt) => {
                    const active = demandSource === opt.value;
                    const Icon = opt.icon;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onDemandSourceChange(opt.value)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 12px',
                                border: active
                                    ? '1.5px solid rgb(1,149,255)'
                                    : '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 8,
                                cursor: 'pointer',
                                textAlign: 'left',
                                backgroundColor: active
                                    ? 'rgba(1,149,255,0.05)'
                                    : '#fff',
                            }}
                        >
                            <div
                                style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    border: `2px solid ${active ? 'rgb(1,149,255)' : 'rgba(0,0,0,0.2)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {active && (
                                    <div
                                        style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgb(1,149,255)',
                                        }}
                                    />
                                )}
                            </div>
                            <span
                                style={{
                                    color: active
                                        ? 'rgb(1,149,255)'
                                        : neutralColors.textMuted,
                                    display: 'flex',
                                }}
                            >
                                <Icon size={14} />
                            </span>
                            <div>
                                <Text
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: active
                                            ? 'rgb(1,149,255)'
                                            : neutralColors.textPrimary,
                                    }}
                                >
                                    {opt.label}
                                </Text>
                                <Text
                                    style={{
                                        fontSize: 11,
                                        color: neutralColors.textMuted,
                                    }}
                                >
                                    {opt.description}
                                </Text>
                            </div>
                        </button>
                    );
                })}
            </Stack>
            {demandSource === 'OPEN_PO' && purchaseOrderId && (
                <Text
                    mt={8}
                    style={{ fontSize: 11, color: neutralColors.textMuted }}
                >
                    Scoped to PO{' '}
                    <span style={{ fontWeight: 600, color: neutralColors.textPrimary }}>
                        {purchaseOrderId}
                    </span>
                </Text>
            )}
            {!!scopedProductCount && (
                <Text mt={8} fz={11} c="dimmed">
                    Scoped to the {scopedProductCount} SKU{scopedProductCount === 1 ? '' : 's'} flagged by the channel alert — every other SKU on the channel is left out of this run.
                </Text>
            )}
        </div>
    );

    // Source / scope controls differ by routing mode:
    //   • Aggregated  → paths ARE sources (channel-level). One "Eligible Sources" control (path selection).
    //   • Per-FC      → destinations (FCs) and sources (From WHs) are distinct. Two controls: "FCs" picks the
    //     destinations; "Eligible Sources" restricts which internal WHs may supply them. The source universe
    //     is derived from the selected FCs' paths (sourceWarehouseOptions), so it narrows as FCs are chosen.
    const eligibleSourcesSection = isAggregated ? (
        availablePaths.length > 1 ? (
            <div>
                <PlanSectionTitle>Eligible Sources</PlanSectionTitle>
                <Text
                    style={{
                        fontSize: 11,
                        color: neutralColors.textMuted,
                        marginBottom: 8,
                    }}
                >
                    Choose which source warehouses participate. Deselect to
                    exclude a warehouse from the mix. Default: all active paths.
                </Text>
                <MultiSelect
                    placeholder={
                        pathsLoading
                            ? 'Loading…'
                            : selectedPathIds.length === 0
                              ? 'All sources'
                              : ''
                    }
                    data={availablePaths.map((p) => ({
                        value: p.id,
                        label: `${p.fromWarehouseName} (${p.leadTimeDays}d)`,
                    }))}
                    value={selectedPathIds}
                    onChange={onPathIdsChange}
                    disabled={pathsLoading}
                    size="sm"
                    searchable
                    clearable
                    maxDropdownHeight={200}
                />
            </div>
        ) : null
    ) : (
        <Stack gap="md">
            {planWarehouses.length > 1 ? (
                <div>
                    <PlanSectionTitle>FCs</PlanSectionTitle>
                    <Text
                        style={{
                            fontSize: 11,
                            color: neutralColors.textMuted,
                            marginBottom: 8,
                        }}
                    >
                        Restrict this plan to specific destination FCs — leave
                        blank to include all FCs.
                    </Text>
                    <MultiSelect
                        placeholder={
                            planWarehouseOptionsLoading
                                ? 'Loading…'
                                : selectedPlanWarehouseIds.length === 0
                                  ? 'All FCs'
                                  : ''
                        }
                        data={planWarehouses.map((w) => ({
                            value: w.warehouseId,
                            label: w.name,
                        }))}
                        value={selectedPlanWarehouseIds}
                        onChange={onWarehouseIdsChange}
                        disabled={planWarehouseOptionsLoading}
                        size="sm"
                        searchable
                        clearable
                        maxDropdownHeight={200}
                    />
                </div>
            ) : null}
            {sourceWarehouseOptions.length > 1 ? (
                <div>
                    <PlanSectionTitle>Eligible Sources</PlanSectionTitle>
                    <Text
                        style={{
                            fontSize: 11,
                            color: neutralColors.textMuted,
                            marginBottom: 8,
                        }}
                    >
                        Restrict which internal source warehouses may supply the
                        selected FCs — leave blank to use all eligible sources.
                    </Text>
                    <MultiSelect
                        placeholder={
                            planWarehouseOptionsLoading
                                ? 'Loading…'
                                : selectedSourceWarehouseIds.length === 0
                                  ? 'All sources'
                                  : ''
                        }
                        data={sourceWarehouseOptions.map((w) => ({
                            value: w.warehouseId,
                            label: w.name,
                        }))}
                        value={selectedSourceWarehouseIds}
                        onChange={onSourceWarehouseIdsChange}
                        disabled={planWarehouseOptionsLoading}
                        size="sm"
                        searchable
                        clearable
                        maxDropdownHeight={200}
                    />
                </div>
            ) : null}
        </Stack>
    );

    const targetDohSection = (
        <div>
            <PlanSectionTitle>Target Days on Hand (DoH)</PlanSectionTitle>
            <Text
                style={{
                    fontSize: 11,
                    color: neutralColors.textMuted,
                    marginBottom: 8,
                }}
            >
                How many days of forward stock you want each channel warehouse
                to hold. Transfers are computed to close the gap between current
                stock and this target.
            </Text>
            {!isAggregated ? (
                planWarehouseOptionsLoading ? (
                    <Loader size="xs" />
                ) : relevantRuleGroups.length > 0 ? (
                    <Stack gap={8}>
                        {relevantRuleGroups.map((group) => {
                            const gKey = planRuleGroupKey(group);
                            const override =
                                planRuleOverrides[gKey] ??
                                ({} as Partial<PlanRuleGroupOverride>);
                            const groupWhs =
                                selectedPlanWarehouseIds.length > 0
                                    ? group.warehouseIds.filter((id) =>
                                          selectedPlanWarehouseIds.includes(id),
                                      )
                                    : group.warehouseIds;
                            return (
                                <Paper key={gKey} withBorder p="sm" radius="sm">
                                    <Stack gap={8}>
                                        <Group gap={4} wrap="wrap">
                                            {groupWhs.map((id) => (
                                                <Badge
                                                    key={id}
                                                    size="xs"
                                                    variant="light"
                                                    color="blue"
                                                    radius="sm"
                                                >
                                                    {whNameById.get(id) ?? id}
                                                </Badge>
                                            ))}
                                        </Group>
                                        {group.dohMode === 'tier' ? (
                                            <Group
                                                gap={6}
                                                grow
                                                align="flex-start"
                                            >
                                                {Object.keys(
                                                    group.defaultTierDoh,
                                                )
                                                    .sort()
                                                    .map((tier) => (
                                                        <NumberInput
                                                            key={tier}
                                                            label={`Tier ${tier}`}
                                                            placeholder={
                                                                group
                                                                    .defaultTierDoh[
                                                                    tier
                                                                ] !== ''
                                                                    ? `${group.defaultTierDoh[tier]}d`
                                                                    : '—'
                                                            }
                                                            value={
                                                                override
                                                                    .tierDoh?.[
                                                                    tier
                                                                ] ?? ''
                                                            }
                                                            onChange={(v) =>
                                                                onPlanTierOverrideChange(
                                                                    gKey,
                                                                    tier,
                                                                    v === ''
                                                                        ? ''
                                                                        : Number(
                                                                              v,
                                                                          ),
                                                                )
                                                            }
                                                            min={1}
                                                            max={180}
                                                            rightSection={
                                                                <Text
                                                                    style={{
                                                                        fontSize: 10,
                                                                        color: neutralColors.textMuted,
                                                                        paddingRight: 4,
                                                                    }}
                                                                >
                                                                    d
                                                                </Text>
                                                            }
                                                            rightSectionWidth={
                                                                20
                                                            }
                                                            hideControls
                                                            size="xs"
                                                        />
                                                    ))}
                                            </Group>
                                        ) : (
                                            <NumberInput
                                                label="Target DoH"
                                                placeholder={
                                                    group.defaultFlat !== ''
                                                        ? `${group.defaultFlat}d (default)`
                                                        : 'Not set'
                                                }
                                                value={override?.flat ?? ''}
                                                onChange={(v) =>
                                                    onPlanFlatOverrideChange(
                                                        gKey,
                                                        v === ''
                                                            ? ''
                                                            : Number(v),
                                                    )
                                                }
                                                min={1}
                                                max={180}
                                                rightSection={
                                                    <Text
                                                        style={{
                                                            fontSize: 11,
                                                            color: neutralColors.textMuted,
                                                            paddingRight: 6,
                                                        }}
                                                    >
                                                        d
                                                    </Text>
                                                }
                                                rightSectionWidth={24}
                                                hideControls
                                                size="xs"
                                            />
                                        )}
                                    </Stack>
                                </Paper>
                            );
                        })}
                        {uncoveredWhs.length > 0 && (
                            <Text
                                style={{
                                    fontSize: 11,
                                    color: neutralColors.textMuted,
                                    fontStyle: 'italic',
                                }}
                            >
                                {uncoveredWhs
                                    .map((id) => whNameById.get(id) ?? id)
                                    .join(', ')}{' '}
                                {uncoveredWhs.length === 1 ? 'has' : 'have'} no
                                DoH rule — channel default applies.
                            </Text>
                        )}
                    </Stack>
                ) : (
                    <Text
                        style={{
                            fontSize: 12,
                            color: neutralColors.textMuted,
                            fontStyle: 'italic',
                        }}
                    >
                        No DoH rules configured for this channel. Set them in
                        Channel Settings.
                    </Text>
                )
            ) : channelTierMode ? (
                <Group gap={8} grow align="flex-start">
                    {Object.keys(tierDohOverride)
                        .sort()
                        .map((tier) => (
                            <NumberInput
                                key={tier}
                                label={`Tier ${tier}`}
                                placeholder="Default"
                                value={tierDohOverride[tier] ?? ''}
                                onChange={(v) =>
                                    onTierDohOverrideChange(
                                        tier,
                                        v === '' ? '' : Number(v),
                                    )
                                }
                                min={1}
                                max={180}
                                rightSection={
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            color: neutralColors.textMuted,
                                            paddingRight: 6,
                                        }}
                                    >
                                        d
                                    </Text>
                                }
                                rightSectionWidth={24}
                                hideControls
                                size="sm"
                            />
                        ))}
                </Group>
            ) : (
                <NumberInput
                    placeholder="Use channel default"
                    value={targetDohDays ?? ''}
                    onChange={(v) =>
                        onTargetDohChange(v === '' ? null : Number(v))
                    }
                    min={1}
                    max={180}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 12,
                                color: neutralColors.textMuted,
                                paddingRight: 8,
                            }}
                        >
                            days
                        </Text>
                    }
                    rightSectionWidth={46}
                    hideControls
                    size="sm"
                />
            )}
        </div>
    );

    const processOverridesSection = (
        <div>
            <PlanSectionTitle>Process Overrides</PlanSectionTitle>
            <Text
                style={{
                    fontSize: 11,
                    color: neutralColors.textMuted,
                    marginBottom: 12,
                }}
            >
                Leave blank to use the channel defaults. These control how long
                each stage of the transfer process takes.
            </Text>
            <Stack gap={10}>
                <NumberInput
                    label="M1 — Availability check"
                    description="Days for the internal WH to confirm stock is available and ready"
                    placeholder={
                        channelDefaultMilestone1Days != null
                            ? `Channel default: ${channelDefaultMilestone1Days}d`
                            : 'Default'
                    }
                    value={milestone1DaysOverride ?? ''}
                    onChange={(v) =>
                        onMilestone1Change(v === '' ? null : Number(v))
                    }
                    min={0}
                    max={30}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            d
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="M2 — Dispatch lead time"
                    description="Days from availability confirmation to stock physically leaving the internal WH"
                    placeholder={
                        channelDefaultMilestone2Days != null
                            ? `Channel default: ${channelDefaultMilestone2Days}d`
                            : 'Default'
                    }
                    value={milestone2DaysOverride ?? ''}
                    onChange={(v) =>
                        onMilestone2Change(v === '' ? null : Number(v))
                    }
                    min={0}
                    max={30}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            d
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Min source stock (units)"
                    description="SKUs with source-WH stock below this threshold are excluded (shown as unsendable)"
                    placeholder={
                        channelDefaultMinSourceStockUnits != null
                            ? `Channel default: ${channelDefaultMinSourceStockUnits} u`
                            : 'Channel default'
                    }
                    value={minSourceStockUnitsOverride ?? ''}
                    onChange={(v) =>
                        onMinSourceStockUnitsChange(v === '' ? null : Number(v))
                    }
                    min={0}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            u
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Max outbound utilisation (%)"
                    description="Maximum % of fromWH stock that can be sent in this plan run"
                    placeholder={
                        channelDefaultMaxOutboundUtilizationPct != null
                            ? `Channel default: ${Math.round(channelDefaultMaxOutboundUtilizationPct * 100)}%`
                            : 'Channel default (no cap)'
                    }
                    value={
                        maxOutboundUtilizationPctOverride != null
                            ? Math.round(
                                  maxOutboundUtilizationPctOverride * 100,
                              )
                            : ''
                    }
                    onChange={(v) =>
                        onMaxOutboundUtilizationPctChange(
                            v === '' ? null : Number(v) / 100,
                        )
                    }
                    min={1}
                    max={100}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            %
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Include inbound in-transit (days)"
                    description="Add inbound POs arriving at the source WH within this many days to available stock (after the cap). 0 = on-hand only"
                    placeholder={
                        channelDefaultInboundInTransitDays != null &&
                        channelDefaultInboundInTransitDays > 0
                            ? `Channel default: ${channelDefaultInboundInTransitDays} d`
                            : 'Channel default (on-hand only)'
                    }
                    value={inboundInTransitDaysOverride ?? ''}
                    onChange={(v) =>
                        onInboundInTransitDaysChange(
                            v === '' ? null : Number(v),
                        )
                    }
                    min={0}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            d
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Source reserve (units)"
                    description="Soft reserve always kept back at each source WH — the plan sends a SKU down to this level but no further"
                    placeholder={
                        channelDefaultSourceReserveUnits != null
                            ? `Channel default: ${channelDefaultSourceReserveUnits} u`
                            : 'Channel default (no reserve)'
                    }
                    value={sourceReserveUnitsOverride ?? ''}
                    onChange={(v) =>
                        onSourceReserveUnitsChange(v === '' ? null : Number(v))
                    }
                    min={0}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            u
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Internal DoH floor (days)"
                    description="Cap total units leaving the internal network per SKU so internal days-on-hand (vs other channels, credited with network-wide inbound in-transit over this window) stays at/above this"
                    placeholder={
                        channelDefaultMinInternalDohDays != null
                            ? `Channel default: ${channelDefaultMinInternalDohDays} d`
                            : 'Channel default (no floor)'
                    }
                    value={minInternalDohDaysOverride ?? ''}
                    onChange={(v) =>
                        onMinInternalDohDaysChange(v === '' ? null : Number(v))
                    }
                    min={0}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            d
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label="Max units per shipment"
                    description="Override the channel-level shipment unit cap for this plan run only"
                    placeholder={
                        channelDefaultMaxUnitsPerShipment != null
                            ? `Channel default: ${channelDefaultMaxUnitsPerShipment} u`
                            : 'Channel default (no cap)'
                    }
                    value={maxUnitsPerShipmentOverride ?? ''}
                    onChange={(v) =>
                        onMaxUnitsPerShipmentChange(v === '' ? null : Number(v))
                    }
                    min={1}
                    rightSection={
                        <Text
                            style={{
                                fontSize: 10,
                                color: neutralColors.textMuted,
                                paddingRight: 4,
                            }}
                        >
                            u
                        </Text>
                    }
                    rightSectionWidth={20}
                    hideControls
                    size="sm"
                />
                <NumberInput
                    label={`Max measurement per shipment${channelMeasurementUnit ? ` (${channelMeasurementUnit})` : ''}`}
                    description="Override the channel-level shipment measurement cap for this plan run only"
                    placeholder={
                        channelDefaultMaxMeasurementPerShipment != null
                            ? `Channel default: ${channelDefaultMaxMeasurementPerShipment}`
                            : 'Channel default (no cap)'
                    }
                    value={maxMeasurementPerShipmentOverride ?? ''}
                    onChange={(v) =>
                        onMaxMeasurementPerShipmentChange(
                            v === '' ? null : Number(v),
                        )
                    }
                    min={0}
                    hideControls
                    size="sm"
                />
            </Stack>
        </div>
    );

    const drrControlSection = (
        <DrrControlSection
            lookbackDays={drrLookbackDays}
            windowStart={drrWindowStart}
            windowEnd={drrWindowEnd}
            growthPct={drrGrowthPct}
            onLookbackChange={onDrrLookbackChange}
            onWindowChange={onDrrWindowChange}
            onGrowthChange={onDrrGrowthChange}
        />
    );

    // ── Panel (2-column) layout ──────────────────────────────────────────────────
    if (layout === 'panel') {
        return (
            <Stack gap={28}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 40,
                        alignItems: 'start',
                    }}
                >
                    <Stack gap={24}>
                        {demandSignalSection}
                        {targetDohSection}
                        {drrControlSection}
                    </Stack>
                    <Stack gap={24}>
                        {eligibleSourcesSection}
                        {processOverridesSection}
                    </Stack>
                </div>
                <div
                    style={{
                        borderTop: '1px solid rgba(0,0,0,0.07)',
                        paddingTop: 20,
                    }}
                >
                    <ComputeAndSchedule channelName={channelName} onCompute={onCompute} computing={computing} scheduleAlert={scheduleAlert} size="md" />
                </div>
            </Stack>
        );
    }

    // ── Modal (single-column) layout ─────────────────────────────────────────────
    return (
        <Stack gap={18}>
            {demandSignalSection}
            {eligibleSourcesSection}
            {targetDohSection}
            {drrControlSection}
            {processOverridesSection}
            <ComputeAndSchedule channelName={channelName} onCompute={onCompute} computing={computing} scheduleAlert={scheduleAlert} size="sm" />
        </Stack>
    );
};

// ─── Settings full-page panel ─────────────────────────────────────────────────

interface SettingsPanelProps extends SettingsFormProps {
    onBack: () => void;
    isNew: boolean;
}

/** Compute + Schedule side by side; the dialog owns the timing, the parent owns what is scheduled. */
const ComputeAndSchedule: FC<{
    channelName: string;
    onCompute: () => void;
    computing: boolean;
    scheduleAlert?: SettingsPanelProps['scheduleAlert'];
    size: 'sm' | 'md';
}> = ({ channelName, onCompute, computing, scheduleAlert, size }) => {
    const [scheduleOpen, setScheduleOpen] = useState(false);
    return (
        <>
            <Group gap={8} wrap="nowrap" align="stretch">
                <Button
                    size={size}
                    style={size === 'md' ? { flex: 1, fontSize: 15, fontWeight: 600, height: 48 } : { flex: 1 }}
                    rightSection={<ArrowRight size={size === 'md' ? 18 : 16} />}
                    onClick={onCompute}
                    loading={computing}
                >
                    Compute Plan — {channelName}
                </Button>
                {scheduleAlert && (
                    <Button size={size} variant="default" style={size === 'md' ? { height: 48 } : undefined} onClick={() => setScheduleOpen(true)}>
                        Schedule
                    </Button>
                )}
            </Group>
            {scheduleAlert && (
                <ScheduleAlertDialog
                    opened={scheduleOpen}
                    onClose={() => setScheduleOpen(false)}
                    title={`Schedule: what to send to ${channelName}`}
                    description="The plan engine runs on this schedule with these settings pinned and saves its draft. The SKUs it would send show as a card on Home; opening it opens that draft."
                    alert={scheduleAlert}
                />
            )}
        </>
    );
};

const SettingsPanel: FC<SettingsPanelProps> = ({
    onBack,
    channelName,
    isNew,
    ...rest
}) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        }}
    >
        <Group justify="space-between" pb="md" style={{ flexShrink: 0 }}>
            <Group gap={8}>
                <Button
                    variant="subtle"
                    color="dark"
                    size="xs"
                    leftSection={<ArrowLeft size={14} />}
                    onClick={onBack}
                >
                    {channelName}
                </Button>
                <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                    /
                </Text>
                <Text
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: neutralColors.textPrimary,
                    }}
                >
                    {isNew ? 'New Distribution Plan' : 'Edit Plan Settings'}
                </Text>
            </Group>
        </Group>
        <div
            style={{
                flex: 1,
                overflowY: 'auto',
                paddingTop: 8,
                paddingBottom: 32,
            }}
        >
            <SettingsForm channelName={channelName} {...rest} layout="panel" />
        </div>
    </div>
);

// ─── Column toggle ─────────────────────────────────────────────────────────────

const PLAN_OPTIONAL_COLS = [
    { key: 'marketplaceSkuId', label: 'Marketplace SKU ID' },
    { key: 'secondaryId', label: 'Secondary ID' },
    { key: 'tier', label: 'Tier' },
    { key: 'toWH', label: 'To' },
    { key: 'channelStock', label: 'Channel Stock' },
    { key: 'internalAvailableUnits', label: 'Internal Inventory' },
    { key: 'demandToMeet', label: 'Demand to Meet' },
    { key: 'orderedValue', label: 'Ordered Value', poPlanOnly: true },
    { key: 'sendingValue', label: 'Sending Value', poPlanOnly: true },
    { key: 'leadTimeDemand', label: 'Lead Time Demand' },
    { key: 'dailyDemand', label: 'Daily Rate' },
    { key: 'inTransit', label: 'In-Transit' },
    { key: 'pastSalesQty', label: 'Past Sales' },
    { key: 'channelDohAfter', label: 'Channel DoH (after)' },
    { key: 'internalDohAfter', label: 'Internal DoH (after)' },
    { key: 'networkInTransit', label: 'Network In-Transit' },
];

/** The value columns exist only on a plan made against a purchase order. */
const PO_PLAN_ONLY_COLS = PLAN_OPTIONAL_COLS.filter((c) => 'poPlanOnly' in c).map((c) => c.key);

function PlanColumnToggle({
    hidden,
    onChange,
    isPoPlan,
}: {
    hidden: Set<string>;
    onChange: (h: Set<string>) => void;
    isPoPlan: boolean;
}) {
    const toggle = (key: string) => {
        const next = new Set(hidden);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        onChange(next);
    };
    return (
        <Popover position="bottom-end" withArrow shadow="md" withinPortal>
            <Popover.Target>
                <Tooltip label="Show / hide columns" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                        <Rows size={15} />
                    </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={6} p={4}>
                    <Text
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }}
                    >
                        Columns
                    </Text>
                    {PLAN_OPTIONAL_COLS.filter((def) => isPoPlan || !('poPlanOnly' in def)).map((def) => (
                        <Checkbox
                            key={def.key}
                            label={def.label}
                            size="xs"
                            checked={!hidden.has(def.key)}
                            onChange={() => toggle(def.key)}
                        />
                    ))}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

// ─── Transfer table types ──────────────────────────────────────────────────────

/** Whether a row/cell is algo-recommended, unsendable (excluded), or available (not planned). */
type UnifiedRowState = 'recommended' | 'available' | 'unsendable';

/** Per-From-WH slot in a SkuRouteRow — holds the transfer (or absence of one) for this SKU × From WH. */
interface FromWhSlot {
    transferId: string | null;
    isExcluded: boolean;
    recommendedQty: number;
    approvedQty: number | null;
    fromWarehouseStock: number | null;
    /** Inbound in-transit units arriving at this source WH within the channel's horizon. 0 when disabled. */
    fromWhInTransit: number;
    cellState: UnifiedRowState;
    saleOrderCode: string | null;
    saleOrderName: string | null;
    wmsStatus: 'CREATED' | 'FAILED' | null;
    measurementValue: number | null;
}

/** One row per SKU × To WH. From WHs appear as dynamic columns. */
interface SkuRouteRow {
    productId: string;
    productName: string | null;
    /** SKU id on the plan's channel; ' | '-joined when the product maps to several. Null when unmapped. */
    marketplaceSkuId?: string | null;
    /** Channel secondary id (e.g. Amazon ASIN), same joining as marketplaceSkuId. */
    secondaryId?: string | null;
    toWarehouseId: string | null;
    toWarehouseName: string | null;
    tier?: string | null;
    category?: string | null;
    productGroup?: string | null;
    isActive?: boolean;
    _channelStock?: number | null;
    networkInTransit?: number | null;
    _gapUnits: number;
    destinationPlanQty?: number | null;
    demandToMeet?: number | null;
    drrDemand?: number | null;
    forecastDemand?: number | null;
    leadTimeDemand?: number | null;
    /**
     * Daily consumption rate at the destination under the plan's demand signal — the divisor behind
     * every DoH on the row. Frozen on the transfer for planned rows, derived from channel-health for
     * available ones (see `resolveDailyRate`).
     */
    dailyDemand?: number | null;
    inTransit?: number;
    /** Reference-only: outgoing units for this SKU at this store over the selected lookback window. */
    pastSalesQty?: number | null;
    /**
     * Reference-only, PO plans: the order's line for this SKU, before tax, read live from the
     * order. What the row sends is valued as its share of orderedValue (see `valueOfQty`).
     */
    orderedValue?: number | null;
    orderedQty?: number | null;
    unitPrice?: number | null;
    fromWhSlots: Record<string, FromWhSlot>;
    rowState: UnifiedRowState;
    rowKey: string;
    /** True when this SKU is on the plan's scoped PO (OPEN_PO plans only). Drives the "PO SKUs" filter. */
    _onPurchaseOrder?: boolean;
}

const isNewSlotKey = (key: string): boolean => key.startsWith('new::');

/**
 * Sendable ceiling for one slot — on-hand and inbound in-transit each capped by the utilisation
 * fraction, then summed. What the cell shows as "avail" and the line a manual quantity is flagged
 * against (red when above it). It never clamps a write: a quantity above it is the operator's call
 * and is saved, approved and pushed as typed. Null only when the source's stock is unknown.
 */
const slotSendableCeiling = (
    slot: FromWhSlot,
    maxUtil: number,
): number | null => {
    const onHandCapped =
        slot.fromWarehouseStock == null
            ? null
            : Math.floor(slot.fromWarehouseStock * maxUtil);
    const inTransitCapped = Math.floor((slot.fromWhInTransit ?? 0) * maxUtil);
    if (onHandCapped == null) return inTransitCapped > 0 ? inTransitCapped : null;
    return onHandCapped + inTransitCapped;
};

/** Every selectable slot key on a row — including unsendable ones, which take a manual qty like any other. */
const rowSelectionKeys = (row: SkuRouteRow): string[] =>
    Object.keys(row.fromWhSlots).map((fromWhId) => slotEditKey(row, fromWhId));

/** One row of the per-store per-category capacity table (top-right of the plan view). */
interface CategoryCapRow {
    bucket: string;
    ideal: number | null;
    current: number;
    intransit: number;
    total: number;
    headroom: number | null;
    plan: number;
}

// ─── CSV export columns ──────────────────────────────────────────────────────
// The plan CSV is a *flattened* export: one row per SKU x From-WH slot. This
// descriptor is the single source of truth for both the download column-picker
// and the CSV builder. `key` doubles as the CSV header. `seedVisible` decides
// whether a column is pre-checked the first time the picker opens (mirroring the
// table's currently-visible columns). `locked` columns are always exported so the
// file stays re-uploadable (the uploader matches productId + planQuantity by name).
interface PlanExportRowCtx {
    row: SkuRouteRow;
    slot: FromWhSlot;
    qty: number;
    fromWhName: string;
    toWarehouse: string;
}
interface PlanExportCol {
    key: string;
    label: string;
    locked?: boolean;
    seedVisible: (hidden: Set<string>, isAggregated: boolean) => boolean;
    value: (ctx: PlanExportRowCtx) => string;
}

const ALWAYS = () => true;
const ceilStr = (n: number | null | undefined): string =>
    n != null ? String(Math.ceil(n)) : '';

const PLAN_EXPORT_COLS: PlanExportCol[] = [
    {
        key: 'productId',
        label: 'Product ID',
        locked: true,
        seedVisible: ALWAYS,
        value: (c) => c.row.productId,
    },
    {
        key: 'productName',
        label: 'Product Name',
        seedVisible: ALWAYS,
        value: (c) => c.row.productName ?? '',
    },
    {
        key: 'marketplaceSkuId',
        label: 'Marketplace SKU ID',
        seedVisible: (h) => !h.has('marketplaceSkuId'),
        value: (c) => c.row.marketplaceSkuId ?? '',
    },
    {
        key: 'secondaryId',
        label: 'Secondary ID',
        seedVisible: (h) => !h.has('secondaryId'),
        value: (c) => c.row.secondaryId ?? '',
    },
    {
        key: 'fromWarehouse',
        label: 'From Warehouse',
        seedVisible: ALWAYS,
        value: (c) => c.fromWhName,
    },
    {
        key: 'toWarehouse',
        label: 'To Warehouse',
        seedVisible: (h, agg) => !agg && !h.has('toWH'),
        value: (c) => c.toWarehouse,
    },
    {
        key: 'tier',
        label: 'Tier',
        seedVisible: (h) => !h.has('tier'),
        value: (c) => c.row.tier ?? '',
    },
    {
        key: 'channelStock',
        label: 'Channel Stock',
        seedVisible: (h) => !h.has('channelStock'),
        value: (c) => ceilStr(c.row._channelStock),
    },
    {
        key: 'demandToMeet',
        label: 'Demand',
        seedVisible: (h) => !h.has('demandToMeet'),
        value: (c) => ceilStr(c.row.demandToMeet),
    },
    {
        key: 'orderedValue',
        label: 'Ordered Value',
        seedVisible: (h) => !h.has('orderedValue'),
        value: (c) => (c.row.orderedValue == null ? '' : String(c.row.orderedValue)),
    },
    {
        // The export is one row per source WH, so this is the value of that slot's quantity.
        key: 'sendingValue',
        label: 'Sending Value',
        seedVisible: (h) => !h.has('sendingValue'),
        value: (c) => String(valueOfQty(c.row, c.qty) ?? ''),
    },
    {
        key: 'fromWhStock',
        label: 'From-WH Stock',
        seedVisible: ALWAYS,
        value: (c) => ceilStr(c.slot.fromWarehouseStock),
    },
    {
        key: 'forecastDemand',
        label: 'Forecast Demand',
        seedVisible: (h) => !h.has('forecastDemand'),
        value: (c) => ceilStr(c.row.forecastDemand),
    },
    {
        key: 'drrDemand',
        label: 'DRR Demand',
        seedVisible: (h) => !h.has('drrDemand'),
        value: (c) => ceilStr(c.row.drrDemand),
    },
    {
        key: 'pastSalesQty',
        label: 'Past Sales',
        seedVisible: (h) => !h.has('pastSalesQty'),
        value: (c) => ceilStr(c.row.pastSalesQty),
    },
    {
        key: 'inTransit',
        label: 'In-Transit',
        seedVisible: (h) => !h.has('inTransit'),
        value: (c) => String(Math.ceil(c.row.inTransit ?? 0)),
    },
    {
        key: 'leadTimeDemand',
        label: 'Lead Time Demand',
        seedVisible: (h) => !h.has('leadTimeDemand'),
        value: (c) => ceilStr(c.row.leadTimeDemand),
    },
    {
        key: 'stockDelta',
        label: 'Stock Delta',
        seedVisible: ALWAYS,
        value: (c) => ceilStr(c.row.destinationPlanQty),
    },
    {
        key: 'planQuantity',
        label: 'Plan Quantity',
        locked: true,
        seedVisible: ALWAYS,
        value: (c) => String(c.qty),
    },
    {
        key: 'state',
        label: 'State',
        seedVisible: ALWAYS,
        value: (c) => c.slot.cellState,
    },
];

interface SkuRouteColOptions {
    editQtyMap: Map<string, number>;
    onQtyChange: (key: string, qty: number) => void;
    isReadonly: boolean;
    hidden: Set<string>;
    isAggregated: boolean;
    uniqueFromWhIds: string[];
    fromWhNameMap: Map<string, string>;
    demandSourceLabel: string;
    onRemoveRow?: (row: SkuRouteRow) => Promise<void>;
    selectedIds?: Set<string>;
    allIds?: string[];
    onToggleSelect?: (rowKey: string) => void;
    onToggleAll?: () => void;
    shipments?: PlanShipment[];
    channelMeasurementUnit?: 'weight' | 'volume' | null;
    pastSalesLookbackDays: number;
    pastSalesLoading: boolean;
    pastSalesError: boolean;
    /** Min source-WH stock to send. Cells with a set qty whose source stock is below this are warned. */
    minSourceStockUnits: number | null;
    /** Utilisation cap (0–1) — sendable per slot = floor(rawStock × this). Bounds the qty input + "avail". */
    maxOutboundUtilizationPct?: number | null;
    /** Live DoH projections keyed by `${productId}::${toWarehouseId ?? 'agg'}`. */
    dohByRowKey?: Map<
        string,
        { dest: number | null; internal: number | null; current: number | null }
    >;
    /** Editing the channel DoH recomputes (and applies) the suggested quantity for the row. */
    onDohEdit?: (row: SkuRouteRow, targetDohDays: number) => void;
    /** Bumped after each DoH preview resolves — forces the DoH input to re-sync to the clamped value. */
    dohEditNonce?: number;
    /** True while a preview is (re)computing this row's DoH cells (qty or DoH edit) — show a cell spinner. */
    isDohRecomputing?: (rowKey: string) => boolean;
    /** True while a DoH edit is recomputing this row's plan-qty cells — show a cell spinner. */
    isQtyRecomputing?: (rowKey: string) => boolean;
}

/** DoH map key for a row — destination/internal DoH are per (SKU × To WH). */
function dohKeyFor(productId: string, toWarehouseId: string | null): string {
    return `${productId}::${toWarehouseId ?? 'agg'}`;
}

/** Editable channel-DoH cell. Commits on blur / Enter; editing recomputes the suggested qty. */
const DohEditCell: FC<{
    value: number | null;
    onCommit: (v: number) => void;
    resetSignal?: number;
}> = ({ value, onCommit, resetSignal }) => {
    const [local, setLocal] = useState<number | string>(value ?? '');
    // Re-sync to the authoritative value on every value change AND every resetSignal bump. The signal
    // is essential: after an unreachable DoH is clamped back to the value the cell already had, `value`
    // is unchanged, so without the signal the user's (rejected) typed number would linger on screen.
    useEffect(() => {
        setLocal(value ?? '');
    }, [value, resetSignal]);
    const commit = () => {
        const n = Number(local);
        if (!Number.isNaN(n) && n >= 0 && n !== value) onCommit(n);
    };
    return (
        <NumberInput
            size="xs"
            value={local}
            onChange={setLocal}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
            }}
            placeholder="—"
            min={0}
            hideControls
            style={{ width: 80 }}
            styles={{ input: { textAlign: 'right', fontSize: 13 } }}
        />
    );
};

function buildSkuRouteColumns(
    opts: SkuRouteColOptions,
): DataSheetColumn<SkuRouteRow>[] {
    const {
        editQtyMap,
        onQtyChange,
        isReadonly,
        hidden,
        isAggregated,
        uniqueFromWhIds,
        fromWhNameMap,
        demandSourceLabel,
    } = opts;
    const cols: DataSheetColumn<SkuRouteRow>[] = [];

    if (
        !isReadonly &&
        opts.selectedIds &&
        opts.onToggleSelect &&
        opts.onToggleAll &&
        opts.allIds
    ) {
        const allSelected =
            opts.allIds.length > 0 &&
            opts.allIds.every((id) => opts.selectedIds!.has(id));
        const someSelected =
            opts.allIds.some((id) => opts.selectedIds!.has(id)) && !allSelected;
        cols.push({
            key: '_select',
            header: '',
            width: 40,
            sticky: true,
            // A checkbox column: no sort caret beside the tick, no hover filter
            // funnel over it, and a click on the tick must not sort the grid.
            sortable: false,
            filterable: false,
            renderHeader: () => (
                <Checkbox
                    size="xs"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={opts.onToggleAll!}
                />
            ),
            render: (_, row): ReactNode => {
                // Available rows are selectable too: the ⋮ "Set quantity = demand" action is how an
                // operator plans a SKU the engine didn't recommend, so it needs a checkbox.
                const rowKeys = rowSelectionKeys(row);
                if (rowKeys.length === 0) return <span />;
                const isChecked = rowKeys.every((id) =>
                    opts.selectedIds!.has(id),
                );
                return (
                    <Checkbox
                        size="xs"
                        checked={isChecked}
                        onChange={() => opts.onToggleSelect!(row.rowKey)}
                    />
                );
            },
        });
    }

    cols.push(
        {
            key: '_idx',
            header: '#',
            width: 44,
            sticky: true,
            render: (_, _r, idx): ReactNode => (
                <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                    {idx + 1}
                </Text>
            ),
        },
        {
            key: 'productName',
            header: 'Product',
            width: 220,
            sticky: true,
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <div>
                    <Text
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: neutralColors.textPrimary,
                        }}
                    >
                        {row.productName || row.productId}
                    </Text>
                    <Text
                        style={{ fontSize: 11, color: neutralColors.textMuted }}
                    >
                        {row.productId}
                    </Text>
                </div>
            ),
        },
    );

    // The ids this SKU is listed under on the channel. Off by default — operators plan by Reparo
    // product id and only need the marketplace id when handing the plan to the channel.
    if (!hidden.has('marketplaceSkuId')) {
        cols.push({
            key: 'marketplaceSkuId',
            header: 'Marketplace SKU ID',
            width: 170,
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{ fontSize: 13, color: neutralColors.textSecondary }}
                >
                    {row.marketplaceSkuId ?? '—'}
                </Text>
            ),
        });
    }

    if (!hidden.has('secondaryId')) {
        cols.push({
            key: 'secondaryId',
            header: 'Secondary ID',
            width: 150,
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{ fontSize: 13, color: neutralColors.textSecondary }}
                >
                    {row.secondaryId ?? '—'}
                </Text>
            ),
        });
    }

    if (!isAggregated && !hidden.has('toWH')) {
        cols.push({
            key: 'toWarehouseName',
            header: 'To',
            width: 140,
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{ fontSize: 13, color: neutralColors.textSecondary }}
                >
                    {row.toWarehouseName ?? '—'}
                </Text>
            ),
        });
    }

    if (opts.shipments && opts.shipments.length > 0) {
        cols.push({
            key: 'shipment',
            header: 'Shipment',
            width: 170,
            render: (_, row): ReactNode => {
                for (const slot of Object.values(row.fromWhSlots)) {
                    if (slot.saleOrderCode) {
                        const label = slot.saleOrderName ?? slot.saleOrderCode;
                        const showCode =
                            slot.saleOrderName &&
                            slot.saleOrderName !== slot.saleOrderCode;
                        return (
                            <div>
                                <Text
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: neutralColors.textPrimary,
                                    }}
                                >
                                    {label}
                                </Text>
                                {showCode && (
                                    <Text
                                        style={{
                                            fontSize: 10,
                                            color: neutralColors.textMuted,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {slot.saleOrderCode}
                                    </Text>
                                )}
                            </div>
                        );
                    }
                }
                return (
                    <Text
                        style={{ fontSize: 13, color: neutralColors.textMuted }}
                    >
                        —
                    </Text>
                );
            },
        });
    }

    if (!hidden.has('tier')) {
        cols.push({
            key: 'tier',
            header: 'Tier',
            width: 70,
            align: 'center',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{
                        fontSize: 13,
                        color: row.tier
                            ? neutralColors.textPrimary
                            : neutralColors.textMuted,
                    }}
                >
                    {row.tier ?? '—'}
                </Text>
            ),
        });
    }
    if (!hidden.has('channelStock')) {
        cols.push({
            key: '_channelStock',
            header: 'Channel Stock',
            width: 110,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => {
                // Planned rows read the live/frozen DoH map. Available rows have no transfer and so no
                // map entry — derive the same figure (stock ÷ daily rate) from the row's own numbers
                // rather than leaving the subtext blank on exactly the rows being triaged.
                const currentDoh =
                    opts.dohByRowKey?.get(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    )?.current ??
                    (row._channelStock != null &&
                    row.dailyDemand != null &&
                    row.dailyDemand > 0
                        ? row._channelStock / row.dailyDemand
                        : null);
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 2,
                        }}
                    >
                        <Text
                            style={{
                                fontSize: 13,
                                color:
                                    row._channelStock != null
                                        ? neutralColors.textPrimary
                                        : neutralColors.textMuted,
                            }}
                        >
                            {row._channelStock != null
                                ? formatUnits(Math.ceil(row._channelStock))
                                : '—'}
                        </Text>
                        {currentDoh != null && (
                            <Text
                                style={{
                                    fontSize: 10,
                                    color: neutralColors.textMuted,
                                    lineHeight: 1,
                                }}
                            >
                                {Math.floor(currentDoh)}d DoH
                            </Text>
                        )}
                    </div>
                );
            },
        });
    }
    if (!hidden.has('demandToMeet')) {
        cols.push({
            key: 'demandToMeet',
            header: `Demand (${demandSourceLabel})`,
            width: 140,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            row.demandToMeet != null
                                ? neutralColors.textSecondary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.demandToMeet != null
                        ? formatUnits(Math.ceil(row.demandToMeet))
                        : '—'}
                </Text>
            ),
        });
    }
    // Reference-only, PO plans: what the order is worth per SKU and what this plan sends of it,
    // before tax. No recommendation is drawn from either.
    if (!hidden.has('orderedValue')) {
        cols.push({
            key: 'orderedValue',
            header: 'Ordered Value',
            width: 140,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text style={{ fontSize: 13, color: row.orderedValue != null ? neutralColors.textSecondary : neutralColors.textMuted }}>
                    {row.orderedValue != null ? formatMoney(row.orderedValue) : '—'}
                </Text>
            ),
        });
    }
    if (!hidden.has('sendingValue')) {
        cols.push({
            key: 'sendingValue',
            header: 'Sending Value',
            width: 140,
            align: 'right',
            sortable: true,
            filterable: true,
            valueAccessor: (row) => rowSendingValue(row, editQtyMap),
            render: (_, row): ReactNode => {
                const value = rowSendingValue(row, editQtyMap);
                return (
                    <Text style={{ fontSize: 13, color: value != null ? neutralColors.textSecondary : neutralColors.textMuted }}>
                        {value != null ? formatMoney(value) : '—'}
                    </Text>
                );
            },
        });
    }
    // The per-day rate the Demand column was built from, under the plan's own signal. Replaces the
    // side-by-side Forecast Demand / DRR Demand columns: the signal is already chosen upstream, so
    // only that one demand figure and the rate behind it are shown.
    if (!hidden.has('dailyDemand')) {
        cols.push({
            key: 'dailyDemand',
            header: `Daily Rate (${demandSourceLabel})`,
            width: 130,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            row.dailyDemand != null
                                ? neutralColors.textSecondary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.dailyDemand != null
                        ? `${row.dailyDemand.toFixed(1)}/d`
                        : '—'}
                </Text>
            ),
        });
    }
    if (!hidden.has('pastSalesQty')) {
        cols.push({
            key: 'pastSalesQty',
            header: `Past Sales (${opts.pastSalesLookbackDays}d)`,
            width: 130,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => {
                // Absent while loading → '…'; fetch failed → '—' (not a misleading 0);
                // absent once loaded → 0 (genuinely no outgoing in the window).
                if (row.pastSalesQty == null) {
                    const txt = opts.pastSalesLoading
                        ? '…'
                        : opts.pastSalesError
                          ? '—'
                          : '0';
                    return (
                        <Tooltip
                            label="Couldn't load past sales — check the connection / retry"
                            withArrow
                            disabled={!opts.pastSalesError}
                        >
                            <Text
                                style={{
                                    fontSize: 13,
                                    color: neutralColors.textMuted,
                                }}
                            >
                                {txt}
                            </Text>
                        </Tooltip>
                    );
                }
                return (
                    <Text
                        style={{
                            fontSize: 13,
                            color: neutralColors.textSecondary,
                            opacity: opts.pastSalesLoading ? 0.5 : 1,
                        }}
                    >
                        {formatUnits(Math.ceil(row.pastSalesQty))}
                    </Text>
                );
            },
        });
    }
    if (!hidden.has('inTransit')) {
        cols.push({
            key: 'inTransit',
            header: 'In-Transit',
            width: 100,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            (row.inTransit ?? 0) > 0
                                ? semanticColors.info.primary
                                : neutralColors.textMuted,
                    }}
                >
                    {(row.inTransit ?? 0) > 0
                        ? formatUnits(Math.ceil(row.inTransit!))
                        : '—'}
                </Text>
            ),
        });
    }
    if (!hidden.has('leadTimeDemand')) {
        cols.push({
            key: 'leadTimeDemand',
            header: 'Lead Time Demand',
            width: 150,
            align: 'right',
            sortable: true,
            filterable: true,
            render: (_, row): ReactNode => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            row.leadTimeDemand != null
                                ? neutralColors.textSecondary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.leadTimeDemand != null
                        ? formatUnits(Math.ceil(row.leadTimeDemand))
                        : '—'}
                </Text>
            ),
        });
    }

    // Stock delta — total gap that gets split across From WHs (always shown)
    if (!hidden.has('channelDohAfter')) {
        cols.push({
            key: 'channelDohAfter',
            header: 'Channel DoH (after)',
            width: 140,
            align: 'right',
            sortable: true,
            filterable: true,
            // Filter/sort on the DISPLAYED (floored) value so ">0" matches what the cell shows.
            valueAccessor: (row): number | null => {
                const d = opts.dohByRowKey?.get(
                    dohKeyFor(row.productId, row.toWarehouseId),
                )?.dest;
                return d == null ? null : Math.floor(d);
            },
            render: (_, row): ReactNode => {
                const doh =
                    opts.dohByRowKey?.get(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    )?.dest ?? null;
                if (
                    opts.isDohRecomputing?.(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    )
                ) {
                    return <Loader size={12} color="violet" />;
                }
                if (isReadonly || !opts.onDohEdit) {
                    return (
                        <Text
                            style={{
                                fontSize: 13,
                                color:
                                    doh != null
                                        ? neutralColors.textSecondary
                                        : neutralColors.textMuted,
                            }}
                        >
                            {doh != null ? `${Math.floor(doh)}d` : '—'}
                        </Text>
                    );
                }
                return (
                    <DohEditCell
                        value={doh}
                        onCommit={(v) => opts.onDohEdit!(row, v)}
                        resetSignal={opts.dohEditNonce}
                    />
                );
            },
        });
    }
    if (!hidden.has('internalDohAfter')) {
        cols.push({
            key: 'internalDohAfter',
            header: 'Internal DoH (after)',
            width: 150,
            align: 'right',
            sortable: true,
            filterable: true,
            // Filter/sort on the DISPLAYED (floored) value so ">0" matches what the cell shows.
            valueAccessor: (row): number | null => {
                const d = opts.dohByRowKey?.get(
                    dohKeyFor(row.productId, row.toWarehouseId),
                )?.internal;
                return d == null ? null : Math.floor(d);
            },
            render: (_, row): ReactNode => {
                const doh =
                    opts.dohByRowKey?.get(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    )?.internal ?? null;
                if (
                    opts.isDohRecomputing?.(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    )
                ) {
                    return <Loader size={12} color="violet" />;
                }
                return (
                    <Tooltip
                        label="Days the internal pool covers OTHER channels' demand once this qty is earmarked"
                        withArrow
                    >
                        <Text
                            style={{
                                fontSize: 13,
                                color:
                                    doh != null
                                        ? neutralColors.textSecondary
                                        : neutralColors.textMuted,
                            }}
                        >
                            {doh != null ? `${Math.floor(doh)}d` : '—'}
                        </Text>
                    </Tooltip>
                );
            },
        });
    }

    if (!hidden.has('networkInTransit')) {
        cols.push({
            key: 'networkInTransit',
            header: 'Network In-Transit',
            width: 150,
            align: 'right',
            sortable: true,
            filterable: true,
            valueAccessor: (row): number | null => row.networkInTransit ?? null,
            render: (_, row): ReactNode => (
                <Tooltip
                    label="Inbound POs arriving across all internal warehouses within the internal-DoH floor window (credited into Internal DoH after)"
                    withArrow
                >
                    <Text
                        style={{
                            fontSize: 13,
                            color:
                                row.networkInTransit != null
                                    ? neutralColors.textSecondary
                                    : neutralColors.textMuted,
                        }}
                    >
                        {row.networkInTransit != null
                            ? formatUnits(Math.round(row.networkInTransit))
                            : '—'}
                    </Text>
                </Tooltip>
            ),
        });
    }

    cols.push({
        key: 'destinationPlanQty',
        header: 'Stock Delta',
        width: 110,
        align: 'right',
        sortable: true,
        filterable: true,
        render: (_, row): ReactNode => (
            <Text
                style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color:
                        row.destinationPlanQty != null
                            ? neutralColors.textPrimary
                            : neutralColors.textMuted,
                }}
            >
                {row.destinationPlanQty != null
                    ? formatUnits(Math.ceil(row.destinationPlanQty))
                    : '—'}
            </Text>
        ),
    });

    // Dynamic From WH columns — one per unique source warehouse in the plan
    const { channelMeasurementUnit } = opts;
    for (const fromWhId of uniqueFromWhIds) {
        const whName = fromWhNameMap.get(fromWhId) ?? fromWhId;
        const colKey = `fromWh_${fromWhId}`;
        cols.push({
            key: colKey,
            header: whName,
            // Wide enough for the two-line "avail" caption (total, then the on-hand + in-transit
            // split) at 10px without clipping. Past the viewport the sheet scrolls sideways.
            width: 184,
            align: 'right',
            // Two-line header ("Plan Qty" over the source WH name) instead of a group-header row,
            // which saved a full row of vertical space. The "Plan Qty" line inherits the standard
            // header typography from the <th>; the WH name is a smaller secondary line below it.
            renderHeader: (): ReactNode => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        lineHeight: 1.25,
                    }}
                >
                    <span>Plan Qty</span>
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 500,
                            textTransform: 'none',
                            letterSpacing: 0,
                            color: neutralColors.textMuted,
                        }}
                    >
                        {whName}
                    </span>
                </div>
            ),
            render: (_, row): ReactNode => {
                const slot = row.fromWhSlots[fromWhId];
                if (!slot) return <span />;
                const editKey =
                    slot.transferId ??
                    `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                // A DoH edit recomputes this row's plan qty — spin + lock the qty inputs until it lands so
                // the user doesn't type over a value that's about to be replaced by the engine's split.
                const qtyBusy =
                    opts.isQtyRecomputing?.(
                        dohKeyFor(row.productId, row.toWarehouseId),
                    ) ?? false;
                const qtyBusyProps = qtyBusy
                    ? {
                          disabled: true,
                          rightSection: <Loader size={10} color="violet" />,
                      }
                    : {};
                const editedQty = editQtyMap.get(editKey);
                const isEdited = editedQty !== undefined;
                const cellQty =
                    editedQty ?? slot.approvedQty ?? slot.recommendedQty;
                // Effective (sendable) stock for this slot — on-hand and inbound in-transit each capped by
                // the utilisation %, then summed. Shown as "avail"; a qty above it is flagged red, never
                // clamped. In-transit is 0 unless the channel enables it.
                const rawStock = slot.fromWarehouseStock;
                const maxUtil = opts.maxOutboundUtilizationPct ?? 1;
                const inTransit = slot.fromWhInTransit ?? 0;
                const onHandCapped: number | null =
                    rawStock == null ? null : Math.floor(rawStock * maxUtil);
                const inTransitCapped = Math.floor(inTransit * maxUtil);
                const effAvail = slotSendableCeiling(slot, maxUtil);
                const overAvail = effAvail != null && cellQty > effAvail;
                // Captions under the qty input. Each stays on one line but is clipped to the cell
                // (ellipsis + full text on hover) so a long figure can never bleed into the next column.
                const captionStyle = {
                    fontSize: 10,
                    color: neutralColors.textMuted,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap' as const,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                };
                // "avail" subtext: total sendable, then the two levers on their own line when
                // in-transit contributes.
                const availSubtext = (): ReactNode => {
                    if (effAvail == null) return null;
                    const breakdown =
                        inTransitCapped > 0 && onHandCapped != null
                            ? `${formatUnits(onHandCapped)} on-hand + ${formatUnits(inTransitCapped)} in-transit`
                            : null;
                    return (
                        <>
                            <Text
                                style={{
                                    ...captionStyle,
                                    ...(overAvail
                                        ? {
                                              color: 'var(--mantine-color-red-7)',
                                              fontWeight: 600,
                                          }
                                        : {}),
                                }}
                            >
                                avail: {formatUnits(effAvail)}
                            </Text>
                            {breakdown && (
                                <Text style={captionStyle} title={breakdown}>
                                    {breakdown}
                                </Text>
                            )}
                        </>
                    );
                };
                // Warn (amber) when a set qty draws on a source whose on-hand + in-transit is below the send
                // threshold. Unsendable cells are already shown red by the compute-time exclusion, so skip them.
                const combinedStock = (rawStock ?? 0) + inTransit;
                const belowThreshold =
                    opts.minSourceStockUnits != null &&
                    slot.cellState !== 'unsendable' &&
                    rawStock != null &&
                    combinedStock < opts.minSourceStockUnits &&
                    cellQty > 0;
                const inputStyle = {
                    textAlign: 'right' as const,
                    fontSize: 13,
                    ...(overAvail
                        ? {
                              borderColor: 'var(--mantine-color-red-6)',
                              backgroundColor: 'rgba(224,49,49,0.06)',
                              color: 'var(--mantine-color-red-8)',
                              fontWeight: 600,
                          }
                        : belowThreshold
                          ? {
                                borderColor: 'var(--mantine-color-orange-5)',
                                backgroundColor: 'rgba(245,159,0,0.06)',
                            }
                          : isEdited
                            ? {
                                  borderColor: 'var(--mantine-color-violet-5)',
                                  backgroundColor: 'rgba(121,80,242,0.04)',
                              }
                            : {}),
                };
                // Red note under a qty above the source's sendable stock — the operator is sending over
                // what the warehouse holds. Kept through approval and the push; nothing clamps it.
                const overAvailNote = overAvail ? (
                    <Text
                        style={{
                            fontSize: 10,
                            color: 'var(--mantine-color-red-7)',
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                        }}
                        title={`${formatUnits(cellQty)} planned vs ${formatUnits(effAvail ?? 0)} available at the source`}
                    >
                        over avail by {formatUnits(cellQty - (effAvail ?? 0))}
                    </Text>
                ) : null;
                const belowMinTooltip = `Source stock ${Math.floor(combinedStock)}${inTransit > 0 ? ' (incl. in-transit)' : ''} is below the minimum of ${opts.minSourceStockUnits} — may be capped or dropped at approval`;
                const belowMinNote = belowThreshold ? (
                    <Text
                        style={{
                            fontSize: 10,
                            color: 'var(--mantine-color-orange-7)',
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        below min ({opts.minSourceStockUnits})
                    </Text>
                ) : null;

                const cellBox = (children: ReactNode) => (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 2,
                            // Bound the captions to the cell so their ellipsis has something to clip against.
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {children}
                    </div>
                );
                const subtext = (label: string, val: number) => (
                    <Text style={captionStyle}>
                        {label}: {formatUnits(Math.ceil(val))}
                    </Text>
                );
                const measurementSubtext = (qty: number) => {
                    if (!channelMeasurementUnit || !slot.measurementValue)
                        return null;
                    const total = qty * slot.measurementValue;
                    const formatted =
                        channelMeasurementUnit === 'weight'
                            ? total >= 1000
                                ? `${(total / 1000).toFixed(2)}kg`
                                : `${total.toFixed(0)}g`
                            : `${total.toFixed(4)} CBM`;
                    return <Text style={captionStyle}>{formatted}</Text>;
                };

                if (isReadonly) {
                    const qty = slot.approvedQty ?? slot.recommendedQty;
                    if (qty === 0 && slot.cellState === 'available')
                        return cellBox(
                            <>
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: neutralColors.textMuted,
                                        display: 'block',
                                        textAlign: 'right',
                                    }}
                                >
                                    —
                                </Text>
                                {availSubtext()}
                            </>,
                        );
                    return cellBox(
                        <>
                            <Text
                                style={{
                                    fontSize: 13,
                                    color: neutralColors.textPrimary,
                                }}
                            >
                                {formatUnits(qty)}
                            </Text>
                            {availSubtext()}
                            {measurementSubtext(qty)}
                        </>,
                    );
                }

                if (slot.cellState === 'unsendable') {
                    const qty = editedQty ?? 0;
                    return cellBox(
                        <>
                            <Tooltip
                                label="Nothing sendable from this source right now — set a qty to send anyway"
                                withArrow
                            >
                                <NumberInput
                                    size="xs"
                                    value={qty === 0 ? '' : qty}
                                    onChange={(v) =>
                                        onQtyChange(editKey, Number(v) || 0)
                                    }
                                    placeholder="0"
                                    min={0}
                                    hideControls
                                    style={{ width: 90 }}
                                    styles={{ input: inputStyle }}
                                    {...qtyBusyProps}
                                />
                            </Tooltip>
                            {subtext('rec', 0)}
                            {availSubtext()}
                            {overAvailNote}
                            {qty > 0 && measurementSubtext(qty)}
                        </>,
                    );
                }

                if (slot.cellState === 'available') {
                    const qty = editedQty ?? 0;
                    return cellBox(
                        <>
                            <Tooltip
                                label={belowMinTooltip}
                                withArrow
                                disabled={!belowThreshold}
                            >
                                <NumberInput
                                    size="xs"
                                    value={editedQty ?? ''}
                                    onChange={(v) =>
                                        onQtyChange(editKey, Number(v) || 0)
                                    }
                                    placeholder="0"
                                    min={0}
                                    hideControls
                                    style={{ width: 90 }}
                                    styles={{ input: inputStyle }}
                                    {...qtyBusyProps}
                                />
                            </Tooltip>
                            {availSubtext()}
                            {overAvailNote}
                            {belowMinNote}
                            {qty > 0 && measurementSubtext(qty)}
                        </>,
                    );
                }

                // recommended
                const qty =
                    editedQty ?? slot.approvedQty ?? slot.recommendedQty;
                return cellBox(
                    <>
                        <Tooltip
                            label={belowMinTooltip}
                            withArrow
                            disabled={!belowThreshold}
                        >
                            <NumberInput
                                size="xs"
                                value={qty === 0 ? '' : qty}
                                onChange={(v) =>
                                    onQtyChange(editKey, Number(v) || 0)
                                }
                                placeholder="0"
                                min={0}
                                hideControls
                                style={{ width: 90 }}
                                styles={{ input: inputStyle }}
                                {...qtyBusyProps}
                            />
                        </Tooltip>
                        {availSubtext()}
                        {overAvailNote}
                        {isEdited && subtext('rec', slot.recommendedQty)}
                        {belowMinNote}
                        {qty > 0 && measurementSubtext(qty)}
                    </>,
                );
            },
        });
    }

    if (!isReadonly && opts.onRemoveRow) {
        cols.push({
            key: '_delete',
            header: '',
            width: 44,
            render: (_, row): ReactNode => {
                const hasTransfer = Object.values(row.fromWhSlots).some(
                    (s) => s.transferId !== null,
                );
                if (!hasTransfer) return <span />;
                return (
                    <ActionIcon
                        variant="subtle"
                        color="red"
                        size="xs"
                        onClick={() => void opts.onRemoveRow!(row)}
                    >
                        <Trash size={13} />
                    </ActionIcon>
                );
            },
        });
    }

    return cols;
}

// ─── Main component ────────────────────────────────────────────────────────────

const DistributionPlanView: FC<Props> = ({
    params,
    existingPlan,
    existingPlanId,
    fromAlertId,
    existingPlanName,
    initialShipmentCode,
    channelTierTargetDohDays: propChannelTierTargetDohDays,
    channelDefaultMinSourceStockUnits,
    channelDefaultMaxOutboundUtilizationPct,
    channelDefaultInboundInTransitDays,
    channelDefaultSourceReserveUnits,
    channelDefaultMinInternalDohDays,
    channelDefaultMilestone1Days,
    channelDefaultMilestone2Days,
    channelDefaultMaxUnitsPerShipment,
    channelDefaultMaxMeasurementPerShipment,
    channelMeasurementUnit,
    onBack,
    onSaveDraft,
    onDelete,
}) => {
    const hasExisting = !!(existingPlan || existingPlanId);
    // undefined = not yet fetched; null = fetched, not configured; object = fetched with tier config
    const [fetchedChannelTierConfig, setFetchedChannelTierConfig] = useState<
        Record<string, number> | null | undefined
    >(undefined);
    const effectiveTierConfig =
        fetchedChannelTierConfig !== undefined
            ? fetchedChannelTierConfig
            : (params?.channelTierTargetDohDays ??
              propChannelTierTargetDohDays ??
              null);
    // ── All hooks at the top (no conditional hook calls) ──
    const [viewState, setViewState] = useState<ViewState>(
        hasExisting ? 'plan' : 'settings',
    );
    const [plan, setPlan] = useState<DistributionPlanDetail | null>(
        existingPlan ?? null,
    );
    // The PO an OPEN_PO plan is scoped to. A fresh plan carries it in params; a recompute of an
    // existing plan reads it back off the plan (params are gone by then), so the same PO scope is
    // reproduced instead of widening to every open channel PO.
    const resolvedPurchaseOrderId =
        params?.purchaseOrderId ?? plan?.sourcePurchaseOrderId ?? undefined;
    const [loadingPlan, setLoadingPlan] = useState(
        !!(existingPlanId && !existingPlan),
    );
    // Warehouse scope for non-aggregated plans
    const [selectedPlanWarehouseIds, setSelectedPlanWarehouseIds] = useState<
        string[]
    >(
        params?.preselectedWarehouseIds ??
            existingPlan?.scopedWarehouseIds ??
            [],
    );
    const [planWarehouses, setPlanWarehouses] = useState<
        Array<{ warehouseId: string; name: string }>
    >([]);
    const [internalWarehouses, setInternalWarehouses] = useState<
        Array<{ warehouseId: string; name: string }>
    >([]);
    const [warehouseRules, setWarehouseRules] = useState<WarehouseDohRule[]>(
        [],
    );
    // Tier mode: channel-level config OR any warehouse rule has tier DOH — checked after warehouseRules is declared
    const channelTierMode =
        effectiveTierConfig != null ||
        warehouseRules.some((r) => r.tierTargetDohDays != null);
    const [planWarehouseOptionsLoading, setPlanWarehouseOptionsLoading] =
        useState(false);
    // Aggregated path selection
    const [availablePaths, setAvailablePaths] = useState<
        Array<{ id: string; fromWarehouseName: string; leadTimeDays: number }>
    >([]);
    const [pathsLoading, setPathsLoading] = useState(false);
    const [selectedPathIds, setSelectedPathIds] = useState<string[]>([]);
    // Non-aggregated eligible-source (from-WH) selection + the raw paths that define the source universe.
    const [selectedSourceWarehouseIds, setSelectedSourceWarehouseIds] =
        useState<string[]>(existingPlan?.scopedSourceWarehouseIds ?? []);
    const [channelPaths, setChannelPaths] = useState<
        Array<{ fromWarehouseId: string; toWarehouseId: string | null }>
    >([]);
    const [planRuleOverrides, setPlanRuleOverrides] = useState<
        Record<string, PlanRuleGroupOverride>
    >({});
    const [deleting, setDeleting] = useState(false);
    const [editQtyMap, setEditQtyMap] = useState<Map<string, number>>(
        new Map(),
    );
    // Live DoH projections per row (`${productId}::${toWarehouseId ?? 'agg'}`), filled by the preview endpoint.
    const [dohByRowKey, setDohByRowKey] = useState<
        Map<
            string,
            {
                dest: number | null;
                internal: number | null;
                current: number | null;
            }
        >
    >(new Map());
    // DoH-edit results pinned per row so the qty-mode refresh can't optically drift them.
    // Cleared when the user manually edits a quantity on that row.
    const [pinnedDoh, setPinnedDoh] = useState<
        Map<
            string,
            {
                dest: number | null;
                internal: number | null;
                current: number | null;
            }
        >
    >(new Map());
    // Bumped whenever a DoH-edit preview resolves, so DohEditCell re-syncs its input to the clamped
    // value even when the clamp equals the value the cell already showed.
    const [dohEditNonce, setDohEditNonce] = useState(0);

    // Per-row "recomputing" tracker for the preview-driven cells, so an edit shows a spinner ONLY on the
    // columns that edit can change — never the whole page — while the rest stay interactive. A row is
    // recomputing while pending > resolved. `scope` = 'doh' (a qty edit recomputes just the DoH cells)
    // or 'both' (a DoH edit recomputes the plan-qty cells too). Monotonic seqs make it race-proof: a
    // superseded/older preview resolving can't clear a newer edit's spinner.
    type RowRecompute = {
        pending: number;
        resolved: number;
        scope: 'doh' | 'both';
    };
    const [rowRecompute, setRowRecompute] = useState<Map<string, RowRecompute>>(
        new Map(),
    );
    const recomputeSeqRef = useRef(0);
    // rowKey → the qty last sent to the DoH preview, so editing one row never re-previews (or spins)
    // the others — only rows whose qty actually changed recompute (qty-mode DoH is independent per row).
    const lastPreviewedQtyRef = useRef<Map<string, number>>(new Map());
    const beginRecompute = useCallback(
        (rowKeys: string[], scope: 'doh' | 'both'): number => {
            const seq = ++recomputeSeqRef.current;
            setRowRecompute((prev) => {
                const next = new Map(prev);
                for (const k of rowKeys) {
                    const ex = next.get(k);
                    // Don't downgrade an in-flight 'both' (DoH edit) to 'doh' (the redundant qty-mode preview).
                    const keepScope =
                        ex && ex.pending > ex.resolved && ex.scope === 'both'
                            ? 'both'
                            : scope;
                    next.set(k, {
                        pending: seq,
                        resolved: ex?.resolved ?? 0,
                        scope: keepScope,
                    });
                }
                return next;
            });
            return seq;
        },
        [],
    );
    const endRecompute = useCallback((rowKeys: string[], seq: number) => {
        setRowRecompute((prev) => {
            const next = new Map(prev);
            for (const k of rowKeys) {
                const ex = next.get(k);
                if (ex)
                    next.set(k, {
                        ...ex,
                        resolved: Math.max(ex.resolved, seq),
                    });
            }
            return next;
        });
    }, []);
    const isDohRecomputing = useCallback(
        (rowKey: string): boolean => {
            const r = rowRecompute.get(rowKey);
            return !!r && r.pending > r.resolved;
        },
        [rowRecompute],
    );
    const isQtyRecomputing = useCallback(
        (rowKey: string): boolean => {
            const r = rowRecompute.get(rowKey);
            return !!r && r.pending > r.resolved && r.scope === 'both';
        },
        [rowRecompute],
    );
    // True while ANY edited row is still settling (debounce window included — beginRecompute is called
    // synchronously on edit, before the 400ms timer). Saving mid-flight would persist pre-preview
    // numbers: a DoH edit only writes its qty back once the preview resolves, and a save clears
    // editQtyMap out from under any preview still in the air. So Save Draft waits for the rows to land.
    const isAnyRowRecomputing = useMemo(() => {
        for (const r of rowRecompute.values()) {
            if (r.pending > r.resolved) return true;
        }
        return false;
    }, [rowRecompute]);

    const [selectedWarehouseId, setSelectedWarehouseId] = useState<
        string | null
    >(null);
    const [settingsOpen, { open: openSettings, close: closeSettings }] =
        useDisclosure(false);
    const [hiddenPlanColsChoice, setHiddenPlanCols] = useState<Set<string>>(
        new Set([
            'marketplaceSkuId',
            'secondaryId',
            'tier',
            'dailyDemand',
            'inTransit',
            'pastSalesQty',
            'networkInTransit',
        ]),
    );
    const isPoPlan = !!resolvedPurchaseOrderId;
    const hiddenPlanCols = useMemo(
        () => (isPoPlan ? hiddenPlanColsChoice : new Set([...hiddenPlanColsChoice, ...PO_PLAN_ONLY_COLS])),
        [hiddenPlanColsChoice, isPoPlan],
    );
    // Order price reference: live (no-recompute) per-SKU price off the plan's order.
    const [poPriceMap, setPoPriceMap] = useState<Map<string, B2bPoLine>>(new Map());
    useEffect(() => {
        if (!resolvedPurchaseOrderId) return;
        let cancelled = false;
        getB2bPoLines(resolvedPurchaseOrderId)
            .then((lines) => {
                if (!cancelled) setPoPriceMap(new Map(lines.map((line) => [line.productId, line])));
            })
            .catch((err: unknown) => {
                // A failed fetch reads as unpriced ('—'), never as a value of 0.
                console.error('Failed to load order price reference', err);
                if (!cancelled) setPoPriceMap(new Map());
            });
        return () => {
            cancelled = true;
        };
    }, [resolvedPurchaseOrderId]);
    // Past-sales reference column: persisted lookback window + live (no-recompute) per-(store,SKU) data.
    const [pastSalesLookbackDays, setPastSalesLookbackDays] =
        usePersistentState<number>(
            'reparo:distribution-past-sales-lookback',
            7,
        );
    const [pastSalesMap, setPastSalesMap] = useState<Map<string, number>>(
        new Map(),
    );
    const [pastSalesLoading, setPastSalesLoading] = useState(false);
    const [pastSalesError, setPastSalesError] = useState(false);
    // productId -> the ids this SKU is listed under on this plan's channel. Fetched channel-wide
    // (not per plan row) because the grid also renders channel-health SKUs that have no transfer
    // line yet, and ungated by column visibility so the CSV picker can select the column
    // independently of the grid toggle without racing a lazy fetch.
    const [channelMappingMap, setChannelMappingMap] = useState<
        Map<string, ChannelProductMapping>
    >(new Map());
    const [
        downloadModalOpen,
        { open: openDownloadModal, close: closeDownloadModal },
    ] = useDisclosure(false);
    // Remembered per browser; null until the user makes a first explicit choice.
    const [savedExportCols, setSavedExportCols] = usePersistentState<
        string[] | null
    >('reparo:distribution-plan-export-cols', null);
    const [draftExportCols, setDraftExportCols] = useState<Set<string>>(
        new Set(),
    );
    const [skuContextMap, setSkuContextMap] = useState<
        Map<string, DistributionSkuHealth>
    >(new Map());
    const [channelHealthMeta, setChannelHealthMeta] = useState<{
        minProcessDays: number;
        maxProcessDays: number;
    } | null>(null);
    // Bumped after every recompute to force a channel-health refetch with the new plan settings —
    // plan.id is unchanged on recompute, so the effect below won't re-fire without this nonce.
    const [channelHealthNonce, setChannelHealthNonce] = useState(0);
    const [savingDraft, setSavingDraft] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [bulkUploadPhase, setBulkUploadPhase] = useState<'idle' | 'done'>(
        'idle',
    );
    const [bulkUploadResult, setBulkUploadResult] = useState<{
        updated: number;
        added: number;
        skipped: Array<{ productId: string; reason: string }>;
        duplicates: string[];
    } | null>(null);
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState('');
    const [approvePlanOpen, setApprovePlanOpen] = useState(false);
    const [createSaleOrdersOpen, setCreateSaleOrdersOpen] = useState(false);
    const [sendPlanOpen, setSendPlanOpen] = useState(false);
    const [emailEnabled, setEmailEnabled] = useState(false);
    const [emailDispatchDate, setEmailDispatchDate] = useState('');
    const [emailReceivingDate, setEmailReceivingDate] = useState('');
    const [emailDrafts, setEmailDrafts] = useState<EmailDraft[]>([]);
    const [emailCombined, setEmailCombined] = useState(false);
    const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
    const [emailPreviewError, setEmailPreviewError] = useState<string | null>(
        null,
    );
    const [lastAddedProductId, setLastAddedProductId] = useState<string | null>(
        null,
    );
    const [selectedTransferIds, setSelectedTransferIds] = useState<Set<string>>(
        new Set(),
    );
    const [stateFilters, setStateFilters] = useState<UnifiedRowState[]>([]);
    // OPEN_PO plans: restrict the table to the SKUs on the plan's scoped PO.
    const [poRowsOnly, setPoRowsOnly] = useState(false);
    const [shipments, setShipments] = useState<PlanShipment[]>([]);
    const [selectedShipmentCode, setSelectedShipmentCode] = useState<
        string | null
    >(initialShipmentCode ?? null);
    const [dsFilteredRows, setDsFilteredRows] = useState<SkuRouteRow[] | null>(
        null,
    );
    // (unplanned-tab state removed — unified table uses route filter only)
    const lastAddedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    // Settings state — seeded from params (new plan) or from existing plan data
    // Settings pinned by an alert deep link win over the channel defaults for a NEW plan, so the
    // plan computed on arrival is the one the alert listed. Absent otherwise.
    const pinned: PlanSettingsOverride = params?.planSettings ?? {};
    const [demandSource, setDemandSource] = useState<DemandSource>(
        (pinned.demandSource ??
            params?.demandSource ??
            existingPlan?.demandSource ??
            'FORECAST') as DemandSource,
    );
    // `||`, not `??`: a target DoH is only meaningful above zero, so a stored 0 means "never
    // configured" and must stay unset — sending it back would mask the channel/per-FC config.
    const [targetDohDays, setTargetDohDays] = useState<number | null>(
        existingPlan?.targetDohDays || pinned.targetDohDays || params?.targetDohDays || null,
    );
    const [milestone1DaysOverride, setMilestone1DaysOverride] = useState<
        number | null
    >(existingPlan?.milestone1DaysOverride ?? pinned.milestone1DaysOverride ?? null);
    const [milestone2DaysOverride, setMilestone2DaysOverride] = useState<
        number | null
    >(existingPlan?.milestone2DaysOverride ?? pinned.milestone2DaysOverride ?? null);
    const [drrLookbackDays, setDrrLookbackDays] = useState<number | null>(
        existingPlan?.drrLookbackDays ?? pinned.drrLookbackDays ?? null,
    );
    // DRR control panel: explicit sales window + growth %, frozen on the plan like the lookback.
    const [drrWindowStart, setDrrWindowStart] = useState<string | null>(
        existingPlan?.drrWindowStart ?? pinned.drrWindowStart ?? null,
    );
    const [drrWindowEnd, setDrrWindowEnd] = useState<string | null>(
        existingPlan?.drrWindowEnd ?? pinned.drrWindowEnd ?? null,
    );
    const [drrGrowthPct, setDrrGrowthPct] = useState<number | null>(
        existingPlan?.drrGrowthPct ?? pinned.drrGrowthPct ?? null,
    );
    const handleDrrWindowChange = useCallback(
        (start: string | null, end: string | null) => {
            setDrrWindowStart(start);
            setDrrWindowEnd(end);
        },
        [],
    );
    // Only a COMPLETE range is sent — a half-picked one would be rejected server-side, and
    // silently falling back to the rolling lookback is the right thing while the user is picking.
    const effectiveDrrWindow = useMemo(
        () =>
            drrWindowStart && drrWindowEnd
                ? { start: drrWindowStart, end: drrWindowEnd }
                : null,
        [drrWindowStart, drrWindowEnd],
    );
    const [minSourceStockUnitsOverride, setMinSourceStockUnitsOverride] =
        useState<number | null>(existingPlan?.minSourceStockUnits ?? pinned.minSourceStockUnitsOverride ?? null);
    const [
        maxOutboundUtilizationPctOverride,
        setMaxOutboundUtilizationPctOverride,
    ] = useState<number | null>(
        existingPlan?.maxOutboundUtilizationPct ?? pinned.maxOutboundUtilizationPctOverride ?? null,
    );
    const [inboundInTransitDaysOverride, setInboundInTransitDaysOverride] =
        useState<number | null>(existingPlan?.inboundInTransitDays ?? pinned.inboundInTransitDaysOverride ?? null);
    const [sourceReserveUnitsOverride, setSourceReserveUnitsOverride] =
        useState<number | null>(existingPlan?.sourceReserveUnits ?? pinned.sourceReserveUnitsOverride ?? null);
    const [minInternalDohDaysOverride, setMinInternalDohDaysOverride] =
        useState<number | null>(existingPlan?.minInternalDohDays ?? pinned.minInternalDohDaysOverride ?? null);
    const [maxUnitsPerShipmentOverride, setMaxUnitsPerShipmentOverride] =
        useState<number | null>(existingPlan?.maxUnitsPerShipment ?? pinned.maxUnitsPerShipmentOverride ?? null);
    const [
        maxMeasurementPerShipmentOverride,
        setMaxMeasurementPerShipmentOverride,
    ] = useState<number | null>(
        existingPlan?.maxMeasurementPerShipment ?? pinned.maxMeasurementPerShipmentOverride ?? null,
    );
    const [tierDohOverride, setTierDohOverride] = useState<
        Record<string, number | ''>
    >(() => {
        // Seed from plan's persisted tier overrides when re-computing an existing plan,
        // otherwise fall back to channel config as the starting point.
        const seed = existingPlan?.tierTargetDohDays ?? effectiveTierConfig;
        if (!seed) return {};
        return Object.fromEntries(
            Object.entries(seed).map(([t, v]) => [t, v as number | '']),
        );
    });

    const effectiveIsAggregated: boolean =
        params?.isAggregated ?? plan?.isAggregated ?? true;

    // For an existing (loaded) plan the tier-vs-flat DoH basis is sacrosanct: it comes from the
    // plan itself (tier overrides present ⇒ tier, else the plan's flat targetDohDays), NOT the
    // current channel config — which may have since switched to tiers and would otherwise make the
    // DoH preview recompute on a different horizon than the plan was sized with. Falls back to the
    // config-derived channelTierMode only when composing a brand-new plan.
    const planTierMode = plan
        ? plan.tierTargetDohDays != null
        : channelTierMode;

    // Tier-DoH + per-warehouse flat-DoH overrides for the DoH/qty PREVIEW, so it runs the engine
    // with the same settings as a compute. (handleCompute builds its own locals + tier-DoH map.)
    const previewTierOverrides = useMemo(() => {
        const o: Record<string, number> = {};
        for (const [tier, val] of Object.entries(tierDohOverride))
            if (val !== '') o[tier] = Number(val);
        return o;
    }, [tierDohOverride]);
    const previewWarehouseDohOverrides = useMemo(() => {
        const allGroups = buildPlanRuleGroups(warehouseRules);
        const o: Record<string, number> = {};
        for (const [key, override] of Object.entries(planRuleOverrides)) {
            const group = allGroups.find((g) => planRuleGroupKey(g) === key);
            if (!group || group.dohMode !== 'flat') continue;
            for (const whId of group.warehouseIds) {
                if (override.flat !== '' && override.flat !== undefined)
                    o[whId] = override.flat as number;
            }
        }
        return o;
    }, [warehouseRules, planRuleOverrides]);

    // Seed tierDohOverride from channel-level config when it first loads (aggregated channels).
    // Guarded so it never overwrites values already seeded from plan data or user edits.
    const tierConfigSnapshot = JSON.stringify(effectiveTierConfig);
    useEffect(() => {
        if (!effectiveTierConfig) return;
        setTierDohOverride((prev) => {
            if (Object.keys(prev).length > 0) return prev;
            return Object.fromEntries(
                Object.entries(effectiveTierConfig).map(([t, v]) => [
                    t,
                    v as number | '',
                ]),
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tierConfigSnapshot]);

    // Load channel warehouses + warehouse DoH rules for non-aggregated channels
    useEffect(() => {
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        const isNonAgg =
            params != null
                ? !params.isAggregated
                : plan?.isAggregated === false;
        if (!channelKey || !isNonAgg) return;
        setPlanWarehouseOptionsLoading(true);
        Promise.all([
            getChannelWarehouseOptions(channelKey),
            listWarehouseDohRules(channelKey),
            getDistributionPaths(channelKey),
        ])
            .then(([opts, rules, paths]) => {
                setPlanWarehouses(opts.channelWarehouses);
                setInternalWarehouses(opts.internalWarehouses);
                setWarehouseRules(rules);
                setChannelPaths(
                    paths
                        .filter((p) => p.isActive)
                        .map((p) => ({
                            fromWarehouseId: p.fromWarehouseId,
                            toWarehouseId: p.toWarehouseId,
                        })),
                );
            })
            .catch(() => {})
            .finally(() => setPlanWarehouseOptionsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params?.channelAbbreviationKey,
        plan?.channelAbbreviationKey,
        params?.isAggregated,
        plan?.isAggregated,
    ]);

    // Seed tierDohOverride from warehouse rules when they load (non-aggregated tier channels).
    // Only seeds if not already populated from plan data or channel-level config.
    useEffect(() => {
        const ruleWithTier = warehouseRules.find(
            (r) => r.tierTargetDohDays != null,
        );
        if (!ruleWithTier?.tierTargetDohDays) return;
        setTierDohOverride((prev) => {
            if (Object.keys(prev).length > 0) return prev; // already seeded — don't overwrite
            return Object.fromEntries(
                Object.entries(ruleWithTier.tierTargetDohDays!).map(
                    ([t, v]) => [t, v as number | ''],
                ),
            );
        });
    }, [warehouseRules]);

    // Seed selected warehouse IDs (destinations) + eligible sources from plan when it loads via existingPlanId path
    useEffect(() => {
        if (plan?.scopedWarehouseIds && selectedPlanWarehouseIds.length === 0) {
            setSelectedPlanWarehouseIds(plan.scopedWarehouseIds);
        }
        if (
            plan?.scopedSourceWarehouseIds &&
            selectedSourceWarehouseIds.length === 0
        ) {
            setSelectedSourceWarehouseIds(plan.scopedSourceWarehouseIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan?.id]);

    // Eligible-source universe (non-aggregated): the internal From WHs reachable from the selected FCs' paths
    // (or all FCs' paths when none are selected). Narrows as FCs are chosen, per the routing model.
    const sourceWarehouseOptions = useMemo(() => {
        const selectedFcs = selectedPlanWarehouseIds;
        const relevant =
            selectedFcs.length > 0
                ? channelPaths.filter(
                      (p) =>
                          p.toWarehouseId != null &&
                          selectedFcs.includes(p.toWarehouseId),
                  )
                : channelPaths;
        const nameById = new Map(
            internalWarehouses.map((w) => [w.warehouseId, w.name]),
        );
        const uniqueFromIds = [
            ...new Set(relevant.map((p) => p.fromWarehouseId)),
        ];
        return uniqueFromIds
            .map((id) => ({ warehouseId: id, name: nameById.get(id) ?? id }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [channelPaths, selectedPlanWarehouseIds, internalWarehouses]);

    // Drop any selected source that falls outside the current universe (e.g. after the FC selection changes).
    // Guard on channelPaths being loaded so we don't clobber a rehydrated selection while paths are still fetching.
    useEffect(() => {
        if (channelPaths.length === 0) return;
        setSelectedSourceWarehouseIds((prev) => {
            const allowed = new Set(
                sourceWarehouseOptions.map((o) => o.warehouseId),
            );
            const next = prev.filter((id) => allowed.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [sourceWarehouseOptions, channelPaths.length]);

    // Seed settings overrides from plan when it loads via existingPlanId path.
    // useState initializers run with existingPlan=null for async-loaded plans,
    // so this effect populates them once the plan arrives.
    useEffect(() => {
        if (!plan) return;
        // Plan settings are sacrosanct: the signal the plan was computed with overrides any
        // default/config-derived value (the seed initializer runs before the async plan arrives).
        setDemandSource(plan.demandSource as DemandSource);
        setTargetDohDays((prev) => prev || plan.targetDohDays || null);
        setMilestone1DaysOverride(
            (prev) => prev ?? plan.milestone1DaysOverride ?? null,
        );
        setMilestone2DaysOverride(
            (prev) => prev ?? plan.milestone2DaysOverride ?? null,
        );
        setDrrLookbackDays((prev) => prev ?? plan.drrLookbackDays ?? null);
        setDrrWindowStart((prev) => prev ?? plan.drrWindowStart ?? null);
        setDrrWindowEnd((prev) => prev ?? plan.drrWindowEnd ?? null);
        setDrrGrowthPct((prev) => prev ?? plan.drrGrowthPct ?? null);
        setMinSourceStockUnitsOverride(
            (prev) => prev ?? plan.minSourceStockUnits ?? null,
        );
        setMaxOutboundUtilizationPctOverride(
            (prev) => prev ?? plan.maxOutboundUtilizationPct ?? null,
        );
        setInboundInTransitDaysOverride(
            (prev) => prev ?? plan.inboundInTransitDays ?? null,
        );
        setSourceReserveUnitsOverride(
            (prev) => prev ?? plan.sourceReserveUnits ?? null,
        );
        setMinInternalDohDaysOverride(
            (prev) => prev ?? plan.minInternalDohDays ?? null,
        );
        setMaxUnitsPerShipmentOverride(
            (prev) => prev ?? plan.maxUnitsPerShipment ?? null,
        );
        setMaxMeasurementPerShipmentOverride(
            (prev) => prev ?? plan.maxMeasurementPerShipment ?? null,
        );
        if (plan.tierTargetDohDays) {
            setTierDohOverride(
                Object.fromEntries(
                    Object.entries(plan.tierTargetDohDays).map(([t, v]) => [
                        t,
                        v as number | '',
                    ]),
                ),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan?.id]);

    // Load plan by ID (immediate navigation path) or when existingPlan has no transfers
    useEffect(() => {
        if (existingPlanId) {
            setLoadingPlan(true);
            getDistributionPlan(existingPlanId)
                .then((p) => setPlan(p))
                .catch(() => {})
                .finally(() => setLoadingPlan(false));
        } else if (
            existingPlan &&
            existingPlan.transfers.length === 0 &&
            existingPlan.lineCount > 0
        ) {
            setLoadingPlan(true);
            getDistributionPlan(existingPlan.id)
                .then((p) => setPlan(p))
                .catch(() => {})
                .finally(() => setLoadingPlan(false));
        }
    }, [existingPlanId, existingPlan]);

    // Fetch fresh channel config so channelTierMode is never stale regardless of parent channels state
    useEffect(() => {
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey) return;
        getChannelConfig(channelKey)
            .then((cfg) => {
                setFetchedChannelTierConfig(cfg?.tierTargetDohDays ?? null);
                setEmailEnabled(!!cfg?.emailConfig?.enabled);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.channelAbbreviationKey, plan?.channelAbbreviationKey]);

    // Build the email drafts whenever the email modal is open — one per shipment, or a single
    // collapsed draft when the user asks for it. Re-runs on date / collapse change.
    useEffect(() => {
        if (!sendPlanOpen || !plan) return;
        setEmailPreviewLoading(true);
        setEmailPreviewError(null);
        getPlanEmailPreview(plan.id, {
            dispatchDate: emailDispatchDate,
            receivingDate: emailReceivingDate,
            combine: emailCombined,
        })
            .then((previews) =>
                setEmailDrafts(
                    previews.map((p) => ({
                        ...p,
                        sending: false,
                        sent: false,
                    })),
                ),
            )
            .catch((e: any) =>
                setEmailPreviewError(
                    e?.error?.message ||
                        e?.message ||
                        'Could not build the email preview.',
                ),
            )
            .finally(() => setEmailPreviewLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sendPlanOpen,
        emailDispatchDate,
        emailReceivingDate,
        emailCombined,
        plan?.id,
    ]);

    // Fetch channel detail context for optional columns — scoped to selected WH for non-aggregated plans
    useEffect(() => {
        if (!plan) return;
        let ignore = false;
        const warehouseId =
            !plan.isAggregated && selectedWarehouseId
                ? selectedWarehouseId
                : undefined;
        getChannelHealth({
            channelKey: plan.channelAbbreviationKey,
            warehouseId,
            targetDohDays: plan.targetDohDays,
            tierTargetDohDays: plan.tierTargetDohDays ?? undefined,
            milestone1Days: plan.milestone1DaysOverride ?? undefined,
            milestone2Days: plan.milestone2DaysOverride ?? undefined,
            drrLookbackDays: plan.drrLookbackDays ?? undefined,
            // The plan's DRR policy, so available rows show the same rate the plan was sized on.
            drrWindowStart: plan.drrWindowStart ?? undefined,
            drrWindowEnd: plan.drrWindowEnd ?? undefined,
            drrGrowthPct: plan.drrGrowthPct ?? undefined,
            // OPEN_PO plans: carry the PO so available rows show PO demand + membership.
            purchaseOrderId:
                plan.demandSource === 'OPEN_PO'
                    ? plan.sourcePurchaseOrderId ?? undefined
                    : undefined,
        })
            .then((d) => {
                if (ignore) return; // superseded by a newer selection — discard stale response
                const ch = d.channels[0];
                const map = new Map<string, DistributionSkuHealth>();
                for (const sku of ch?.skus ?? []) map.set(sku.productId, sku);
                setSkuContextMap(map);
                setChannelHealthMeta(
                    ch
                        ? {
                              minProcessDays: ch.minProcessDays,
                              maxProcessDays: ch.maxProcessDays,
                          }
                        : null,
                );
            })
            .catch(() => {
                /* context columns will show "—" */
            });
        return () => {
            ignore = true;
        };
    }, [plan?.id, selectedWarehouseId, channelHealthNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load internal warehouses + available paths for aggregated plans
    useEffect(() => {
        if (!effectiveIsAggregated) return;
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey) return;
        setPathsLoading(true);
        Promise.all([
            getChannelWarehouseOptions(channelKey),
            getDistributionPaths(channelKey),
        ])
            .then(([whOpts, paths]) => {
                setInternalWarehouses(whOpts.internalWarehouses);
                const whNameMap = new Map(
                    whOpts.internalWarehouses.map((w) => [
                        w.warehouseId,
                        w.name,
                    ]),
                );
                setAvailablePaths(
                    paths
                        .filter((p) => p.isActive && !!p.id)
                        .map((p) => ({
                            id: p.id,
                            fromWarehouseName:
                                whNameMap.get(p.fromWarehouseId) ??
                                p.fromWarehouseId,
                            leadTimeDays: p.leadTimeDays,
                        })),
                );
            })
            .catch(() => {})
            .finally(() => setPathsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        plan?.channelAbbreviationKey,
        params?.channelAbbreviationKey,
        effectiveIsAggregated,
    ]);

    // Load shipments for non-DRAFT plans (shipment column + filter)
    useEffect(() => {
        if (!plan || plan.status === 'DRAFT') {
            setShipments([]);
            return;
        }
        getPlanShipments(plan.id)
            .then(setShipments)
            .catch(() => {});
    }, [plan?.id, plan?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // Emailable shipments — mirrors the preview's positive-qty filter. Drives the collapse toggle,
    // and is read from `shipments` rather than the drafts so it doesn't flicker while a preview loads.
    const shipmentCountForEmail = useMemo(
        () => shipments.filter((s) => s.totalQty > 0).length,
        [shipments],
    );

    // ── Plan edit hook (add / remove / undo) ──
    const handlePlanUpdated = useCallback(
        (updater: (prev: DistributionPlanDetail) => DistributionPlanDetail) => {
            setPlan((prev) => (prev ? updater(prev) : prev));
        },
        [],
    );
    const {
        removeSku,
        undo: undoEdit,
        clearUndoStack,
        canUndo,
        undoStackSize,
        undoing,
    } = usePlanEdits(plan?.id ?? '', handlePlanUpdated);

    // ── Computed values (all unconditional) ──
    const allTransfers = useMemo(() => plan?.transfers ?? [], [plan]);

    // Map transferId → shipment for quick slot lookup
    const transferShipmentMap = useMemo(() => {
        const m = new Map<string, PlanShipment>();
        for (const s of shipments) {
            for (const tid of s.transferIds) m.set(tid, s);
        }
        return m;
    }, [shipments]);

    // Destination warehouses for non-aggregated plans. The plan's own scope (scopedWarehouseIds) is
    // the source of truth for which FCs it covers — NOT the transfers, which are empty whenever
    // nothing was recommended (e.g. a PO already covered by FC stock). Deriving from transfers there
    // left the FC unselected, so TO / channel stock / DoH all fell back to the whole-channel view.
    const destinationWarehouses = useMemo(() => {
        if (!plan || plan.isAggregated) return [];
        // Names: the channel's FC list (loaded for the picker), plus any carried on transfers.
        const nameById = new Map<string, string>();
        for (const w of planWarehouses) nameById.set(w.warehouseId, w.name);
        for (const t of allTransfers) {
            if (t.toWarehouseId && t.toWarehouseName) {
                nameById.set(t.toWarehouseId, t.toWarehouseName);
            }
        }
        // Scoped plan → those FCs. Unscoped (all FCs) → fall back to whatever transfers exist.
        let destIds = plan.scopedWarehouseIds ?? [];
        if (destIds.length === 0) {
            const seen = new Set<string>();
            for (const t of allTransfers) {
                if (t.toWarehouseId) seen.add(t.toWarehouseId);
            }
            destIds = Array.from(seen);
        }
        return destIds.map((id) => ({
            value: id,
            label: nameById.get(id) ?? id,
        }));
    }, [plan, allTransfers, planWarehouses]);

    // Auto-select first WH for non-aggregated plans when none selected
    useEffect(() => {
        if (destinationWarehouses.length > 0 && !selectedWarehouseId) {
            setSelectedWarehouseId(destinationWarehouses[0].value);
        }
    }, [destinationWarehouses]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedWarehouseName = useMemo(
        () =>
            destinationWarehouses.find((w) => w.value === selectedWarehouseId)
                ?.label,
        [destinationWarehouses, selectedWarehouseId],
    );

    // Transfers filtered by selected warehouse (for non-aggregated plans)
    const warehouseFilteredTransfers = useMemo(
        () =>
            selectedWarehouseId
                ? allTransfers.filter(
                      (t) => t.toWarehouseId === selectedWarehouseId,
                  )
                : allTransfers,
        [allTransfers, selectedWarehouseId],
    );

    // Unique From WH IDs and name map — drives dynamic columns
    // Show EVERY eligible source WH as a column — not just the ones the auto-split allocated to.
    // fromWhStockMap (from getPlan) covers all active source paths; a zero-allocation WH (e.g. one
    // the split didn't need) then renders as an editable "available" cell at qty 0, so the split is
    // visually complete and you can hand-allocate to it.
    const uniqueFromWhIds = useMemo(() => {
        const ids = new Set(
            warehouseFilteredTransfers.map((t) => t.fromWarehouseId),
        );
        // Surface EVERY eligible source path as a column (zero-allocation WHs included):
        //  • aggregated → all source WHs on the channel (`destToSourceWhs.null`),
        //  • per-FC     → the source WHs that have a path to the selected destination FC.
        const destKey = plan?.isAggregated ? 'null' : selectedWarehouseId;
        if (destKey)
            for (const whId of plan?.destToSourceWhs?.[destKey] ?? [])
                ids.add(whId);
        return [...ids];
    }, [
        warehouseFilteredTransfers,
        plan?.isAggregated,
        plan?.destToSourceWhs,
        selectedWarehouseId,
    ]);

    const fromWhNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of warehouseFilteredTransfers) {
            if (!map.has(t.fromWarehouseId))
                map.set(
                    t.fromWarehouseId,
                    t.fromWarehouseName ?? t.fromWarehouseId,
                );
        }
        // Names for source WHs that have no transfer row (zero-allocation) come from the plan payload.
        for (const [whId, name] of Object.entries(plan?.fromWhNames ?? {})) {
            if (!map.has(whId)) map.set(whId, name);
        }
        return map;
    }, [warehouseFilteredTransfers, plan?.fromWhNames]);

    // Product filter options derived from skuContextMap (channel-health). When the user
    // has filtered to a specific warehouse, channel-health returns the per-warehouse tier
    // — so options like "S" appear when an SKU is S-tier at this store, even if no plan
    // transfer for that SKU exists in the channel-level products.tiers_by_channel.
    const [productFilter, setProductFilter] = useState<FilterValue>({});
    const skuContextValues = useMemo(
        () => Array.from(skuContextMap.values()),
        [skuContextMap],
    );
    const categoryOptions = useMemo(
        () =>
            [
                ...new Set(
                    skuContextValues
                        .map((s) => s.category)
                        .filter((c): c is string => !!c),
                ),
            ]
                .sort()
                .map((c) => ({ label: c, value: c })),
        [skuContextValues],
    );
    const productGroupOptions = useMemo(
        () =>
            [
                ...new Set(
                    skuContextValues
                        .map((s) => s.productGroup)
                        .filter((g): g is string => !!g),
                ),
            ]
                .sort()
                .map((g) => ({ label: g, value: g })),
        [skuContextValues],
    );
    const tierOptions = useMemo(
        () =>
            [
                ...new Set(
                    skuContextValues
                        .map((s) => s.tier)
                        .filter((t): t is string => !!t),
                ),
            ]
                .sort()
                .map((t) => ({ label: t, value: t })),
        [skuContextValues],
    );
    const {
        params: productFilterParams,
        resolvedAttrIds: planViewResolvedAttrIds,
    } = useProductFilterParams(
        { categoryOptions, productGroupOptions, tierOptions },
        productFilter,
    );

    const planFilters: DataSheetFilterConfig[] = useMemo(
        () => [
            {
                label: 'Product',
                params: productFilterParams,
                value: productFilter,
                onChange: setProductFilter,
            },
        ],
        [productFilterParams, productFilter],
    );

    // ── Derived state ──
    const planStatus = plan?.status ?? 'DRAFT';
    const isReadonly = planStatus !== 'DRAFT';
    const hasUnpushedShipments =
        shipments.length > 0 &&
        shipments.some((s) => s.wmsStatus !== 'CREATED');
    const statusColor =
        planStatus === 'DRAFT'
            ? neutralColors.textMuted
            : ['APPROVED', 'COMPLETED'].includes(planStatus)
              ? semanticColors.positive.primary
              : semanticColors.info.primary;
    const channelName =
        params?.channelName ?? plan?.channelAbbreviationKey ?? '';
    const showWarehouseFilter = !!(
        plan &&
        !plan.isAggregated &&
        destinationWarehouses.length > 1
    );

    // ── Name editing ──
    const startEditingName = () => {
        setNameValue(plan?.name ?? existingPlanName ?? '');
        setEditingName(true);
        setTimeout(() => nameInputRef.current?.select(), 0);
    };

    const commitNameEdit = async () => {
        setEditingName(false);
        const trimmed = nameValue.trim();
        if (!trimmed || !plan || trimmed === plan.name) return;
        try {
            await renameDistributionPlan(plan.id, trimmed);
            setPlan((prev) => (prev ? { ...prev, name: trimmed } : prev));
        } catch {
            showError('Failed to rename plan.');
        }
    };

    // ── Handlers ──
    const handleBack = () => {
        if (viewState === 'plan' && !isReadonly && editQtyMap.size > 0) {
            openLeaveConfirm(async () => {
                await handleSaveDraftEdits();
                await onSaveDraft();
                onBack();
            });
        } else {
            onBack();
        }
    };

    // "Schedule" next to Compute: the same settings this panel would compute with, pinned onto a
    // DISTRIBUTION_CHANNEL_UNDERSTOCK alert, with the deep link back to this builder. Only the
    // whitelisted plan-settings keys travel (the backend sanitises the rest away).
    const [headerScheduleOpen, setHeaderScheduleOpen] = useState(false);
    const scheduleAlert = useMemo<SettingsPanelProps['scheduleAlert']>(() => {
        const channelKey = params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey || resolvedPurchaseOrderId) return null;
        const tierOverrides: Record<string, number> = {};
        for (const [tier, val] of Object.entries(tierDohOverride)) {
            if (val !== '') tierOverrides[tier] = Number(val);
        }
        const planSettings: Record<string, unknown> = {
            demandSource,
            targetDohDays: channelTierMode ? undefined : (targetDohDays || undefined),
            tierTargetDohDays: effectiveIsAggregated && channelTierMode && Object.keys(tierOverrides).length > 0 ? tierOverrides : undefined,
            milestone1DaysOverride: milestone1DaysOverride ?? undefined,
            milestone2DaysOverride: milestone2DaysOverride ?? undefined,
            drrLookbackDays: drrLookbackDays ?? undefined,
            drrWindowStart: effectiveDrrWindow?.start ?? undefined,
            drrWindowEnd: effectiveDrrWindow?.end ?? undefined,
            drrGrowthPct: drrGrowthPct ?? undefined,
            minSourceStockUnitsOverride: minSourceStockUnitsOverride ?? undefined,
            maxOutboundUtilizationPctOverride: maxOutboundUtilizationPctOverride ?? undefined,
            inboundInTransitDaysOverride: inboundInTransitDaysOverride ?? undefined,
            sourceReserveUnitsOverride: sourceReserveUnitsOverride ?? undefined,
            minInternalDohDaysOverride: minInternalDohDaysOverride ?? undefined,
            maxUnitsPerShipmentOverride: maxUnitsPerShipmentOverride ?? undefined,
            maxMeasurementPerShipmentOverride: maxMeasurementPerShipmentOverride ?? undefined,
            warehouseIds: !effectiveIsAggregated && selectedPlanWarehouseIds.length > 0 ? selectedPlanWarehouseIds : undefined,
            pathIds: effectiveIsAggregated && selectedPathIds.length > 0 ? selectedPathIds : undefined,
            sourceWarehouseIds: !effectiveIsAggregated && selectedSourceWarehouseIds.length > 0 ? selectedSourceWarehouseIds : undefined,
        };
        for (const k of Object.keys(planSettings)) if (planSettings[k] === undefined) delete planSettings[k];
        // No artifact here: the type's default (action.constants) links the card to the draft plan
        // each run writes, by plan id — the card and the plan are the same object.
        return {
            alertType: 'DISTRIBUTION_CHANNEL_UNDERSTOCK',
            alertLabel: `Send to ${channelName}`,
            context: {
                channelKey,
                channelName,
                planSettings,
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params?.channelAbbreviationKey, plan?.channelAbbreviationKey, resolvedPurchaseOrderId, channelName, demandSource, targetDohDays, channelTierMode,
        tierDohOverride, effectiveIsAggregated, milestone1DaysOverride, milestone2DaysOverride, drrLookbackDays, effectiveDrrWindow, drrGrowthPct,
        minSourceStockUnitsOverride, maxOutboundUtilizationPctOverride, inboundInTransitDaysOverride, sourceReserveUnitsOverride, minInternalDohDaysOverride,
        maxUnitsPerShipmentOverride, maxMeasurementPerShipmentOverride, selectedPlanWarehouseIds, selectedPathIds, selectedSourceWarehouseIds,
    ]);

    const handleCompute = async () => {
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey) return;
        closeSettings();
        setViewState('computing');
        try {
            const tierOverrides: Record<string, number> = {};
            for (const [tier, val] of Object.entries(tierDohOverride)) {
                if (val !== '') tierOverrides[tier] = Number(val);
            }
            // Expand per-rule-group DoH overrides into per-warehouse overrides (flat + tier).
            // Each group's override applies to every store in that group, for this run only.
            const allGroups = buildPlanRuleGroups(warehouseRules);
            const warehouseDohOverrides: Record<string, number> = {};
            const warehouseTierDohOverrides: Record<
                string,
                Record<string, number>
            > = {};
            for (const [key, override] of Object.entries(planRuleOverrides)) {
                const group = allGroups.find(
                    (g) => planRuleGroupKey(g) === key,
                );
                if (!group) continue;
                if (group.dohMode === 'flat') {
                    if (override.flat !== '' && override.flat !== undefined) {
                        for (const whId of group.warehouseIds)
                            warehouseDohOverrides[whId] =
                                override.flat as number;
                    }
                } else if (override.tierDoh) {
                    const tierMap: Record<string, number> = {};
                    for (const [tier, val] of Object.entries(
                        override.tierDoh,
                    )) {
                        if (val !== '' && val != null)
                            tierMap[tier] = Number(val);
                    }
                    if (Object.keys(tierMap).length > 0) {
                        for (const whId of group.warehouseIds)
                            warehouseTierDohOverrides[whId] = tierMap;
                    }
                }
            }
            const result = await computeDistributionPlan(channelKey, {
                demandSource,
                existingPlanId: plan?.id ?? undefined,
                // PO-driven plans compute against the scoped PO (from params on a new plan, or read
                // back off the plan on recompute). Backend forces OPEN_PO + derives the FC.
                purchaseOrderId: resolvedPurchaseOrderId,
                // Alert deep link: run the same engine over just the flagged SKUs.
                productIds: params?.productIds?.length ? params.productIds : undefined,
                targetDohDays: channelTierMode
                    ? undefined
                    : (targetDohDays || undefined),
                milestone1DaysOverride: milestone1DaysOverride ?? undefined,
                milestone2DaysOverride: milestone2DaysOverride ?? undefined,
                drrLookbackDays: drrLookbackDays ?? undefined,
                drrWindowStart: effectiveDrrWindow?.start ?? undefined,
                drrWindowEnd: effectiveDrrWindow?.end ?? undefined,
                drrGrowthPct: drrGrowthPct ?? undefined,
                minSourceStockUnitsOverride:
                    minSourceStockUnitsOverride ?? undefined,
                maxOutboundUtilizationPctOverride:
                    maxOutboundUtilizationPctOverride ?? undefined,
                inboundInTransitDaysOverride:
                    inboundInTransitDaysOverride ?? undefined,
                sourceReserveUnitsOverride:
                    sourceReserveUnitsOverride ?? undefined,
                minInternalDohDaysOverride:
                    minInternalDohDaysOverride ?? undefined,
                maxUnitsPerShipmentOverride:
                    maxUnitsPerShipmentOverride ?? undefined,
                maxMeasurementPerShipmentOverride:
                    maxMeasurementPerShipmentOverride ?? undefined,
                // Plan-level tier override applies to aggregated channels only; non-aggregated channels
                // carry per-group tier overrides via warehouseTierDohOverrides instead.
                tierTargetDohDays:
                    effectiveIsAggregated &&
                    channelTierMode &&
                    Object.keys(tierOverrides).length > 0
                        ? tierOverrides
                        : undefined,
                warehouseIds:
                    !effectiveIsAggregated &&
                    selectedPlanWarehouseIds.length > 0
                        ? selectedPlanWarehouseIds
                        : null,
                pathIds:
                    effectiveIsAggregated && selectedPathIds.length > 0
                        ? selectedPathIds
                        : null,
                sourceWarehouseIds:
                    !effectiveIsAggregated &&
                    selectedSourceWarehouseIds.length > 0
                        ? selectedSourceWarehouseIds
                        : null,
                warehouseDohOverrides:
                    Object.keys(warehouseDohOverrides).length > 0
                        ? warehouseDohOverrides
                        : undefined,
                warehouseTierDohOverrides:
                    Object.keys(warehouseTierDohOverrides).length > 0
                        ? warehouseTierDohOverrides
                        : undefined,
            });
            setPlan(result);
            setSelectedWarehouseId(null);
            // A recompute deletes and re-inserts every transfer (new ids) and regenerates the
            // recommended quantities, so any pending inline edits are now meaningless AND keyed by
            // dead ids. Drop them so a later Save can't reference stale rows.
            setEditQtyMap(new Map());
            // plan.id is unchanged on recompute, so bump the nonce to force channel-health to refetch
            // with the new plan settings — otherwise available rows keep stale demand numbers.
            setChannelHealthNonce((n) => n + 1);
            setViewState('plan');
        } catch {
            setViewState('error');
        }
    };

    // Alert deep link ("open the distribution plan"): once the settings page has loaded the channel's
    // config, warehouses and paths, compute straight away with those saved settings — the operator
    // lands on the plan, not on the settings form. Runs once; a later Edit Settings → Compute is manual.
    const autoComputedRef = useRef(false);
    useEffect(() => {
        if (!params?.autoCompute || autoComputedRef.current || viewState !== 'settings') return;
        if (fetchedChannelTierConfig === undefined) return;
        if (effectiveIsAggregated) {
            if (pathsLoading || availablePaths.length === 0) return;
        } else if (planWarehouseOptionsLoading || planWarehouses.length === 0) {
            return;
        }
        autoComputedRef.current = true;
        void handleCompute();
        // handleCompute is re-created every render; the ref makes re-runs no-ops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params?.autoCompute,
        viewState,
        fetchedChannelTierConfig,
        effectiveIsAggregated,
        pathsLoading,
        availablePaths.length,
        planWarehouseOptionsLoading,
        planWarehouses.length,
    ]);

    const handleTierDohOverrideChange = useCallback(
        (tier: string, v: number | '') => {
            setTierDohOverride((prev) => ({ ...prev, [tier]: v }));
        },
        [],
    );

    const handlePlanFlatOverrideChange = useCallback(
        (groupKey: string, v: number | '') => {
            setPlanRuleOverrides((prev) => ({
                ...prev,
                [groupKey]: { ...(prev[groupKey] ?? { flat: '' }), flat: v },
            }));
        },
        [],
    );

    const handlePlanTierOverrideChange = useCallback(
        (groupKey: string, tier: string, v: number | '') => {
            setPlanRuleOverrides((prev) => ({
                ...prev,
                [groupKey]: {
                    ...(prev[groupKey] ?? { flat: '' }),
                    tierDoh: { ...(prev[groupKey]?.tierDoh ?? {}), [tier]: v },
                },
            }));
        },
        [],
    );

    // Inline edits are stored locally — committed on explicit Save Draft / Save and Approve.
    // Map keys: transfer.id for persisted rows; `new::${productId}::${toWhId}::${fromWhId}`
    // for available rows that haven't been POSTed yet.
    const handleQtyChange = useCallback(
        (editKey: string, qty: number) => {
            setEditQtyMap((prev) => {
                const next = new Map(prev);
                // Treat empty/0 on a 'new::' key as removing the entry — no row gets created on save.
                if (editKey.startsWith('new::') && qty === 0) {
                    next.delete(editKey);
                } else {
                    next.set(editKey, qty);
                }
                return next;
            });
            // Clear the "newly added" highlight once user sets a qty on that row
            setLastAddedProductId((prev) => {
                if (!prev) return null;
                const match = (plan?.transfers ?? []).find(
                    (t) => t.id === editKey,
                );
                return match?.productId === prev ? null : prev;
            });
        },
        [plan?.transfers],
    );

    const handleSaveDraftEdits = useCallback(
        async (thenCallback?: () => void | Promise<void>) => {
            if (editQtyMap.size === 0) {
                await thenCallback?.();
                return;
            }
            if (!plan) return;
            setSavingDraft(true);
            try {
                // Resolve every edit to its natural key (productId × fromWh × toWh) so the server can
                // upsert without transfer ids — no stale-id 404s, no partial saves. Existing rows are
                // found via plan.transfers; available rows carry their key in the `new::` map key.
                const edits: SaveTransferEdit[] = [];
                for (const [key, qty] of editQtyMap.entries()) {
                    if (key.startsWith('new::')) {
                        if (qty <= 0) continue; // a never-persisted row at 0 is a no-op
                        const [productId, toWhRaw, fromWhId] = key
                            .slice(5)
                            .split('::');
                        edits.push({
                            productId,
                            productName:
                                skuContextMap.get(productId)?.productName ??
                                null,
                            fromWarehouseId: fromWhId,
                            toWarehouseId: toWhRaw === 'agg' ? null : toWhRaw,
                            quantity: qty,
                        });
                    } else {
                        const transfer = plan.transfers.find(
                            (t) => t.id === key,
                        );
                        if (!transfer) continue; // row no longer exists (e.g. after a recompute) — skip
                        edits.push({
                            productId: transfer.productId,
                            productName: transfer.productName,
                            fromWarehouseId: transfer.fromWarehouseId,
                            toWarehouseId: transfer.toWarehouseId,
                            quantity: qty,
                        });
                    }
                }

                // One atomic transactional write; the response is the authoritative refreshed plan.
                const refreshed = await saveDistributionTransfers(
                    plan.id,
                    edits,
                );
                setPlan(refreshed);
                setEditQtyMap(new Map());
                // A tick on a not-yet-saved cell is keyed `new::sku::dest::source`. The save just
                // gave that line a real id, and approval only sends real ids — left as-is the tick
                // would be silently dropped and the line REJECTED on approve. Swap each one for the
                // id of the line the save created on the same lane.
                setSelectedTransferIds((prev) => {
                    if (![...prev].some(isNewSlotKey)) return prev;
                    const idByLane = new Map(
                        refreshed.transfers.map((t) => [
                            `${t.productId}::${t.toWarehouseId ?? 'agg'}::${t.fromWarehouseId}`,
                            t.id,
                        ]),
                    );
                    const next = new Set<string>();
                    for (const key of prev) {
                        if (!isNewSlotKey(key)) {
                            next.add(key);
                            continue;
                        }
                        const persistedId = idByLane.get(key.slice(5));
                        if (persistedId) next.add(persistedId);
                    }
                    return next;
                });
                await thenCallback?.();
            } catch (e: unknown) {
                // 409 = the plan was approved/finalised elsewhere and can no longer be edited. Re-sync to
                // the server truth (flips the view to readonly) and keep the edit map so nothing vanishes.
                const err = e as {
                    error?: {
                        statusCode?: number;
                        message?: string | string[];
                    };
                    message?: string;
                };
                const statusCode = err?.error?.statusCode;
                if (statusCode === 409) {
                    showError(
                        'This plan was approved elsewhere and can no longer be edited. Reloading the latest version — your unsaved changes were not applied.',
                    );
                    try {
                        const latest = await getDistributionPlan(plan.id);
                        setPlan(latest);
                    } catch {
                        /* leave current plan as-is */
                    }
                } else {
                    // Surface the ACTUAL backend reason (validation message, etc.) instead of a generic
                    // string — class-validator returns message as string[], so join it.
                    const raw = err?.error?.message ?? err?.message;
                    const reason = Array.isArray(raw) ? raw.join('; ') : raw;
                    showError(
                        reason
                            ? `Failed to save changes: ${reason}`
                            : 'Failed to save changes. Please try again.',
                    );
                }
            } finally {
                setSavingDraft(false);
            }
        },
        [editQtyMap, plan, skuContextMap],
    );

    const handleRemoveRow = useCallback(
        async (row: SkuRouteRow): Promise<void> => {
            const rowTransfers = (plan?.transfers ?? []).filter(
                (t) =>
                    t.productId === row.productId &&
                    (plan?.isAggregated
                        ? true
                        : t.toWarehouseId === row.toWarehouseId),
            );
            for (const t of rowTransfers) {
                await removeSku(t);
            }
        },
        [plan, removeSku],
    );

    const handleDelete = () => {
        if (!plan) return;
        modals.openConfirmModal({
            title: 'Delete draft plan?',
            children: (
                <Text
                    style={{ fontSize: 14, color: neutralColors.textSecondary }}
                >
                    This will permanently delete "<strong>{plan.name}</strong>"
                    and all its transfer lines.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setDeleting(true);
                try {
                    await deleteDistributionPlan(plan.id);
                    showSuccess(`Plan "${plan.name}" deleted`);
                    onDelete?.();
                    onBack();
                } catch {
                    showError('Failed to delete plan. Please try again.');
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    const handleWarehouseChange = (whId: string | null) => {
        setSelectedWarehouseId(whId);
    };

    // Unsendable (excluded at compute time): isExcluded=true transfers; used for the warning banner
    const lowStockTransfers = useMemo(
        () => warehouseFilteredTransfers.filter((t) => t.isExcluded === true),
        [warehouseFilteredTransfers],
    );

    // One row per SKU × To WH — each row has per-From-WH slots (dynamic columns).
    const skuRouteRows = useMemo((): SkuRouteRow[] => {
        if (!plan) return [];

        const planDemandSource = (plan.demandSource ??
            demandSource) as DemandSource;
        const resolveDemandToMeet = (
            forecast: number | null | undefined,
            drr: number | null | undefined,
            target: number | null | undefined,
        ): number | null => {
            // PO-driven: the demand to meet is the PO quantity (frozen on the line as targetDemand).
            if (planDemandSource === 'OPEN_PO') return target ?? null;
            if (planDemandSource === 'FORECAST') return forecast ?? null;
            if (planDemandSource === 'PAST_SALES') return drr ?? null;
            if (planDemandSource === 'MAX_OF_FORECAST_PAST_SALES') {
                if (forecast == null && drr == null) return null;
                return Math.max(forecast ?? 0, drr ?? 0);
            }
            return null;
        };

        // Daily consumption rate for an available row (no transfer, so no frozen rate). Mirrors the
        // backend's `resolveDailyDemand`: forecast is a window total, so it becomes a rate by dividing
        // by that window (the SKU's target DoH); past sales is already a rate. OPEN_PO has no forward
        // rate of its own and measures velocity off past sales.
        const resolveDailyRate = (
            ctx: DistributionSkuHealth | undefined,
        ): number | null => {
            if (!ctx) return null;
            const doh = ctx.targetDohDays || 0;
            const forecastDaily =
                ctx.forecastDemandHigh != null && doh > 0
                    ? ctx.forecastDemandHigh / doh
                    : 0;
            if (planDemandSource === 'FORECAST') return forecastDaily;
            if (planDemandSource === 'MAX_OF_FORECAST_PAST_SALES')
                return Math.max(ctx.currentDrr ?? 0, forecastDaily);
            return ctx.currentDrr ?? 0;
        };

        // Group transfers by (productId, toWarehouseId)
        const routeMap = new Map<
            string,
            {
                toWhId: string | null;
                toWhName: string | null;
                slots: Map<string, DistributionTransferRow>;
            }
        >();
        for (const t of warehouseFilteredTransfers) {
            const key = `${t.productId}::${t.toWarehouseId ?? 'agg'}`;
            if (!routeMap.has(key)) {
                routeMap.set(key, {
                    toWhId: t.toWarehouseId,
                    toWhName: t.toWarehouseName,
                    slots: new Map(),
                });
            }
            routeMap.get(key)!.slots.set(t.fromWarehouseId, t);
        }

        // Add entries for SKUs in channel-health that have no transfers (available rows)
        for (const [productId] of skuContextMap) {
            const toWhId = plan.isAggregated ? null : selectedWarehouseId;
            const key = `${productId}::${toWhId ?? 'agg'}`;
            if (!routeMap.has(key)) {
                routeMap.set(key, {
                    toWhId,
                    toWhName:
                        destinationWarehouses.find((w) => w.value === toWhId)
                            ?.label ?? null,
                    slots: new Map(),
                });
            }
        }

        const rows: SkuRouteRow[] = [];
        for (const [routeKey, { toWhId, toWhName, slots }] of routeMap) {
            const productId = routeKey.split('::')[0];
            const ctx = skuContextMap.get(productId);
            let firstSlot: DistributionTransferRow | undefined;
            for (const v of slots.values()) {
                firstSlot = v;
                break;
            }

            // Build per-From-WH slots
            const fromWhSlots: Record<string, FromWhSlot> = {};
            for (const fromWhId of uniqueFromWhIds) {
                const t = slots.get(fromWhId);
                if (t) {
                    const shipment = transferShipmentMap.get(t.id);
                    fromWhSlots[fromWhId] = {
                        transferId: t.id,
                        isExcluded: t.isExcluded,
                        // An excluded ("cannot send") line's recommendation is 0 — nothing sendable under
                        // the settings. Plans computed before this was stored as 0 carried the demand here;
                        // normalise so no sum, cell or flag reads the demand as a plan quantity.
                        recommendedQty: t.isExcluded ? 0 : t.recommendedQty,
                        approvedQty: t.approvedQty,
                        // Avail is read from the plan's fromWhStockMap for every cell (falls back to
                        // the line's compute-time snapshot only if the path is no longer active). No
                        // entry means nothing on hand — 0, never "unknown", so the qty cap always applies.
                        fromWarehouseStock:
                            plan.fromWhStockMap?.[fromWhId]?.[productId] ??
                            t.fromWarehouseStock ??
                            0,
                        fromWhInTransit:
                            plan.fromWhInTransitMap?.[fromWhId]?.[productId] ??
                            0,
                        cellState: t.isExcluded ? 'unsendable' : 'recommended',
                        saleOrderCode:
                            shipment?.saleOrderCode ?? t.saleOrderCode,
                        saleOrderName:
                            shipment?.saleOrderName ?? t.saleOrderName,
                        wmsStatus: shipment?.wmsStatus ?? t.wmsStatus,
                        measurementValue: t.measurementValue,
                    };
                } else {
                    fromWhSlots[fromWhId] = {
                        transferId: null,
                        isExcluded: false,
                        recommendedQty: 0,
                        approvedQty: null,
                        fromWarehouseStock:
                            plan.fromWhStockMap?.[fromWhId]?.[productId] ?? 0,
                        fromWhInTransit:
                            plan.fromWhInTransitMap?.[fromWhId]?.[productId] ??
                            0,
                        cellState: 'available',
                        saleOrderCode: null,
                        saleOrderName: null,
                        wmsStatus: null,
                        measurementValue: null,
                    };
                }
            }

            const slotVals = Object.values(fromWhSlots);
            const hasRecommended = slotVals.some(
                (s) => s.cellState === 'recommended',
            );
            const hasUnsendable = slotVals.some(
                (s) => s.cellState === 'unsendable',
            );
            const rowState: UnifiedRowState = hasRecommended
                ? 'recommended'
                : hasUnsendable
                  ? 'unsendable'
                  : 'available';

            // Rows that HAVE a transfer read demand straight from the (re)compute response — the exact
            // numbers used to size that line. The slot that carries them is any transfer with a frozen
            // demand — never "whichever line the database returned first", which is what let a
            // null-snapshot manual line blank a row that still had its auto line beside it.
            // Channel-health (ctx) is the source ONLY when no line on the row carries a frozen demand
            // (available rows, or legacy manual lines saved before manual lines were stamped with the
            // engine's figures), so a recompute never surfaces stale channel-health values.
            const hasFrozenDemand = (t: DistributionTransferRow): boolean =>
                t.dailyDemand != null ||
                t.drrDemand != null ||
                t.forecastDemand != null ||
                t.targetDemand != null;
            const metricSlot = [...slots.values()].find(hasFrozenDemand);
            const forecastDemand = metricSlot
                ? (metricSlot.forecastDemand ?? null)
                : (ctx?.forecastDemandHigh ?? null);
            const drrDemand = metricSlot
                ? (metricSlot.drrDemand ?? null)
                : (ctx?.drrDemand ?? null);
            // Rows without a frozen demand read PO demand from channel-health, so an OPEN_PO plan
            // shows the PO qty for every SKU on the PO — recommended or not — exactly like the
            // recommended lines read targetDemand off the frozen transfer.
            const targetDemand = metricSlot
                ? (metricSlot.targetDemand ?? null)
                : (ctx?.openPoDemand ?? null);
            const demandToMeet = resolveDemandToMeet(
                forecastDemand,
                drrDemand,
                targetDemand,
            );
            // Stock delta for a row with no transfer line (nothing the engine could send, or a plan
            // computed before such SKUs got a row): the engine's own formula on channel-health
            // figures — demand minus projected stock (on hand − burn over the process window
            // + in-transit). Same fallback shape as Lead Time Demand, so a SKU with demand never
            // shows a blank delta.
            const fallbackDelta = ((): number | null => {
                if (metricSlot || !ctx || demandToMeet == null) return null;
                const burnDays = channelHealthMeta?.maxProcessDays ?? 0;
                const projected = Math.max(
                    0,
                    Math.ceil(
                        (ctx.channelStock ?? 0) -
                            (resolveDailyRate(ctx) ?? 0) * burnDays +
                            (ctx.inTransit ?? 0),
                    ),
                );
                return Math.max(0, Math.ceil(demandToMeet) - projected);
            })();
            const mapping = channelMappingMap.get(productId);
            rows.push({
                productId,
                productName: ctx?.productName ?? firstSlot?.productName ?? null,
                marketplaceSkuId: mapping?.marketplaceSkuId ?? null,
                secondaryId: mapping?.secondaryId ?? null,
                toWarehouseId: toWhId,
                toWarehouseName: toWhName,
                tier: ctx?.tier ?? firstSlot?.tier,
                category: ctx?.category ?? firstSlot?.category,
                productGroup: ctx?.productGroup ?? firstSlot?.productGroup,
                isActive: ctx?.isActive ?? firstSlot?.isActive ?? undefined,
                _channelStock:
                    metricSlot?.channelStock ??
                    firstSlot?.channelStock ??
                    ctx?.channelStock,
                networkInTransit:
                    metricSlot?.networkInTransit ??
                    firstSlot?.networkInTransit ??
                    null,
                _gapUnits: ctx?.gapUnits ?? 0,
                destinationPlanQty:
                    metricSlot?.destinationPlanQty ??
                    firstSlot?.destinationPlanQty ??
                    fallbackDelta,
                demandToMeet,
                drrDemand,
                forecastDemand,
                leadTimeDemand: metricSlot
                    ? (metricSlot.leadTimeDemand ?? null)
                    : channelHealthMeta
                      ? (ctx?.currentDrr ?? 0) *
                        channelHealthMeta.maxProcessDays
                      : null,
                dailyDemand: metricSlot
                    ? (metricSlot.dailyDemand ?? null)
                    : resolveDailyRate(ctx),
                inTransit:
                    metricSlot?.inTransit ??
                    firstSlot?.inTransit ??
                    ctx?.inTransit ??
                    0,
                pastSalesQty: pastSalesMap.get(routeKey) ?? null,
                orderedValue: poPriceMap.get(productId)?.lineTotal ?? null,
                orderedQty: poPriceMap.get(productId)?.quantity ?? null,
                unitPrice: poPriceMap.get(productId)?.unitPrice ?? null,
                fromWhSlots,
                rowState,
                rowKey: routeKey,
                // On the PO iff a PO demand resolved for it (frozen targetDemand or channel-health openPoDemand).
                _onPurchaseOrder:
                    planDemandSource === 'OPEN_PO' && targetDemand != null,
            });
        }

        // Apply product filter
        let all = rows;
        if (productFilter.category?.length)
            all = all.filter((r) =>
                productFilter.category!.includes(r.category ?? ''),
            );
        if (productFilter.productGroup?.length)
            all = all.filter((r) =>
                productFilter.productGroup!.includes(r.productGroup ?? ''),
            );
        if (productFilter.tier?.length)
            all = all.filter((r) => productFilter.tier!.includes(r.tier ?? ''));
        if (productFilter.isActive?.length) {
            const wantActive = productFilter.isActive[0] === 'true';
            all = all.filter((r) => r.isActive === wantActive);
        }
        if (planViewResolvedAttrIds)
            all = all.filter((r) => planViewResolvedAttrIds.has(r.productId));

        // Plan quantity for a SKU, as saved - the sort must not reshuffle while a cell is being typed in.
        const rowPlanQty = (r: SkuRouteRow): number => rowSendQty(r);
        // Stock available across the internal (source) warehouses for a SKU.
        const rowAvail = (r: SkuRouteRow): number =>
            Object.values(r.fromWhSlots).reduce(
                (sum, s) => sum + (s.fromWarehouseStock ?? 0),
                0,
            );
        all.sort((a, b) => {
            const aHasPlan = rowPlanQty(a) > 0;
            const bHasPlan = rowPlanQty(b) > 0;
            // 1. SKUs with a send recommendation (plan qty > 0) come first, highest plan quantity on top.
            if (aHasPlan !== bHasPlan) return aHasPlan ? -1 : 1;
            if (aHasPlan) return b._gapUnits - a._gapUnits;
            // 2. Unsendable SKUs next — plan qty shows 0 but a recommendation existed (source stock below threshold).
            const aUnsendable = a.rowState === 'unsendable';
            const bUnsendable = b.rowState === 'unsendable';
            if (aUnsendable !== bUnsendable) return aUnsendable ? -1 : 1;
            if (aUnsendable) return b._gapUnits - a._gapUnits;
            // 3. Genuinely-0 SKUs last — highest available internal-warehouse stock on top.
            return rowAvail(b) - rowAvail(a);
        });

        return all;
    }, [
        plan,
        warehouseFilteredTransfers,
        skuContextMap,
        uniqueFromWhIds,
        selectedWarehouseId,
        destinationWarehouses,
        productFilter,
        channelHealthMeta,
        demandSource,
        planViewResolvedAttrIds,
        pastSalesMap,
        poPriceMap,
        channelMappingMap,
    ]);

    // Counts per state for the summary subtitle
    const unifiedCounts = useMemo(
        () => ({
            recommended: skuRouteRows.filter(
                (r) => r.rowState === 'recommended',
            ).length,
            available: skuRouteRows.filter((r) => r.rowState === 'available')
                .length,
            unsendable: skuRouteRows.filter((r) => r.rowState === 'unsendable')
                .length,
        }),
        [skuRouteRows],
    );

    // ── Live DoH projections (read-only preview endpoint) ──
    const dohChannelKey =
        params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey ?? null;
    const dohPlanId = plan?.id ?? existingPlanId ?? null;

    // Current quantity for a row = sum across source-WH slots (edited value wins).
    const rowCurrentQty = useCallback(
        (row: SkuRouteRow): number => {
            let qty = 0;
            for (const fromWhId of Object.keys(row.fromWhSlots)) {
                const slot = row.fromWhSlots[fromWhId];
                const editKey =
                    slot.transferId ??
                    `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                const edited = editQtyMap.get(editKey);
                const base =
                    slot.cellState === 'recommended'
                        ? (slot.approvedQty ?? slot.recommendedQty)
                        : 0;
                qty += edited ?? base;
            }
            return qty;
        },
        [editQtyMap],
    );

    // Must mirror the compute call (handleCompute) exactly so the what-if engine runs with the
    // SAME constraints the plan was built with — especially the utilisation cap, which otherwise
    // lets the preview over-allocate past what's actually sendable.
    const dohPreviewBody = useCallback(
        (lines: PreviewTransferLine[]) => ({
            demandSource,
            // PO-driven plans: the OPEN_PO demand derives from this PO, so the what-if must carry it too.
            purchaseOrderId: resolvedPurchaseOrderId,
            targetDohDays: planTierMode
                ? undefined
                : (targetDohDays || undefined),
            milestone1DaysOverride: milestone1DaysOverride ?? undefined,
            milestone2DaysOverride: milestone2DaysOverride ?? undefined,
            drrLookbackDays: drrLookbackDays ?? undefined,
            drrWindowStart: effectiveDrrWindow?.start ?? undefined,
            drrWindowEnd: effectiveDrrWindow?.end ?? undefined,
            drrGrowthPct: drrGrowthPct ?? undefined,
            minSourceStockUnitsOverride:
                minSourceStockUnitsOverride ?? undefined,
            maxOutboundUtilizationPctOverride:
                maxOutboundUtilizationPctOverride ?? undefined,
            inboundInTransitDaysOverride:
                inboundInTransitDaysOverride ?? undefined,
            sourceReserveUnitsOverride: sourceReserveUnitsOverride ?? undefined,
            minInternalDohDaysOverride: minInternalDohDaysOverride ?? undefined,
            maxUnitsPerShipmentOverride:
                maxUnitsPerShipmentOverride ?? undefined,
            maxMeasurementPerShipmentOverride:
                maxMeasurementPerShipmentOverride ?? undefined,
            tierTargetDohDays:
                planTierMode && Object.keys(previewTierOverrides).length > 0
                    ? previewTierOverrides
                    : undefined,
            warehouseIds:
                !effectiveIsAggregated && selectedPlanWarehouseIds.length > 0
                    ? selectedPlanWarehouseIds
                    : null,
            pathIds:
                effectiveIsAggregated && selectedPathIds.length > 0
                    ? selectedPathIds
                    : null,
            sourceWarehouseIds:
                !effectiveIsAggregated && selectedSourceWarehouseIds.length > 0
                    ? selectedSourceWarehouseIds
                    : null,
            warehouseDohOverrides:
                Object.keys(previewWarehouseDohOverrides).length > 0
                    ? previewWarehouseDohOverrides
                    : undefined,
            lines,
        }),
        [
            demandSource,
            resolvedPurchaseOrderId,
            planTierMode,
            targetDohDays,
            milestone1DaysOverride,
            milestone2DaysOverride,
            drrLookbackDays,
            effectiveDrrWindow,
            drrGrowthPct,
            minSourceStockUnitsOverride,
            maxOutboundUtilizationPctOverride,
            inboundInTransitDaysOverride,
            sourceReserveUnitsOverride,
            minInternalDohDaysOverride,
            maxUnitsPerShipmentOverride,
            maxMeasurementPerShipmentOverride,
            previewTierOverrides,
            effectiveIsAggregated,
            selectedPlanWarehouseIds,
            selectedPathIds,
            selectedSourceWarehouseIds,
            previewWarehouseDohOverrides,
        ],
    );

    // Phase 2: seed the DoH cells from the values getPlan serves (the plan's frozen columns, or a
    // live fallback the backend computed with the PLAN's params for column-less DRAFTs). No on-load
    // preview — the live recompute below only runs once the user edits a quantity. Re-seeds when the
    // plan is (re)loaded, including after a recompute (planDate changes).
    useEffect(() => {
        if (!plan?.transfers) return;
        const seeded = new Map<
            string,
            {
                dest: number | null;
                internal: number | null;
                current: number | null;
            }
        >();
        for (const t of plan.transfers) {
            seeded.set(dohKeyFor(t.productId, t.toWarehouseId), {
                dest: t.destinationDohAfterReplenishment,
                internal: t.internalDohAfterEarmark,
                current: t.currentDestinationDoh,
            });
        }
        setDohByRowKey(seeded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan?.id, plan?.planDate]);

    // Debounced LIVE recompute of DoH cells — runs ONLY after the user changes a quantity (DoH
    // overrides go through handleDohEdit directly). On load the seeded values above are shown.
    // Only the CHANGED rows are previewed (qty-mode DoH is independent per row) and merged into the
    // seeded map, so editing one row recomputes just that row.
    useEffect(() => {
        if (
            viewState !== 'plan' ||
            !dohChannelKey ||
            !dohPlanId ||
            skuRouteRows.length === 0
        )
            return;
        const rowIsDirty = (row: SkuRouteRow): boolean => {
            // DoH-edited rows are handled by handleDohEdit (their DoH is pinned) — skip them here so we
            // don't fire a redundant preview or fight the pin.
            if (pinnedDoh.has(dohKeyFor(row.productId, row.toWarehouseId)))
                return false;
            for (const fromWhId of Object.keys(row.fromWhSlots)) {
                const slot = row.fromWhSlots[fromWhId];
                const editKey =
                    slot.transferId ??
                    `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                if (editQtyMap.has(editKey)) return true;
            }
            return false;
        };
        const dirtyRows =
            editQtyMap.size === 0 ? [] : skuRouteRows.filter(rowIsDirty);
        // Only rows whose qty changed since their last preview actually need recomputing — so editing
        // row B never re-previews or spins row A.
        const changedRows = dirtyRows.filter(
            (r) =>
                lastPreviewedQtyRef.current.get(
                    dohKeyFor(r.productId, r.toWarehouseId),
                ) !== rowCurrentQty(r),
        );
        const changedKeys = changedRows.map((r) =>
            dohKeyFor(r.productId, r.toWarehouseId),
        );
        const changedSet = new Set(changedKeys);
        // Reconcile: clear the DoH spinner for any qty-scoped row that's no longer being recomputed
        // (reverted, resolved, or wiped by a save/recompute) — nothing else would resolve it.
        setRowRecompute((prev) => {
            let changed = false;
            const next = new Map(prev);
            for (const [k, r] of prev) {
                if (
                    r.scope === 'doh' &&
                    r.pending > r.resolved &&
                    !changedSet.has(k)
                ) {
                    next.set(k, { ...r, resolved: r.pending });
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        if (editQtyMap.size === 0) lastPreviewedQtyRef.current.clear();
        if (changedRows.length === 0) return;
        // Remember the qty we're about to send per row, keyed by row — recorded only once the preview
        // succeeds so a change is compared against what was actually previewed (not the clamped result,
        // which would otherwise loop).
        const sentQtyByKey = new Map(
            changedRows.map((r) => [
                dohKeyFor(r.productId, r.toWarehouseId),
                rowCurrentQty(r),
            ]),
        );
        const lines: PreviewTransferLine[] = changedRows.map((r) => ({
            productId: r.productId,
            toWarehouseId: r.toWarehouseId,
            qty: rowCurrentQty(r),
        }));
        // Spinner on just these rows' DoH cells while the preview is pending/in-flight (instant feedback,
        // set synchronously — before the debounce even fires — so the stale DoH value is never shown).
        const seq = beginRecompute(changedKeys, 'doh');
        let cancelled = false;
        const handle = setTimeout(() => {
            previewDistributionTransfers(
                dohChannelKey,
                dohPlanId,
                dohPreviewBody(lines),
            )
                .then((results) => {
                    if (cancelled) return;
                    setDohByRowKey((prev) => {
                        const merged = new Map(prev);
                        for (const res of results) {
                            merged.set(
                                dohKeyFor(res.productId, res.toWarehouseId),
                                {
                                    dest: res.destinationDohAfterReplenishment,
                                    internal: res.internalDohAfterEarmark,
                                    current: res.currentDestinationDoh,
                                },
                            );
                        }
                        return merged;
                    });
                    for (const [k, q] of sentQtyByKey)
                        lastPreviewedQtyRef.current.set(k, q);
                })
                .catch(() => {
                    /* DoH cells fall back to "—" */
                })
                .finally(() => endRecompute(changedKeys, seq));
        }, 400);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [
        skuRouteRows,
        editQtyMap,
        viewState,
        dohChannelKey,
        dohPlanId,
        rowCurrentQty,
        dohPreviewBody,
        pinnedDoh,
        beginRecompute,
        endRecompute,
    ]);

    // Editing the channel DoH recomputes the suggested qty and applies it to the row's primary source WH.
    const handleDohEdit = useCallback(
        (row: SkuRouteRow, targetDoh: number) => {
            if (!dohChannelKey || !dohPlanId) return;
            // A DoH edit recomputes BOTH the plan-qty cells and the DoH cells — spinner on both ('both'
            // scope) until the engine responds, so the user sees the row settling rather than stale numbers.
            const rowKey = dohKeyFor(row.productId, row.toWarehouseId);
            const seq = beginRecompute([rowKey], 'both');
            previewDistributionTransfers(
                dohChannelKey,
                dohPlanId,
                dohPreviewBody([
                    {
                        productId: row.productId,
                        toWarehouseId: row.toWarehouseId,
                        targetDohDays: targetDoh,
                    },
                ]),
            )
                .then((results) => {
                    const res = results[0];
                    if (!res) return;
                    // Apply the engine's per-source-WH split to each slot (0 where unallocated) —
                    // same splitter the plan uses, so multi-source rows distribute correctly.
                    for (const fromWhId of Object.keys(row.fromWhSlots)) {
                        const slot = row.fromWhSlots[fromWhId];
                        const editKey =
                            slot.transferId ??
                            `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                        handleQtyChange(
                            editKey,
                            res.allocationsByFromWh[fromWhId] ?? 0,
                        );
                    }
                    // Pin the engine's DoH result so the qty-mode refresh doesn't drift it optically.
                    const key = dohKeyFor(row.productId, row.toWarehouseId);
                    setPinnedDoh((prev) => {
                        const next = new Map(prev);
                        next.set(key, {
                            dest: res.destinationDohAfterReplenishment,
                            internal: res.internalDohAfterEarmark,
                            current: res.currentDestinationDoh,
                        });
                        return next;
                    });
                    // Force the DoH input to re-sync to the resolved value.
                    setDohEditNonce((n) => n + 1);
                    const label = row.productName ?? row.productId;
                    if (res.targetMet) {
                        showSuccess(
                            `${label}: reached ${targetDoh}d DoH → ${res.recommendedQty} units`,
                        );
                    } else {
                        showError(
                            res.shortfallReason ??
                                `${label}: ${targetDoh}d DoH could not be reached.`,
                        );
                    }
                })
                .catch((e: unknown) => {
                    const msg =
                        e && typeof e === 'object' && 'message' in e
                            ? String((e as { message: unknown }).message)
                            : 'Could not recompute quantity for the new DoH';
                    showError(msg);
                })
                .finally(() => endRecompute([rowKey], seq));
        },
        [
            dohChannelKey,
            dohPlanId,
            dohPreviewBody,
            handleQtyChange,
            beginRecompute,
            endRecompute,
        ],
    );

    // transferId → DoH row-key, so a manual qty edit can clear that row's pin.
    const transferIdToDohKey = useMemo(() => {
        const m = new Map<string, string>();
        for (const row of skuRouteRows) {
            for (const slot of Object.values(row.fromWhSlots)) {
                if (slot.transferId)
                    m.set(
                        slot.transferId,
                        dohKeyFor(row.productId, row.toWarehouseId),
                    );
            }
        }
        return m;
    }, [skuRouteRows]);

    // Qty edits coming from the table cells clear any pinned DoH for that row (then defer to the
    // live qty-mode recompute). handleDohEdit uses the raw handleQtyChange so its own pin survives.
    const handleCellQtyChange = useCallback(
        (editKey: string, qty: number) => {
            const dohKey = editKey.startsWith('new::')
                ? (() => {
                      const p = editKey.split('::');
                      return `${p[1]}::${p[2]}`;
                  })()
                : transferIdToDohKey.get(editKey);
            if (dohKey) {
                setPinnedDoh((prev) => {
                    if (!prev.has(dohKey)) return prev;
                    const next = new Map(prev);
                    next.delete(dohKey);
                    return next;
                });
            }
            handleQtyChange(editKey, qty);
        },
        [handleQtyChange, transferIdToDohKey],
    );

    // ── Bulk row actions (the ⋮ menu) ──────────────────────────────────────────
    // Rows with at least one selected slot: a recommended transfer, an available (not-yet-planned)
    // slot, or an unsendable one — the operator can plan any of them by hand.
    const selectedActionRows = useMemo(
        () =>
            selectedTransferIds.size === 0
                ? []
                : skuRouteRows.filter((r) =>
                      Object.keys(r.fromWhSlots).some((fromWhId) =>
                          selectedTransferIds.has(slotEditKey(r, fromWhId)),
                      ),
                  ),
        [skuRouteRows, selectedTransferIds],
    );

    /**
     * Override the quantity of every selected row with its demand number — the PO quantity on a
     * PO-driven plan, else the forecast / DRR-based demand the engine sized against (the "Demand"
     * column). Netting against destination stock / in-transit is what the override deliberately
     * skips: the operator is saying "send the whole demand".
     *
     * This is also the bulk way to plan rows the engine left as *available*: the demand is written
     * into the row's `new::` cell key (what typing into the cell would do), so the row becomes a
     * planned line on the next Save Draft.
     *
     *  • Recommended rows split across several source WHs keep the engine's ratio (demand prorated
     *    by the recommended quantities, remainder to the largest slot) so the sources don't shuffle.
     *  • Available rows have no engine split, so the whole demand goes to the single source WH with
     *    the most stock on hand + inbound (first column on a tie) — the operator can re-split by hand.
     */
    const handleOverrideSelectedWithDemand = useCallback(() => {
        // Writes the whole demand, even above the source's sendable stock — that is the point of
        // the override; the cells flag anything over "avail" in red. A recommended row's demand is
        // prorated by the engine's split so the sources don't shuffle; everything else goes to one
        // source. Nothing is clamped.
        const maxUtil =
            maxOutboundUtilizationPctOverride ??
            channelDefaultMaxOutboundUtilizationPct ??
            1;
        const ceilingOf = (s: FromWhSlot): number =>
            slotSendableCeiling(s, maxUtil) ?? 0;
        let applied = 0;
        let planned = 0;
        let skipped = 0;
        let overAvail = 0;
        for (const row of selectedActionRows) {
            const demand = row.demandToMeet;
            if (demand == null) {
                skipped += 1;
                continue;
            }
            const target = Math.max(0, Math.ceil(demand));
            const selectedSlots = Object.entries(row.fromWhSlots).filter(
                ([fromWhId]) =>
                    selectedTransferIds.has(slotEditKey(row, fromWhId)),
            );
            const recommended = selectedSlots.filter(
                ([, s]) => s.cellState === 'recommended',
            );

            if (recommended.length > 0) {
                const totalRec = recommended.reduce((sum, [, s]) => sum + s.recommendedQty, 0);
                const shares = recommended.map(([, s]) =>
                    totalRec > 0
                        ? Math.floor((target * s.recommendedQty) / totalRec)
                        : Math.floor(target / recommended.length),
                );
                const remainder = target - shares.reduce((a, b) => a + b, 0);
                const largest = recommended.reduce(
                    (best, [, s], i) =>
                        s.recommendedQty > recommended[best][1].recommendedQty ? i : best,
                    0,
                );
                shares[largest] += remainder;
                if (recommended.some(([, s], i) => shares[i] > ceilingOf(s))) overAvail += 1;
                recommended.forEach(([fromWhId], i) =>
                    handleCellQtyChange(slotEditKey(row, fromWhId), shares[i]),
                );
                applied += 1;
                continue;
            }

            // Available / unsendable row: one source, the best-stocked selected slot, whole demand.
            const [bestFromWhId, bestSlot] = selectedSlots.reduce((best, cur) => {
                const avail = (s: FromWhSlot) => (s.fromWarehouseStock ?? 0) + s.fromWhInTransit;
                return avail(cur[1]) > avail(best[1]) ? cur : best;
            });
            if (target > ceilingOf(bestSlot)) overAvail += 1;
            handleCellQtyChange(slotEditKey(row, bestFromWhId), target);
            planned += 1;
        }
        const touched = applied + planned;
        if (touched > 0) {
            const parts: string[] = [];
            if (applied > 0) parts.push(`overrode ${applied} recommended row${applied === 1 ? '' : 's'}`);
            if (planned > 0) parts.push(`planned ${planned} available row${planned === 1 ? '' : 's'}`);
            const notes: string[] = [];
            if (overAvail > 0) notes.push(`${overAvail} above the source's available stock — flagged in red`);
            if (skipped > 0) notes.push(`${skipped} skipped — no demand figure`);
            showSuccess(
                `Set quantity to demand: ${parts.join(', ')}${
                    notes.length > 0 ? ` (${notes.join('; ')})` : ''
                }. Save the draft to keep the change.`,
            );
        } else {
            showError('None of the selected rows carry a demand figure to apply.');
        }
    }, [
        selectedActionRows,
        selectedTransferIds,
        handleCellQtyChange,
        maxOutboundUtilizationPctOverride,
        channelDefaultMaxOutboundUtilizationPct,
    ]);

    /**
     * Drop the local edits on every selected slot so the cell falls back to the engine's
     * suggestion — for an available row that means back to unplanned.
     */
    const handleRevertSelectedToSuggested = useCallback(() => {
        const ids = [...selectedTransferIds];
        setEditQtyMap((prev) => {
            const next = new Map(prev);
            for (const id of ids) next.delete(id);
            return next;
        });
        setPinnedDoh((prev) => {
            if (prev.size === 0) return prev;
            const next = new Map(prev);
            for (const id of ids) {
                // `new::${productId}::${toWh}::${fromWh}` → DoH key `${productId}::${toWh}`
                const key = isNewSlotKey(id)
                    ? id.split('::').slice(1, 3).join('::')
                    : transferIdToDohKey.get(id);
                if (key) next.delete(key);
            }
            return next;
        });
        showSuccess(`Reverted ${selectedActionRows.length} row${selectedActionRows.length === 1 ? '' : 's'} to the suggested quantity.`);
    }, [selectedTransferIds, selectedActionRows.length, transferIdToDohKey]);

    // Pinned DoH (from a DoH edit) takes precedence over the live qty-mode values.
    const mergedDoh = useMemo(() => {
        if (pinnedDoh.size === 0) return dohByRowKey;
        const m = new Map(dohByRowKey);
        for (const [k, v] of pinnedDoh) m.set(k, v);
        return m;
    }, [dohByRowKey, pinnedDoh]);

    // Apply state + shipment filters — in-memory; not persisted
    // Approved plans only show rows that got a shipment assigned — the rest were discarded by the user.
    const visibleSkuRouteRows = useMemo((): SkuRouteRow[] => {
        let rows = skuRouteRows;
        if (planStatus === 'APPROVED') {
            rows = rows.filter((r) =>
                Object.values(r.fromWhSlots).some(
                    (s) => s.saleOrderCode !== null,
                ),
            );
        }
        if (stateFilters.length > 0)
            rows = rows.filter((r) => stateFilters.includes(r.rowState));
        if (poRowsOnly) rows = rows.filter((r) => r._onPurchaseOrder);
        if (selectedShipmentCode) {
            rows = rows.filter((r) =>
                Object.values(r.fromWhSlots).some(
                    (s) => s.saleOrderCode === selectedShipmentCode,
                ),
            );
        }
        return rows;
    }, [skuRouteRows, stateFilters, poRowsOnly, selectedShipmentCode, planStatus]);

    // SKUs with a user-set qty drawing on a source below the send threshold (amber-flagged cells).
    const belowThresholdSetCount = useMemo(() => {
        const threshold = plan?.minSourceStockUnits;
        if (threshold == null || isReadonly) return 0;
        const skus = new Set<string>();
        for (const row of skuRouteRows) {
            for (const [fromWhId, slot] of Object.entries(row.fromWhSlots)) {
                if (slot.cellState === 'unsendable') continue;
                if (
                    slot.fromWarehouseStock == null ||
                    slot.fromWarehouseStock >= threshold
                )
                    continue;
                const editKey =
                    slot.transferId ??
                    `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                const cellQty =
                    editQtyMap.get(editKey) ??
                    slot.approvedQty ??
                    slot.recommendedQty;
                if (cellQty > 0) {
                    skus.add(row.productId);
                    break;
                }
            }
        }
        return skus.size;
    }, [skuRouteRows, editQtyMap, plan?.minSourceStockUnits, isReadonly]);

    // Per-store per-category capacity rollup (retail). Current/In-transit from the store's SKU universe;
    // Plan from the live (editQtyMap) transfers — same numbers as the capacity pill, split by Retail
    // Category bucket. Headroom = Ideal - Total. SKUs with no Retail Category are not capacity-managed,
    // so the uncategorized "Other" pool is omitted from this table.
    const categoryCapacityRows = useMemo((): CategoryCapRow[] => {
        if (!plan || plan.isAggregated || !selectedWarehouseId) return [];
        const caps = plan.warehouseCategoryCapacities?.[selectedWarehouseId];
        if (!caps || Object.keys(caps).length === 0) return [];
        const bucketOf = (pid: string) =>
            plan.productRetailCategory?.[pid] ?? 'Other';
        const acc = new Map<
            string,
            { current: number; intransit: number; plan: number }
        >();
        const bump = (bucket: string) => {
            let e = acc.get(bucket);
            if (!e) {
                e = { current: 0, intransit: 0, plan: 0 };
                acc.set(bucket, e);
            }
            return e;
        };
        for (const [pid, sku] of skuContextMap) {
            const e = bump(bucketOf(pid));
            e.current += sku.channelStock ?? 0;
            e.intransit += sku.inTransit ?? 0;
        }
        for (const t of warehouseFilteredTransfers) {
            if (t.isExcluded) continue;
            bump(bucketOf(t.productId)).plan +=
                editQtyMap.get(t.id) ?? t.approvedQty ?? t.recommendedQty;
        }
        const order: string[] = [...Object.keys(caps)];
        for (const b of acc.keys())
            if (!order.includes(b) && b !== 'Other') order.push(b);
        return order.map((bucket) => {
            const e = acc.get(bucket) ?? { current: 0, intransit: 0, plan: 0 };
            const ideal = caps[bucket] ?? null;
            const total = e.current + e.intransit;
            return {
                bucket,
                ideal,
                current: e.current,
                intransit: e.intransit,
                total,
                headroom: ideal != null ? ideal - total : null,
                plan: e.plan,
            };
        });
    }, [
        plan,
        selectedWarehouseId,
        skuContextMap,
        warehouseFilteredTransfers,
        editQtyMap,
    ]);

    const categoryCapColumns = useMemo(
        (): DataSheetColumn<CategoryCapRow>[] => [
            {
                key: 'bucket',
                header: 'Category',
                width: 190,
                sticky: true,
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            fontWeight: r.bucket === 'Other' ? 400 : 500,
                            color:
                                r.bucket === 'Other'
                                    ? neutralColors.textMuted
                                    : neutralColors.textPrimary,
                        }}
                    >
                        {r.bucket}
                    </Text>
                ),
            },
            {
                key: 'ideal',
                header: 'Ideal',
                width: 64,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            color: neutralColors.textSecondary,
                        }}
                    >
                        {r.ideal != null ? formatUnits(r.ideal) : '—'}
                    </Text>
                ),
            },
            {
                key: 'current',
                header: 'Current',
                width: 72,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            color: neutralColors.textSecondary,
                        }}
                    >
                        {formatUnits(Math.ceil(r.current))}
                    </Text>
                ),
            },
            {
                key: 'intransit',
                header: 'In-transit',
                width: 80,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            color:
                                r.intransit > 0
                                    ? semanticColors.info.primary
                                    : neutralColors.textMuted,
                        }}
                    >
                        {formatUnits(Math.ceil(r.intransit))}
                    </Text>
                ),
            },
            {
                key: 'total',
                header: 'Total',
                width: 64,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            color: neutralColors.textPrimary,
                        }}
                    >
                        {formatUnits(Math.ceil(r.total))}
                    </Text>
                ),
            },
            {
                key: 'headroom',
                header: 'Headroom',
                width: 90,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color:
                                r.headroom == null
                                    ? neutralColors.textMuted
                                    : r.headroom < 0
                                      ? semanticColors.critical.primary
                                      : semanticColors.positive.primary,
                        }}
                    >
                        {r.headroom != null
                            ? formatUnits(Math.ceil(r.headroom))
                            : '—'}
                    </Text>
                ),
            },
            {
                key: 'plan',
                header: 'Plan',
                width: 64,
                align: 'right',
                render: (_, r): ReactNode => (
                    <Text
                        style={{
                            fontSize: 12,
                            color:
                                r.plan > 0
                                    ? neutralColors.textPrimary
                                    : neutralColors.textMuted,
                        }}
                    >
                        {formatUnits(Math.ceil(r.plan))}
                    </Text>
                ),
            },
        ],
        [],
    );

    // Pin highlighted (just-added) row to the top so it's immediately visible after save+refetch
    const sortedSkuRouteRows = useMemo((): SkuRouteRow[] => {
        if (!lastAddedProductId) return visibleSkuRouteRows;
        const idx = visibleSkuRouteRows.findIndex(
            (r) => r.productId === lastAddedProductId,
        );
        if (idx <= 0) return visibleSkuRouteRows;
        return [
            visibleSkuRouteRows[idx],
            ...visibleSkuRouteRows.slice(0, idx),
            ...visibleSkuRouteRows.slice(idx + 1),
        ];
    }, [visibleSkuRouteRows, lastAddedProductId]);

    // Row selection handlers. The selection set holds slot keys: transfer ids for persisted
    // slots and `new::` edit keys for available ones (see slotEditKey), so recommended AND
    // available rows can be selected for the ⋮ bulk actions. Unsendable-only rows have no keys.
    const selectableSkuRouteRows = useMemo(
        () => visibleSkuRouteRows.filter((r) => rowSelectionKeys(r).length > 0),
        [visibleSkuRouteRows],
    );
    const handleToggleSelect = useCallback(
        (rowKey: string) => {
            const row = visibleSkuRouteRows.find((r) => r.rowKey === rowKey);
            if (!row) return;
            const keys = rowSelectionKeys(row);
            setSelectedTransferIds((prev) => {
                const next = new Set(prev);
                const allSelected = keys.every((id) => prev.has(id));
                for (const id of keys) {
                    if (allSelected) next.delete(id);
                    else next.add(id);
                }
                return next;
            });
        },
        [visibleSkuRouteRows],
    );
    const handleToggleAll = useCallback(() => {
        const allKeys = selectableSkuRouteRows.flatMap(rowSelectionKeys);
        setSelectedTransferIds((prev) => {
            const allSelected = allKeys.every((id) => prev.has(id));
            return allSelected ? new Set() : new Set(allKeys);
        });
    }, [selectableSkuRouteRows]);
    // Only real transfer ids reach approval — a selected available row has nothing to approve yet.
    const selectedPersistedTransferIds = useMemo(() => {
        const ids = new Set<string>();
        for (const id of selectedTransferIds) if (!isNewSlotKey(id)) ids.add(id);
        return ids;
    }, [selectedTransferIds]);

    const downloadPlanCsv = useCallback(
        (selectedKeys: Set<string>) => {
            if (!plan) return;

            // Flatten SKU×Route rows to one CSV line per SKU×FromWH transfer slot.
            const cols = PLAN_EXPORT_COLS.filter(
                (c) => c.locked || selectedKeys.has(c.key),
            );
            const lines: string[] = [];

            for (const row of skuRouteRows) {
                const toWarehouse =
                    row.toWarehouseName ??
                    row.toWarehouseId ??
                    plan.channelAbbreviationKey;
                for (const [fromWhId, slot] of Object.entries(
                    row.fromWhSlots,
                )) {
                    const editKey =
                        slot.transferId ??
                        `new::${row.productId}::${row.toWarehouseId ?? 'agg'}::${fromWhId}`;
                    if (
                        slot.cellState === 'available' &&
                        !editQtyMap.has(editKey)
                    )
                        continue;
                    const qty =
                        editQtyMap.get(editKey) ??
                        slot.approvedQty ??
                        slot.recommendedQty;
                    const ctx: PlanExportRowCtx = {
                        row,
                        slot,
                        qty,
                        fromWhName: fromWhNameMap.get(fromWhId) ?? fromWhId,
                        toWarehouse,
                    };
                    lines.push(
                        cols
                            .map(
                                (col) =>
                                    `"${col.value(ctx).replace(/"/g, '""')}"`,
                            )
                            .join(','),
                    );
                }
            }

            const csv = [cols.map((c) => c.key).join(','), ...lines].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${plan.name ?? 'distribution-plan'}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        },
        [plan, skuRouteRows, editQtyMap, fromWhNameMap],
    );

    // Bulk-override template: always the full column set, so it round-trips cleanly.
    const handleDownloadTemplate = useCallback(
        () => downloadPlanCsv(new Set(PLAN_EXPORT_COLS.map((c) => c.key))),
        [downloadPlanCsv],
    );

    // Pre-checked selection when the picker opens: saved choice if any, else mirror
    // the table's currently-visible columns (plus the always-on / locked ones).
    const seedExportSelection = useCallback((): Set<string> => {
        const isAgg = !!plan?.isAggregated;
        if (savedExportCols) {
            return new Set(
                savedExportCols.filter((k) =>
                    PLAN_EXPORT_COLS.some((c) => c.key === k),
                ),
            );
        }
        return new Set(
            PLAN_EXPORT_COLS.filter((c) =>
                c.seedVisible(hiddenPlanCols, isAgg),
            ).map((c) => c.key),
        );
    }, [savedExportCols, hiddenPlanCols, plan?.isAggregated]);

    const handleOpenDownloadModal = useCallback(() => {
        setDraftExportCols(seedExportSelection());
        openDownloadModal();
    }, [seedExportSelection, openDownloadModal]);

    const handleConfirmDownload = useCallback(() => {
        const selected = new Set(draftExportCols);
        for (const c of PLAN_EXPORT_COLS) if (c.locked) selected.add(c.key);
        setSavedExportCols([...selected]);
        downloadPlanCsv(selected);
        closeDownloadModal();
    }, [
        draftExportCols,
        downloadPlanCsv,
        setSavedExportCols,
        closeDownloadModal,
    ]);

    const toggleExportCol = useCallback((key: string) => {
        setDraftExportCols((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleUploadCsv = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            // Target store: a store must be selected on non-aggregated plans (aggregated -> toWh null).
            const isAgg = !!plan?.isAggregated;
            if (!isAgg && !selectedWarehouseId) {
                showError('Select a store before uploading overrides.');
                e.target.value = '';
                return;
            }
            const toWhId = isAgg ? null : selectedWarehouseId;

            // Primary source WH = the one the selected store's existing lines predominantly use.
            const sourceFreq = new Map<string, number>();
            for (const t of warehouseFilteredTransfers) {
                sourceFreq.set(
                    t.fromWarehouseId,
                    (sourceFreq.get(t.fromWarehouseId) ?? 0) + 1,
                );
            }
            let primarySourceWh: string | null = null;
            let maxFreq = 0;
            for (const [wh, n] of sourceFreq)
                if (n > maxFreq) {
                    maxFreq = n;
                    primarySourceWh = wh;
                }

            // SKUs valid for this store: the channel universe plus anything already on a line.
            const validSkus = new Set<string>(skuContextMap.keys());
            for (const t of warehouseFilteredTransfers)
                validSkus.add(t.productId);

            const reader = new FileReader();
            reader.onload = (ev) => {
                const text = ev.target?.result as string;
                const lines = text
                    .split('\n')
                    .map((l) => l.trim())
                    .filter(Boolean);
                if (lines.length < 2) {
                    showError('CSV has no data rows.');
                    return;
                }
                const headerCols = lines[0]
                    .split(',')
                    .map((c) => c.replace(/^"|"$/g, '').trim().toLowerCase());
                const productIdIdx = headerCols.indexOf('productid');
                const planQtyIdx = headerCols.indexOf('planquantity');
                if (productIdIdx === -1 || planQtyIdx === -1) {
                    showError(
                        'CSV must have productId and planQuantity columns.',
                    );
                    return;
                }

                // Parse rows; last value wins on a repeated productId.
                const parsed = new Map<string, number>();
                const duplicates = new Set<string>();
                const skipped: Array<{ productId: string; reason: string }> =
                    [];
                for (const line of lines.slice(1)) {
                    const cols = line
                        .split(',')
                        .map((c) =>
                            c.replace(/^"|"$/g, '').replace(/""/g, '"'),
                        );
                    const productId = cols[productIdIdx]?.trim();
                    const qtyStr = cols[planQtyIdx]?.trim();
                    if (!productId) continue;
                    const qty = parseFloat(qtyStr ?? '');
                    if (!qtyStr || isNaN(qty)) {
                        skipped.push({ productId, reason: 'Invalid quantity' });
                        continue;
                    }
                    if (parsed.has(productId)) duplicates.add(productId);
                    parsed.set(productId, qty);
                }

                // Classify into existing-line updates and new-line adds, scoped to the selected store.
                const staged = new Map<string, number>();
                let updated = 0;
                let added = 0;
                for (const [productId, qty] of parsed) {
                    const existing = warehouseFilteredTransfers.filter(
                        (t) => t.productId === productId,
                    );
                    if (existing.length > 0) {
                        for (const t of existing) staged.set(t.id, qty);
                        updated += 1;
                    } else if (!validSkus.has(productId)) {
                        skipped.push({
                            productId,
                            reason: 'Not a SKU in this channel',
                        });
                    } else if (qty <= 0) {
                        skipped.push({
                            productId,
                            reason: 'No line yet and quantity is 0',
                        });
                    } else if (!primarySourceWh) {
                        skipped.push({
                            productId,
                            reason: 'No source warehouse for this store',
                        });
                    } else {
                        staged.set(
                            `new::${productId}::${toWhId ?? 'agg'}::${primarySourceWh}`,
                            qty,
                        );
                        added += 1;
                    }
                }

                if (staged.size === 0 && skipped.length === 0) {
                    showError(
                        'No productId / planQuantity rows found in the CSV.',
                    );
                    return;
                }
                if (staged.size > 0) {
                    // Stored locally — user persists via Save Draft / Save and Approve.
                    setEditQtyMap((prev) => {
                        const next = new Map(prev);
                        staged.forEach((qty, key) => next.set(key, qty));
                        return next;
                    });
                }
                setBulkUploadResult({
                    updated,
                    added,
                    skipped,
                    duplicates: [...duplicates],
                });
                setBulkUploadPhase('done');
            };
            reader.readAsText(file);
            e.target.value = '';
        },
        [
            plan?.isAggregated,
            selectedWarehouseId,
            warehouseFilteredTransfers,
            skuContextMap,
        ],
    );

    const aggregateRow = useMemo((): Record<string, ReactNode> => {
        const rows = dsFilteredRows ?? visibleSkuRouteRows;
        if (rows.length === 0) return {};
        const result: Record<string, ReactNode> = {};

        // Per-From-WH plan qty totals (dynamic columns)
        for (const fromWhId of uniqueFromWhIds) {
            const { totalUnits, totalMeasurement } = rows.reduce(
                (s, r) => {
                    const slot = r.fromWhSlots[fromWhId];
                    if (!slot) return s;
                    const editKey =
                        slot.transferId ??
                        `new::${r.productId}::${r.toWarehouseId ?? 'agg'}::${fromWhId}`;
                    const qty =
                        editQtyMap.get(editKey) ??
                        slot.approvedQty ??
                        slot.recommendedQty;
                    return {
                        totalUnits: s.totalUnits + qty,
                        totalMeasurement:
                            s.totalMeasurement +
                            qty * (slot.measurementValue ?? 0),
                    };
                },
                { totalUnits: 0, totalMeasurement: 0 },
            );
            if (totalUnits > 0) {
                const measurementLabel =
                    channelMeasurementUnit && totalMeasurement > 0
                        ? channelMeasurementUnit === 'weight'
                            ? totalMeasurement >= 1000
                                ? `${(totalMeasurement / 1000).toFixed(2)} kg`
                                : `${totalMeasurement.toFixed(0)} g`
                            : `${totalMeasurement.toFixed(2)} CBM`
                        : null;
                result[`fromWh_${fromWhId}`] = measurementLabel ? (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {formatUnits(Math.ceil(totalUnits))}
                        <span
                            style={{
                                fontSize: 10,
                                fontWeight: 400,
                                opacity: 0.65,
                                marginLeft: 6,
                            }}
                        >
                            {measurementLabel}
                        </span>
                    </span>
                ) : (
                    formatUnits(Math.ceil(totalUnits))
                );
            }
        }

        const channelStocks = rows.filter((r) => r._channelStock != null);
        if (channelStocks.length) {
            result._channelStock = formatUnits(
                Math.ceil(
                    channelStocks.reduce((s, r) => s + r._channelStock!, 0),
                ),
            );
        }

        const totalInTransit = rows.reduce((s, r) => s + (r.inTransit ?? 0), 0);
        if (totalInTransit > 0)
            result.inTransit = formatUnits(Math.ceil(totalInTransit));

        const ltd = rows.filter((r) => r.leadTimeDemand != null);
        if (ltd.length) {
            result.leadTimeDemand = formatUnits(
                Math.ceil(ltd.reduce((s, r) => s + r.leadTimeDemand!, 0)),
            );
        }

        const dtm = rows.filter((r) => r.demandToMeet != null);
        if (dtm.length) {
            result.demandToMeet = formatUnits(
                Math.ceil(dtm.reduce((s, r) => s + r.demandToMeet!, 0)),
            );
        }

        const priced = rows.filter((r) => rowSendingValue(r) != null);
        if (priced.length) {
            result.orderedValue = formatMoney(priced.reduce((s, r) => s + (r.orderedValue ?? 0), 0));
            result.sendingValue = formatMoney(
                priced.reduce((s, r) => s + (rowSendingValue(r, editQtyMap) ?? 0), 0),
            );
        }

        const forecasts = rows.filter((r) => r.forecastDemand != null);
        if (forecasts.length) {
            result.forecastDemand = formatUnits(
                Math.ceil(forecasts.reduce((s, r) => s + r.forecastDemand!, 0)),
            );
        }

        const drrs = rows.filter((r) => r.drrDemand != null);
        if (drrs.length) {
            result.drrDemand = formatUnits(
                Math.ceil(drrs.reduce((s, r) => s + r.drrDemand!, 0)),
            );
        }

        const pastSales = rows.filter((r) => r.pastSalesQty != null);
        if (pastSales.length) {
            result.pastSalesQty = formatUnits(
                Math.ceil(pastSales.reduce((s, r) => s + r.pastSalesQty!, 0)),
            );
        }

        return result;
    }, [
        dsFilteredRows,
        visibleSkuRouteRows,
        editQtyMap,
        uniqueFromWhIds,
        channelMeasurementUnit,
    ]);

    const demandSourceLabel = useMemo(() => {
        const src = (plan?.demandSource ?? demandSource) as DemandSource;
        if (src === 'FORECAST') return 'Forecast';
        if (src === 'PAST_SALES') return 'DRR';
        if (src === 'MAX_OF_FORECAST_PAST_SALES') return 'Max';
        if (src === 'OPEN_PO') return 'Open PO';
        return 'Demand';
    }, [plan?.demandSource, demandSource]);

    // Live past-sales fetch — only while the column is visible. Changing the window re-reads
    // this per-(store,SKU) data without recomputing the plan.
    const pastSalesColVisible = !hiddenPlanCols.has('pastSalesQty');
    useEffect(() => {
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey || !pastSalesColVisible) return;
        let cancelled = false;
        setPastSalesLoading(true);
        setPastSalesError(false);
        getChannelPastSales(channelKey, pastSalesLookbackDays)
            .then((rows) => {
                if (cancelled) return;
                const map = new Map<string, number>();
                for (const r of rows)
                    map.set(`${r.productId}::${r.warehouseId ?? 'agg'}`, r.qty);
                setPastSalesMap(map);
                setPastSalesError(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // Don't let a failed fetch read as "0 sales everywhere" — surface it as '—'.
                console.error(
                    'Failed to load past-sales reference column',
                    err,
                );
                setPastSalesMap(new Map());
                setPastSalesError(true);
            })
            .finally(() => {
                if (!cancelled) setPastSalesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        params?.channelAbbreviationKey,
        plan?.channelAbbreviationKey,
        pastSalesLookbackDays,
        pastSalesColVisible,
    ]);

    // Marketplace SKU / secondary id for the whole channel. Master data, so it is read live rather
    // than from the plan's frozen snapshot — a re-listed SKU should show its current id.
    useEffect(() => {
        const channelKey =
            params?.channelAbbreviationKey ?? plan?.channelAbbreviationKey;
        if (!channelKey) return;
        let cancelled = false;
        getChannelProductMappings(channelKey)
            .then((rows) => {
                if (cancelled) return;
                setChannelMappingMap(new Map(rows.map((r) => [r.productId, r])));
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // Leave the map empty — the columns render '—' rather than a wrong id.
                console.error('Failed to load channel product mappings', err);
                setChannelMappingMap(new Map());
            });
        return () => {
            cancelled = true;
        };
    }, [params?.channelAbbreviationKey, plan?.channelAbbreviationKey]);

    const columns = useMemo(
        () =>
            buildSkuRouteColumns({
                editQtyMap,
                onQtyChange: handleCellQtyChange,
                isReadonly,
                hidden: hiddenPlanCols,
                isAggregated: plan?.isAggregated ?? true,
                uniqueFromWhIds,
                fromWhNameMap,
                demandSourceLabel,
                onRemoveRow: !isReadonly ? handleRemoveRow : undefined,
                selectedIds: !isReadonly ? selectedTransferIds : undefined,
                allIds: !isReadonly
                    ? selectableSkuRouteRows.flatMap(rowSelectionKeys)
                    : undefined,
                onToggleSelect: !isReadonly ? handleToggleSelect : undefined,
                onToggleAll: !isReadonly ? handleToggleAll : undefined,
                shipments: shipments.length > 0 ? shipments : undefined,
                channelMeasurementUnit,
                pastSalesLookbackDays,
                pastSalesLoading,
                pastSalesError,
                minSourceStockUnits: plan?.minSourceStockUnits ?? null,
                maxOutboundUtilizationPct:
                    maxOutboundUtilizationPctOverride ??
                    channelDefaultMaxOutboundUtilizationPct ??
                    null,
                dohByRowKey: mergedDoh,
                onDohEdit: !isReadonly ? handleDohEdit : undefined,
                dohEditNonce,
                isDohRecomputing,
                isQtyRecomputing,
            }),
        [
            editQtyMap,
            handleCellQtyChange,
            isReadonly,
            hiddenPlanCols,
            plan?.isAggregated,
            uniqueFromWhIds,
            fromWhNameMap,
            demandSourceLabel,
            handleRemoveRow,
            selectedTransferIds,
            selectableSkuRouteRows,
            handleToggleSelect,
            handleToggleAll,
            shipments,
            channelMeasurementUnit,
            pastSalesLookbackDays,
            pastSalesLoading,
            pastSalesError,
            plan?.minSourceStockUnits,
            maxOutboundUtilizationPctOverride,
            channelDefaultMaxOutboundUtilizationPct,
            mergedDoh,
            handleDohEdit,
            dohEditNonce,
            isDohRecomputing,
            isQtyRecomputing,
        ],
    );

    // (route-resolved warehouses moved above handleSaveDraftEdits — needed there too)

    // ── Approve plan handler ──
    const handlePlanApproved = useCallback(async () => {
        if (!plan) return;
        try {
            const updated = await getDistributionPlan(plan.id);
            setPlan(updated);
            clearUndoStack();
        } catch {
            setPlan((prev) => (prev ? { ...prev, status: 'APPROVED' } : prev));
        }
    }, [plan, clearUndoStack]);

    // ── Settings screen ──
    if (viewState === 'settings' || viewState === 'computing') {
        return (
            <SettingsPanel
                channelName={channelName}
                channelSupportsOpenPo={
                    params?.demandSource === 'OPEN_PO' ||
                    plan?.demandSource === 'OPEN_PO'
                }
                demandSource={demandSource}
                purchaseOrderId={resolvedPurchaseOrderId}
                scopedProductCount={params?.productIds?.length ?? null}
                targetDohDays={targetDohDays}
                milestone1DaysOverride={milestone1DaysOverride}
                milestone2DaysOverride={milestone2DaysOverride}
                drrLookbackDays={drrLookbackDays}
                drrWindowStart={drrWindowStart}
                drrWindowEnd={drrWindowEnd}
                drrGrowthPct={drrGrowthPct}
                minSourceStockUnitsOverride={minSourceStockUnitsOverride}
                maxOutboundUtilizationPctOverride={
                    maxOutboundUtilizationPctOverride
                }
                inboundInTransitDaysOverride={inboundInTransitDaysOverride}
                sourceReserveUnitsOverride={sourceReserveUnitsOverride}
                minInternalDohDaysOverride={minInternalDohDaysOverride}
                channelDefaultMinSourceStockUnits={
                    channelDefaultMinSourceStockUnits
                }
                channelDefaultMaxOutboundUtilizationPct={
                    channelDefaultMaxOutboundUtilizationPct
                }
                channelDefaultInboundInTransitDays={
                    channelDefaultInboundInTransitDays
                }
                channelDefaultSourceReserveUnits={
                    channelDefaultSourceReserveUnits
                }
                channelDefaultMinInternalDohDays={
                    channelDefaultMinInternalDohDays
                }
                channelDefaultMilestone1Days={channelDefaultMilestone1Days}
                channelDefaultMilestone2Days={channelDefaultMilestone2Days}
                channelTierMode={channelTierMode}
                tierDohOverride={tierDohOverride}
                onDemandSourceChange={setDemandSource}
                onTargetDohChange={setTargetDohDays}
                onMilestone1Change={setMilestone1DaysOverride}
                onMilestone2Change={setMilestone2DaysOverride}
                onDrrLookbackChange={setDrrLookbackDays}
                onDrrWindowChange={handleDrrWindowChange}
                onDrrGrowthChange={setDrrGrowthPct}
                onMinSourceStockUnitsChange={setMinSourceStockUnitsOverride}
                onMaxOutboundUtilizationPctChange={
                    setMaxOutboundUtilizationPctOverride
                }
                onInboundInTransitDaysChange={setInboundInTransitDaysOverride}
                onSourceReserveUnitsChange={setSourceReserveUnitsOverride}
                onMinInternalDohDaysChange={setMinInternalDohDaysOverride}
                onMaxUnitsPerShipmentChange={setMaxUnitsPerShipmentOverride}
                onMaxMeasurementPerShipmentChange={
                    setMaxMeasurementPerShipmentOverride
                }
                onTierDohOverrideChange={handleTierDohOverrideChange}
                onCompute={handleCompute}
                scheduleAlert={scheduleAlert}
                computing={viewState === 'computing'}
                onBack={onBack}
                isNew={!hasExisting}
                isAggregated={effectiveIsAggregated}
                planWarehouses={planWarehouses}
                warehouseRules={warehouseRules}
                selectedPlanWarehouseIds={selectedPlanWarehouseIds}
                onWarehouseIdsChange={setSelectedPlanWarehouseIds}
                planWarehouseOptionsLoading={planWarehouseOptionsLoading}
                planRuleOverrides={planRuleOverrides}
                onPlanFlatOverrideChange={handlePlanFlatOverrideChange}
                onPlanTierOverrideChange={handlePlanTierOverrideChange}
                availablePaths={availablePaths}
                selectedPathIds={selectedPathIds}
                onPathIdsChange={setSelectedPathIds}
                pathsLoading={pathsLoading}
                sourceWarehouseOptions={sourceWarehouseOptions}
                selectedSourceWarehouseIds={selectedSourceWarehouseIds}
                onSourceWarehouseIdsChange={setSelectedSourceWarehouseIds}
                maxUnitsPerShipmentOverride={maxUnitsPerShipmentOverride}
                maxMeasurementPerShipmentOverride={
                    maxMeasurementPerShipmentOverride
                }
                channelDefaultMaxUnitsPerShipment={
                    channelDefaultMaxUnitsPerShipment
                }
                channelDefaultMaxMeasurementPerShipment={
                    channelDefaultMaxMeasurementPerShipment
                }
                channelMeasurementUnit={channelMeasurementUnit}
            />
        );
    }

    // ── Error screen ──
    if (viewState === 'error') {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Group pb="md" style={{ flexShrink: 0 }}>
                    <Button
                        variant="subtle"
                        color="dark"
                        size="xs"
                        leftSection={<ArrowLeft size={14} />}
                        onClick={handleBack}
                    >
                        {channelName}
                    </Button>
                </Group>
                <Stack
                    align="center"
                    justify="center"
                    style={{ flex: 1 }}
                    gap={8}
                >
                    <WarningCircle
                        size={32}
                        color={semanticColors.warning.primary}
                    />
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: neutralColors.textSecondary,
                        }}
                    >
                        Could not compute plan
                    </Text>
                    <Text
                        style={{
                            fontSize: 13,
                            color: neutralColors.textMuted,
                            textAlign: 'center',
                            maxWidth: 360,
                        }}
                    >
                        Check that distribution paths are configured for{' '}
                        {channelName}.
                    </Text>
                    <Group gap={8}>
                        <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => setViewState('settings')}
                        >
                            Back to Settings
                        </Button>
                        <Button size="xs" onClick={handleCompute}>
                            Retry
                        </Button>
                    </Group>
                </Stack>
            </div>
        );
    }

    // ── Loading screen (existingPlanId path) ──
    if (loadingPlan) {
        const displayName = existingPlanName ?? plan?.name;
        return (
            <Stack
                align="center"
                justify="center"
                style={{ flex: 1, minHeight: 200 }}
                gap={10}
            >
                <Loader size="sm" />
                <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                    {displayName
                        ? `Loading plan "${displayName}"…`
                        : 'Loading plan…'}
                </Text>
            </Stack>
        );
    }

    // ── Plan view ──
    // Source-stock status banners — excluded (red) + below-threshold overrides (amber).
    // Stacked vertically so they tuck into the capacity pill's left column; rendered as a
    // standalone block only when no capacity pill is shown (aggregated / no store selected).
    const statusBanners =
        lowStockTransfers.length > 0 || belowThresholdSetCount > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {lowStockTransfers.length > 0 && (
                    <Group
                        gap={8}
                        align="center"
                        wrap="nowrap"
                        style={{
                            padding: '7px 12px',
                            borderRadius: 8,
                            backgroundColor: 'rgba(220,38,38,0.03)',
                            border: '1px solid rgba(220,38,38,0.12)',
                        }}
                    >
                        <Tooltip
                            label="These SKUs are shown below with a red stripe — set a qty to include them in the plan"
                            withArrow
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    flexShrink: 0,
                                }}
                            >
                                <WarningCircle
                                    size={16}
                                    color={semanticColors.critical.primary}
                                    weight="fill"
                                />
                            </span>
                        </Tooltip>
                        <Text
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: semanticColors.critical.primary,
                            }}
                        >
                            {lowStockTransfers.length} line
                            {lowStockTransfers.length !== 1 ? 's' : ''} with
                            demand but nothing sendable at the source
                            {plan?.minSourceStockUnits != null
                                ? ` (no stock or below the ${plan.minSourceStockUnits}-unit minimum)`
                                : ' (no stock)'}{' '}
                            — set a qty to send anyway (
                            {
                                new Set(
                                    lowStockTransfers.map((t) => t.productId),
                                ).size
                            }{' '}
                            SKU
                            {new Set(lowStockTransfers.map((t) => t.productId))
                                .size !== 1
                                ? 's'
                                : ''}
                            )
                        </Text>
                    </Group>
                )}
                {belowThresholdSetCount > 0 && (
                    <Group
                        gap={8}
                        align="center"
                        wrap="nowrap"
                        style={{
                            padding: '7px 12px',
                            borderRadius: 8,
                            backgroundColor: 'rgba(245,159,0,0.04)',
                            border: '1px solid rgba(245,159,0,0.18)',
                        }}
                    >
                        <WarningCircle
                            size={16}
                            color="var(--mantine-color-orange-6)"
                            weight="fill"
                            style={{ flexShrink: 0 }}
                        />
                        <Text
                            style={{
                                fontSize: 12,
                                color: 'var(--mantine-color-orange-8)',
                            }}
                        >
                            {belowThresholdSetCount} SKU
                            {belowThresholdSetCount !== 1 ? 's' : ''} set below
                            the {plan?.minSourceStockUnits}-unit minimum — may
                            be capped at approval
                        </Text>
                    </Group>
                )}
            </div>
        ) : null;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                gap: 12,
            }}
        >
            {scheduleAlert && (
                <ScheduleAlertDialog
                    opened={headerScheduleOpen}
                    onClose={() => setHeaderScheduleOpen(false)}
                    title={`Schedule: what to send to ${channelName}`}
                    description="The plan engine runs on this schedule with this plan's settings pinned and saves its draft. The SKUs it would send show as a card on Home; opening it opens that draft."
                    alert={scheduleAlert}
                />
            )}
            {/* Edit Settings modal */}
            <Modal
                opened={settingsOpen}
                onClose={closeSettings}
                title={
                    <Text style={{ fontSize: 15, fontWeight: 700 }}>
                        Plan Settings — {channelName}
                    </Text>
                }
                size={900}
                centered
            >
                <SettingsForm
                    channelName={channelName}
                    channelSupportsOpenPo={
                        params?.demandSource === 'OPEN_PO' ||
                        plan?.demandSource === 'OPEN_PO'
                    }
                    demandSource={demandSource}
                    purchaseOrderId={resolvedPurchaseOrderId}
                    scopedProductCount={params?.productIds?.length ?? null}
                    targetDohDays={targetDohDays}
                    milestone1DaysOverride={milestone1DaysOverride}
                    milestone2DaysOverride={milestone2DaysOverride}
                    drrLookbackDays={drrLookbackDays}
                    drrWindowStart={drrWindowStart}
                    drrWindowEnd={drrWindowEnd}
                    drrGrowthPct={drrGrowthPct}
                    minSourceStockUnitsOverride={minSourceStockUnitsOverride}
                    maxOutboundUtilizationPctOverride={
                        maxOutboundUtilizationPctOverride
                    }
                    inboundInTransitDaysOverride={inboundInTransitDaysOverride}
                    sourceReserveUnitsOverride={sourceReserveUnitsOverride}
                    minInternalDohDaysOverride={minInternalDohDaysOverride}
                    channelDefaultMinSourceStockUnits={
                        channelDefaultMinSourceStockUnits
                    }
                    channelDefaultMaxOutboundUtilizationPct={
                        channelDefaultMaxOutboundUtilizationPct
                    }
                    channelDefaultInboundInTransitDays={
                        channelDefaultInboundInTransitDays
                    }
                    channelDefaultSourceReserveUnits={
                        channelDefaultSourceReserveUnits
                    }
                    channelDefaultMinInternalDohDays={
                        channelDefaultMinInternalDohDays
                    }
                    channelDefaultMilestone1Days={channelDefaultMilestone1Days}
                    channelDefaultMilestone2Days={channelDefaultMilestone2Days}
                    channelTierMode={channelTierMode}
                    tierDohOverride={tierDohOverride}
                    onDemandSourceChange={setDemandSource}
                    onTargetDohChange={setTargetDohDays}
                    onMilestone1Change={setMilestone1DaysOverride}
                    onMilestone2Change={setMilestone2DaysOverride}
                    onDrrLookbackChange={setDrrLookbackDays}
                    onDrrWindowChange={handleDrrWindowChange}
                    onDrrGrowthChange={setDrrGrowthPct}
                    onMinSourceStockUnitsChange={setMinSourceStockUnitsOverride}
                    onMaxOutboundUtilizationPctChange={
                        setMaxOutboundUtilizationPctOverride
                    }
                    onInboundInTransitDaysChange={
                        setInboundInTransitDaysOverride
                    }
                    onSourceReserveUnitsChange={setSourceReserveUnitsOverride}
                    onMinInternalDohDaysChange={setMinInternalDohDaysOverride}
                    onMaxUnitsPerShipmentChange={setMaxUnitsPerShipmentOverride}
                    onMaxMeasurementPerShipmentChange={
                        setMaxMeasurementPerShipmentOverride
                    }
                    onTierDohOverrideChange={handleTierDohOverrideChange}
                    onCompute={handleCompute}
                scheduleAlert={scheduleAlert}
                    computing={false}
                    isAggregated={effectiveIsAggregated}
                    planWarehouses={planWarehouses}
                    warehouseRules={warehouseRules}
                    selectedPlanWarehouseIds={selectedPlanWarehouseIds}
                    onWarehouseIdsChange={setSelectedPlanWarehouseIds}
                    availablePaths={availablePaths}
                    selectedPathIds={selectedPathIds}
                    onPathIdsChange={setSelectedPathIds}
                    pathsLoading={pathsLoading}
                    sourceWarehouseOptions={sourceWarehouseOptions}
                    selectedSourceWarehouseIds={selectedSourceWarehouseIds}
                    onSourceWarehouseIdsChange={setSelectedSourceWarehouseIds}
                    planWarehouseOptionsLoading={planWarehouseOptionsLoading}
                    planRuleOverrides={planRuleOverrides}
                    onPlanFlatOverrideChange={handlePlanFlatOverrideChange}
                    onPlanTierOverrideChange={handlePlanTierOverrideChange}
                    maxUnitsPerShipmentOverride={maxUnitsPerShipmentOverride}
                    maxMeasurementPerShipmentOverride={
                        maxMeasurementPerShipmentOverride
                    }
                    channelDefaultMaxUnitsPerShipment={
                        channelDefaultMaxUnitsPerShipment
                    }
                    channelDefaultMaxMeasurementPerShipment={
                        channelDefaultMaxMeasurementPerShipment
                    }
                    channelMeasurementUnit={channelMeasurementUnit}
                    layout="panel"
                />
            </Modal>

            {/* Header row 1 — page title + plan action buttons (in line with "Distribution") */}
            <PageHeader
                icon={<Package size={20} weight="duotone" />}
                title="Distribution"
                subtitle="Move stock from internal warehouses to channel warehouses to meet forward demand"
                actions={
                    <Group gap={8}>
                        {scheduleAlert && (
                            <Button
                                variant="subtle"
                                color="dark"
                                size="sm"
                                leftSection={<CalendarPlus size={15} />}
                                onClick={() => setHeaderScheduleOpen(true)}
                            >
                                Schedule
                            </Button>
                        )}
                        {!isReadonly && (
                            <Button
                                variant="subtle"
                                color="dark"
                                size="sm"
                                leftSection={<GearSix size={15} />}
                                onClick={openSettings}
                            >
                                Edit Settings
                            </Button>
                        )}
                        {!isReadonly && canUndo && (
                            <Button
                                size="sm"
                                variant="subtle"
                                color="dark"
                                leftSection={
                                    <ArrowCounterClockwise size={15} />
                                }
                                loading={undoing}
                                onClick={() => void undoEdit()}
                            >
                                Undo ({undoStackSize})
                            </Button>
                        )}
                        {!isReadonly && editQtyMap.size > 0 && (
                            <Tooltip
                                label="Recalculating edited rows…"
                                withArrow
                                disabled={!isAnyRowRecomputing}
                            >
                                {/* Wrapper so the tooltip still fires while the button is disabled. */}
                                <Box display="inline-flex">
                                    <Button
                                        size="sm"
                                        color="violet"
                                        variant="filled"
                                        loading={
                                            savingDraft || isAnyRowRecomputing
                                        }
                                        onClick={() => handleSaveDraftEdits()}
                                    >
                                        Save Draft
                                    </Button>
                                </Box>
                            </Tooltip>
                        )}
                        {!isReadonly && (
                            <Button
                                size="sm"
                                color="violet"
                                leftSection={<Checks size={15} />}
                                onClick={() => setApprovePlanOpen(true)}
                            >
                                Approve Plan
                            </Button>
                        )}
                        {plan?.status === 'APPROVED' &&
                            hasUnpushedShipments && (
                                <Button
                                    size="sm"
                                    color="teal"
                                    leftSection={<ShoppingCart size={15} />}
                                    onClick={() =>
                                        setCreateSaleOrdersOpen(true)
                                    }
                                >
                                    Push to WMS
                                </Button>
                            )}
                        {plan?.status === 'DRAFT' && (
                            <Button
                                variant="subtle"
                                color="red"
                                size="sm"
                                leftSection={<Trash size={15} />}
                                loading={deleting}
                                onClick={handleDelete}
                            >
                                Delete
                            </Button>
                        )}
                    </Group>
                }
            />

            {/* Header row 2 — breadcrumb (left) + compute timestamp / staleness warning (right),
                tucked directly under the title. The whole draft view is a frozen snapshot as of
                planDate (qty, in-transit, DoH, channel stock AND source availability); a persistent
                Recompute refreshes it, with an extra "stale" nudge once the date is in the past. */}
            <Group
                justify="space-between"
                align="center"
                style={{ flexShrink: 0 }}
                mt={-36}
                mb={-4}
            >
                <Group gap={8} wrap="nowrap">
                    <Button
                        variant="subtle"
                        color="dark"
                        size="xs"
                        leftSection={<ArrowLeft size={14} />}
                        onClick={handleBack}
                    >
                        {channelName}
                    </Button>
                    <Text
                        style={{ fontSize: 13, color: neutralColors.textMuted }}
                    >
                        /
                    </Text>
                    {editingName ? (
                        <TextInput
                            ref={nameInputRef}
                            value={nameValue}
                            onChange={(e) =>
                                setNameValue(e.currentTarget.value)
                            }
                            onBlur={commitNameEdit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                } else if (e.key === 'Escape') {
                                    setEditingName(false);
                                }
                            }}
                            size="xs"
                            style={{ width: 240 }}
                            styles={{
                                input: { fontSize: 14, fontWeight: 600 },
                            }}
                            autoFocus
                        />
                    ) : (
                        <Group gap={6} align="center">
                            <Text
                                style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: neutralColors.textPrimary,
                                }}
                            >
                                {plan?.name ??
                                    existingPlanName ??
                                    'Distribution Plan'}
                            </Text>
                            {plan && plan.status === 'DRAFT' && (
                                <ActionIcon
                                    variant="subtle"
                                    color="dark"
                                    size="xs"
                                    onClick={startEditingName}
                                >
                                    <PencilSimple size={12} />
                                </ActionIcon>
                            )}
                        </Group>
                    )}
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: statusColor,
                            backgroundColor: `${statusColor}18`,
                            borderRadius: 4,
                            padding: '2px 8px',
                        }}
                    >
                        {planStatus}
                    </span>
                    {plan?.demandSource === 'OPEN_PO' &&
                        plan?.sourcePurchaseOrderId && (
                            <Tooltip
                                label="This plan's demand is scoped to this purchase order"
                                withArrow
                            >
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: neutralColors.textSecondary,
                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                        borderRadius: 4,
                                        padding: '2px 8px',
                                    }}
                                >
                                    PO {plan.sourcePurchaseOrderId}
                                </span>
                            </Tooltip>
                        )}
                </Group>

                {plan && isReadonly && plan.approvedAt && (
                    <Text
                        style={{
                            fontSize: 12,
                            color: neutralColors.textMuted,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Approved{' '}
                        {new Date(plan.approvedAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                        {' · all figures are frozen as of approval'}
                    </Text>
                )}
                {plan && !isReadonly && (
                    <Group gap={10} align="center" wrap="nowrap">
                        <Text
                            style={{
                                fontSize: 12,
                                color: neutralColors.textMuted,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Computed{' '}
                            {new Date(plan.planDate).toLocaleDateString(
                                'en-IN',
                                {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                },
                            )}
                            {' · all figures are a snapshot as of this date'}
                        </Text>
                        {plan.planDate <
                            new Date().toISOString().slice(0, 10) && (
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: semanticColors.warning.primary,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    backgroundColor: `${semanticColors.warning.primary}14`,
                                    borderRadius: 6,
                                    padding: '2px 8px',
                                }}
                            >
                                Stock & demand have moved since — may be stale
                            </Text>
                        )}
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            color="orange"
                            leftSection={<ArrowCounterClockwise size={12} />}
                            onClick={() => void handleCompute()}
                        >
                            Recompute
                        </Button>
                        {fromAlertId && (
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                color="gray"
                                component="a"
                                href={`/alert/${fromAlertId}`}
                            >
                                Opened from a Home card · view it
                            </Button>
                        )}
                    </Group>
                )}
            </Group>

            {/* Hidden CSV upload input — triggered by bulk upload modal */}
            <input
                ref={uploadInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleUploadCsv}
            />

            {/* Bulk quantity override modal */}
            <Modal
                opened={bulkUploadOpen}
                onClose={() => {
                    setBulkUploadOpen(false);
                    setBulkUploadPhase('idle');
                }}
                title={
                    <Text style={{ fontSize: 15, fontWeight: 700 }}>
                        Bulk Override Plan Quantities
                    </Text>
                }
                size="md"
                centered
                radius="md"
            >
                {bulkUploadPhase === 'idle' ? (
                    <Stack gap={16}>
                        <Text
                            style={{
                                fontSize: 13,
                                color: neutralColors.textSecondary,
                            }}
                        >
                            Edit the <strong>planQuantity</strong> column and
                            re-upload. SKUs already in the plan are updated;
                            other SKUs in this store are added as new lines.
                            Overrides apply to the{' '}
                            <strong>currently selected store</strong>. All other
                            columns are ignored.
                        </Text>
                        <div>
                            <Text
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: neutralColors.textMuted,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    marginBottom: 8,
                                }}
                            >
                                Key column
                            </Text>
                            <Stack gap={6}>
                                {(
                                    [
                                        [
                                            'productId',
                                            'Used to match / add SKUs in this store — do not edit',
                                        ],
                                        [
                                            'planQuantity',
                                            'Edit this — becomes the new plan quantity',
                                        ],
                                    ] as const
                                ).map(([col, note]) => (
                                    <Group key={col} gap={8} align="flex-start">
                                        <Text
                                            style={{
                                                fontSize: 12,
                                                fontFamily: 'monospace',
                                                color: 'var(--mantine-color-violet-6)',
                                                width: 140,
                                                flexShrink: 0,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {col}
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: 12,
                                                color: neutralColors.textMuted,
                                            }}
                                        >
                                            {note}
                                        </Text>
                                    </Group>
                                ))}
                            </Stack>
                        </div>
                        <Button
                            variant="light"
                            color="violet"
                            size="sm"
                            leftSection={<DownloadSimple size={14} />}
                            onClick={handleDownloadTemplate}
                        >
                            Download current plan as template
                        </Button>
                        <Button
                            variant="outline"
                            color="gray"
                            size="sm"
                            leftSection={<UploadSimple size={14} />}
                            fullWidth
                            onClick={() => uploadInputRef.current?.click()}
                        >
                            Choose CSV file to upload
                        </Button>
                    </Stack>
                ) : (
                    <Stack gap={12} py="md">
                        <Group justify="center" gap={8}>
                            <CheckCircle
                                size={32}
                                color="var(--mantine-color-green-6)"
                            />
                            <Text
                                style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: neutralColors.textPrimary,
                                }}
                            >
                                {(bulkUploadResult?.updated ?? 0) +
                                    (bulkUploadResult?.added ?? 0)}{' '}
                                of{' '}
                                {(bulkUploadResult?.updated ?? 0) +
                                    (bulkUploadResult?.added ?? 0) +
                                    (bulkUploadResult?.skipped.length ??
                                        0)}{' '}
                                rows applied
                            </Text>
                        </Group>
                        <Group justify="center" gap={16}>
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: neutralColors.textSecondary,
                                }}
                            >
                                {bulkUploadResult?.updated ?? 0} updated
                            </Text>
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: neutralColors.textSecondary,
                                }}
                            >
                                {bulkUploadResult?.added ?? 0} added
                            </Text>
                            <Text
                                style={{
                                    fontSize: 12,
                                    color:
                                        (bulkUploadResult?.skipped.length ??
                                            0) > 0
                                            ? 'var(--mantine-color-orange-7)'
                                            : neutralColors.textMuted,
                                }}
                            >
                                {bulkUploadResult?.skipped.length ?? 0} skipped
                            </Text>
                        </Group>
                        {bulkUploadResult &&
                            bulkUploadResult.duplicates.length > 0 && (
                                <Text
                                    style={{
                                        fontSize: 11,
                                        color: neutralColors.textMuted,
                                        textAlign: 'center',
                                    }}
                                >
                                    {bulkUploadResult.duplicates.length}{' '}
                                    duplicate productId
                                    {bulkUploadResult.duplicates.length !== 1
                                        ? 's'
                                        : ''}{' '}
                                    in the file — kept the last value.
                                </Text>
                            )}
                        {bulkUploadResult &&
                            bulkUploadResult.skipped.length > 0 && (
                                <div
                                    style={{
                                        maxHeight: 160,
                                        overflowY: 'auto',
                                        border: '1px solid var(--mantine-color-gray-3)',
                                        borderRadius: 6,
                                        padding: 8,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: neutralColors.textMuted,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            marginBottom: 6,
                                        }}
                                    >
                                        Skipped rows
                                    </Text>
                                    <Stack gap={3}>
                                        {bulkUploadResult.skipped.map((s) => (
                                            <Group
                                                key={s.productId}
                                                gap={8}
                                                wrap="nowrap"
                                                justify="space-between"
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        fontFamily: 'monospace',
                                                        color: neutralColors.textSecondary,
                                                    }}
                                                >
                                                    {s.productId}
                                                </Text>
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: neutralColors.textMuted,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {s.reason}
                                                </Text>
                                            </Group>
                                        ))}
                                    </Stack>
                                </div>
                            )}
                        <Text
                            style={{
                                fontSize: 12,
                                color: neutralColors.textMuted,
                                textAlign: 'center',
                            }}
                        >
                            Quantities are saved locally. Use{' '}
                            <strong>Save Draft</strong> to persist, or{' '}
                            <strong>Save and Approve</strong> to finalise.
                        </Text>
                        <Group gap={8} mt={4} justify="center">
                            <Button
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={() => setBulkUploadPhase('idle')}
                            >
                                Upload another file
                            </Button>
                            <Button
                                color="violet"
                                size="sm"
                                onClick={() => {
                                    setBulkUploadOpen(false);
                                    setBulkUploadPhase('idle');
                                }}
                            >
                                Done
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>

            {/* Download column picker */}
            <Modal
                opened={downloadModalOpen}
                onClose={closeDownloadModal}
                title={
                    <Text style={{ fontSize: 15, fontWeight: 700 }}>
                        Choose columns to download
                    </Text>
                }
                size="md"
                centered
                radius="md"
            >
                <Stack gap={14}>
                    <Group justify="space-between" align="center">
                        <Text
                            style={{
                                fontSize: 12,
                                color: neutralColors.textMuted,
                            }}
                        >
                            {
                                PLAN_EXPORT_COLS.filter(
                                    (c) =>
                                        c.locked || draftExportCols.has(c.key),
                                ).length
                            }{' '}
                            of {PLAN_EXPORT_COLS.length} columns selected
                        </Text>
                        <Group gap={4}>
                            <Button
                                variant="subtle"
                                size="compact-xs"
                                color="gray"
                                onClick={() =>
                                    setDraftExportCols(
                                        new Set(
                                            PLAN_EXPORT_COLS.map((c) => c.key),
                                        ),
                                    )
                                }
                            >
                                Select all
                            </Button>
                            <Button
                                variant="subtle"
                                size="compact-xs"
                                color="gray"
                                onClick={() =>
                                    setDraftExportCols(
                                        new Set(
                                            PLAN_EXPORT_COLS.filter(
                                                (c) => c.locked,
                                            ).map((c) => c.key),
                                        ),
                                    )
                                }
                            >
                                Clear
                            </Button>
                        </Group>
                    </Group>

                    <Stack gap={8}>
                        {PLAN_EXPORT_COLS.map((col) => (
                            <Checkbox
                                key={col.key}
                                size="xs"
                                label={
                                    col.locked ? (
                                        <Group
                                            gap={6}
                                            align="center"
                                            wrap="nowrap"
                                        >
                                            <Text style={{ fontSize: 13 }}>
                                                {col.label}
                                            </Text>
                                            <Text
                                                style={{
                                                    fontSize: 11,
                                                    color: neutralColors.textMuted,
                                                }}
                                            >
                                                always included
                                            </Text>
                                        </Group>
                                    ) : (
                                        <Text style={{ fontSize: 13 }}>
                                            {col.label}
                                        </Text>
                                    )
                                }
                                checked={
                                    col.locked || draftExportCols.has(col.key)
                                }
                                disabled={col.locked}
                                onChange={() => toggleExportCol(col.key)}
                            />
                        ))}
                    </Stack>

                    <Text
                        style={{ fontSize: 11, color: neutralColors.textMuted }}
                    >
                        Product ID and Plan Quantity are always included so the
                        file can be re-uploaded.
                    </Text>

                    <Group justify="flex-end" gap={8}>
                        <Button
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={closeDownloadModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="violet"
                            size="sm"
                            leftSection={<DownloadSimple size={14} />}
                            onClick={handleConfirmDownload}
                        >
                            Download CSV
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Capacity info bar — DRAFT only: these are drafting decision aids (channel stock,
                headroom, per-category capacity). Approved plans show the SO/shipment status panel below instead. */}
            {plan &&
                !plan.isAggregated &&
                selectedWarehouseId &&
                planStatus === 'DRAFT' &&
                (() => {
                    const maxCapacity =
                        plan.warehouseCapacities?.[selectedWarehouseId]
                            ?.maxCapacity ?? null;
                    const totalWHStock = Array.from(
                        skuContextMap.values(),
                    ).reduce((s, sku) => s + sku.channelStock, 0);
                    const totalPlanQty = warehouseFilteredTransfers
                        .filter((t) => !t.isExcluded)
                        .reduce(
                            (s, t) =>
                                s +
                                (editQtyMap.get(t.id) ??
                                    t.approvedQty ??
                                    t.recommendedQty),
                            0,
                        );
                    const projected = totalWHStock + totalPlanQty;
                    const delta =
                        maxCapacity != null ? maxCapacity - projected : null;
                    const isOver = delta != null && delta < 0;
                    return (
                        <div
                            style={{
                                flexShrink: 0,
                                display: 'flex',
                                gap: 12,
                                alignItems: 'stretch',
                                flexWrap: 'wrap',
                            }}
                        >
                            <div
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    backgroundColor: isOver
                                        ? 'rgba(220,38,38,0.03)'
                                        : 'rgba(0,0,0,0.02)',
                                    border: `1px solid ${isOver ? 'rgba(220,38,38,0.12)' : 'rgba(0,0,0,0.06)'}`,
                                }}
                            >
                                <Group
                                    gap={28}
                                    wrap="nowrap"
                                    align="flex-start"
                                >
                                    <div>
                                        <Text
                                            style={{
                                                fontSize: 11,
                                                color: neutralColors.textMuted,
                                            }}
                                        >
                                            Channel Stock
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: neutralColors.textSecondary,
                                            }}
                                        >
                                            {formatUnits(
                                                Math.ceil(totalWHStock),
                                            )}
                                        </Text>
                                    </div>
                                    {plan.warehouseSales7d?.[
                                        selectedWarehouseId
                                    ] && (
                                        <Tooltip
                                            label={`Units sold at this store from ${plan.salesWindow?.start} to ${plan.salesWindow?.end}`}
                                            withArrow
                                        >
                                            <div>
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: neutralColors.textMuted,
                                                    }}
                                                >
                                                    Last 7d Sales
                                                </Text>
                                                <Text
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: neutralColors.textSecondary,
                                                    }}
                                                >
                                                    {formatUnits(
                                                        Math.ceil(
                                                            plan
                                                                .warehouseSales7d[
                                                                selectedWarehouseId
                                                            ].units,
                                                        ),
                                                    )}
                                                </Text>
                                            </div>
                                        </Tooltip>
                                    )}
                                    <div>
                                        <Text
                                            style={{
                                                fontSize: 11,
                                                color: neutralColors.textMuted,
                                            }}
                                        >
                                            Plan Qty
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: neutralColors.textPrimary,
                                            }}
                                        >
                                            {formatUnits(
                                                Math.ceil(totalPlanQty),
                                            )}
                                        </Text>
                                    </div>
                                    {maxCapacity != null && (
                                        <>
                                            <div>
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: neutralColors.textMuted,
                                                    }}
                                                >
                                                    Max Capacity
                                                </Text>
                                                <Text
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: neutralColors.textSecondary,
                                                    }}
                                                >
                                                    {formatUnits(maxCapacity)}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: neutralColors.textMuted,
                                                    }}
                                                >
                                                    {isOver
                                                        ? 'Over by'
                                                        : 'Headroom'}
                                                </Text>
                                                <Text
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        color: isOver
                                                            ? semanticColors
                                                                  .critical
                                                                  .primary
                                                            : semanticColors
                                                                  .positive
                                                                  .primary,
                                                    }}
                                                >
                                                    {formatUnits(
                                                        Math.abs(
                                                            Math.ceil(delta!),
                                                        ),
                                                    )}
                                                </Text>
                                            </div>
                                        </>
                                    )}
                                </Group>
                                {statusBanners && (
                                    <div style={{ marginTop: 10 }}>
                                        {statusBanners}
                                    </div>
                                )}
                            </div>
                            {categoryCapacityRows.length > 0 && (
                                <div
                                    style={{
                                        flex: '1 1 380px',
                                        minWidth: 320,
                                        borderRadius: 8,
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <DataSheet
                                        data={categoryCapacityRows}
                                        columns={categoryCapColumns}
                                        getRowKey={(r) => r.bucket}
                                        rowHeight={30}
                                        bare
                                        hideHeaderBar
                                    />
                                </div>
                            )}
                        </div>
                    );
                })()}

            {/* Approved view: the plan is decided, so we drop the planning tables and instead show
                what matters now — the status of each sale order / shipment (live from the WMS). */}
            {plan && planStatus !== 'DRAFT' && shipments.length > 0 && (
                <div
                    style={{
                        flexShrink: 0,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                    }}
                >
                    <Group
                        justify="space-between"
                        wrap="nowrap"
                        style={{
                            padding: '6px 12px',
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            backgroundColor: 'rgba(0,0,0,0.02)',
                        }}
                    >
                        <Text
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: neutralColors.textPrimary,
                            }}
                        >
                            Sale Orders & Shipments
                        </Text>
                        <Text
                            style={{
                                fontSize: 11,
                                color: neutralColors.textMuted,
                            }}
                        >
                            {shipments.length} shipment
                            {shipments.length !== 1 ? 's' : ''}
                        </Text>
                    </Group>
                    <Stack gap={0}>
                        {shipments.map((s, i) => (
                            <Group
                                key={s.saleOrderCode}
                                justify="space-between"
                                wrap="nowrap"
                                style={{
                                    padding: '6px 12px',
                                    borderTop:
                                        i > 0
                                            ? '1px solid rgba(0,0,0,0.04)'
                                            : undefined,
                                }}
                            >
                                <Group
                                    gap={10}
                                    wrap="nowrap"
                                    style={{ minWidth: 0 }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            color: neutralColors.textPrimary,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {s.wmsExternalId ?? s.saleOrderCode}
                                    </Text>
                                    {s.toWarehouseName && (
                                        <Text
                                            style={{
                                                fontSize: 11,
                                                color: neutralColors.textMuted,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            → {s.toWarehouseName}
                                        </Text>
                                    )}
                                </Group>
                                <Group gap={14} wrap="nowrap">
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: neutralColors.textSecondary,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {formatUnits(Math.ceil(s.totalQty))} u
                                    </Text>
                                    <ShipmentStatusBadge
                                        wmsStatus={s.wmsStatus}
                                        saleOrderStatus={s.saleOrderStatus}
                                        saleOrderIsLive={s.saleOrderIsLive}
                                        isActive={false}
                                    />
                                </Group>
                            </Group>
                        ))}
                    </Stack>
                </div>
            )}

            {/* Status banners fall back to a standalone row only when no capacity pill is shown
                (aggregated plans / no store selected / approved plans). Otherwise they live inside the pill's left column. */}
            {!(
                plan &&
                !plan.isAggregated &&
                selectedWarehouseId &&
                planStatus === 'DRAFT'
            ) &&
                statusBanners && (
                    <div style={{ flexShrink: 0 }}>{statusBanners}</div>
                )}

            {/* Unified table — recommended, available, unsendable rows in one place */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <DataSheet
                    data={sortedSkuRouteRows}
                    columns={columns}
                    getRowKey={(row) => row.rowKey}
                    rowHeight={44}
                    aggregateRow={aggregateRow}
                    enableAiView
                    onFilteredRowsChange={setDsFilteredRows}
                    getRowClassName={(row) => {
                        const stateClass =
                            row.rowState === 'recommended'
                                ? styles.stateRecommended
                                : row.rowState === 'unsendable'
                                  ? styles.stateUnsendable
                                  : styles.stateAvailable;
                        const highlight =
                            row.productId === lastAddedProductId
                                ? styles.addedRow
                                : '';
                        return [stateClass, highlight]
                            .filter(Boolean)
                            .join(' ');
                    }}
                    title={
                        showWarehouseFilter ? (
                            <Group gap={8} align="center" wrap="nowrap">
                                <Text
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: neutralColors.textPrimary,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    SKUs
                                </Text>
                                <Select
                                    size="xs"
                                    placeholder="All FCs"
                                    clearable
                                    data={destinationWarehouses}
                                    value={selectedWarehouseId}
                                    onChange={handleWarehouseChange}
                                    style={{ width: 200 }}
                                />
                            </Group>
                        ) : selectedWarehouseName ? (
                            `SKUs — ${selectedWarehouseName}`
                        ) : (
                            'SKUs'
                        )
                    }
                    subtitle={
                        isReadonly
                            ? `${skuRouteRows.length} SKU${skuRouteRows.length !== 1 ? 's' : ''}`
                            : `${skuRouteRows.length} SKU${skuRouteRows.length !== 1 ? 's' : ''} · ${unifiedCounts.recommended} recommended · ${unifiedCounts.available} available${unifiedCounts.unsendable > 0 ? ` · ${unifiedCounts.unsendable} cannot send` : ''}`
                    }
                    enableSearch
                    searchPlaceholder="Search by SKU or name…"
                    filters={planFilters}
                    toolbarActionsRight={
                        <Group gap={6} wrap="nowrap">
                            {pastSalesColVisible && (
                                <Group gap={4} wrap="nowrap">
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            color: neutralColors.textMuted,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Past sales
                                    </Text>
                                    <Tooltip
                                        label="Lookback window (days) for the Past Sales column. Reference only — changing it does not recompute the plan."
                                        withArrow
                                    >
                                        <NumberInput
                                            size="xs"
                                            value={pastSalesLookbackDays}
                                            onChange={(v) => {
                                                const n = Math.floor(Number(v));
                                                if (
                                                    Number.isFinite(n) &&
                                                    n >= 1
                                                )
                                                    setPastSalesLookbackDays(
                                                        Math.min(365, n),
                                                    );
                                            }}
                                            min={1}
                                            max={365}
                                            hideControls
                                            style={{ width: 60 }}
                                        />
                                    </Tooltip>
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            color: neutralColors.textMuted,
                                        }}
                                    >
                                        d
                                    </Text>
                                </Group>
                            )}
                            {shipments.length > 0 && (
                                <Select
                                    size="xs"
                                    placeholder="All shipments"
                                    clearable
                                    data={shipments.map((s) => ({
                                        value: s.saleOrderCode,
                                        label: `${s.wmsStatus === 'CREATED' ? '● ' : s.wmsStatus === 'FAILED' ? '◉ ' : '○ '}${s.saleOrderName ?? s.saleOrderCode}`,
                                    }))}
                                    value={selectedShipmentCode}
                                    onChange={setSelectedShipmentCode}
                                    style={{ minWidth: 180 }}
                                    comboboxProps={{ withinPortal: true }}
                                />
                            )}
                            {plan?.demandSource === 'OPEN_PO' &&
                                plan?.sourcePurchaseOrderId && (
                                    <Checkbox
                                        size="xs"
                                        label={`PO ${plan.sourcePurchaseOrderId} only`}
                                        checked={poRowsOnly}
                                        onChange={(e) =>
                                            setPoRowsOnly(
                                                e.currentTarget.checked,
                                            )
                                        }
                                    />
                                )}
                            {!isReadonly && (
                                <MultiSelect
                                    size="xs"
                                    placeholder="All types"
                                    data={[
                                        {
                                            value: 'recommended',
                                            label: 'Recommended',
                                        },
                                        {
                                            value: 'unsendable',
                                            label: 'Cannot send',
                                        },
                                        {
                                            value: 'available',
                                            label: 'Available',
                                        },
                                    ]}
                                    value={stateFilters}
                                    onChange={(v) =>
                                        setStateFilters(v as UnifiedRowState[])
                                    }
                                    style={{ minWidth: 160 }}
                                    clearable
                                />
                            )}
                            {!isReadonly && (
                                <Tooltip
                                    label="Bulk upload CSV overrides"
                                    withArrow
                                >
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        onClick={() => {
                                            setBulkUploadOpen(true);
                                            setBulkUploadPhase('idle');
                                        }}
                                    >
                                        <UploadSimple size={15} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            {!isReadonly && (
                                <Menu
                                    shadow="md"
                                    width={260}
                                    position="bottom-end"
                                    withinPortal
                                >
                                    <Menu.Target>
                                        <Tooltip
                                            label="Row actions (select rows first)"
                                            withArrow
                                        >
                                            <ActionIcon
                                                variant="subtle"
                                                color="gray"
                                                size="sm"
                                                aria-label="Row actions"
                                            >
                                                <DotsThreeVertical
                                                    size={16}
                                                    weight="bold"
                                                />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Label>
                                            {selectedActionRows.length > 0
                                                ? `${selectedActionRows.length} row${selectedActionRows.length === 1 ? '' : 's'} selected`
                                                : 'Select rows to act on'}
                                        </Menu.Label>
                                        <Menu.Item
                                            leftSection={<Equals size={14} />}
                                            disabled={
                                                selectedActionRows.length === 0
                                            }
                                            onClick={
                                                handleOverrideSelectedWithDemand
                                            }
                                        >
                                            <Text style={{ fontSize: 13 }}>
                                                Set quantity = demand
                                            </Text>
                                            <Text
                                                style={{
                                                    fontSize: 11,
                                                    color: neutralColors.textMuted,
                                                }}
                                            >
                                                {plan?.demandSource === 'OPEN_PO'
                                                    ? 'Use the PO quantity, ignoring stock netting. Plans available rows too.'
                                                    : `Use the ${demandSourceLabel.toLowerCase()} demand, ignoring stock netting. Plans available rows too.`}
                                            </Text>
                                        </Menu.Item>
                                        <Menu.Item
                                            leftSection={
                                                <ArrowCounterClockwise size={14} />
                                            }
                                            disabled={
                                                selectedActionRows.length === 0
                                            }
                                            onClick={
                                                handleRevertSelectedToSuggested
                                            }
                                        >
                                            <Text style={{ fontSize: 13 }}>
                                                Revert to suggested
                                            </Text>
                                            <Text
                                                style={{
                                                    fontSize: 11,
                                                    color: neutralColors.textMuted,
                                                }}
                                            >
                                                Drop unsaved edits on the selected rows
                                            </Text>
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            )}
                            {plan?.status === 'APPROVED' && emailEnabled && (
                                <Tooltip label="Email this plan" withArrow>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        onClick={() => {
                                            {
                                                const today = new Date()
                                                    .toISOString()
                                                    .slice(0, 10);
                                                setEmailDispatchDate(today);
                                                setEmailReceivingDate(today);
                                            }
                                            setEmailCombined(false);
                                            setSendPlanOpen(true);
                                        }}
                                        aria-label="Email this plan"
                                    >
                                        <EnvelopeSimple size={15} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            <PlanColumnToggle
                                hidden={hiddenPlanCols}
                                onChange={setHiddenPlanCols}
                                isPoPlan={isPoPlan}
                            />
                        </Group>
                    }
                    enableDownload
                    onDownload={handleOpenDownloadModal}
                    pageSizeOptions={[20, 50, 100]}
                    defaultPageSize={50}
                    emptyState={
                        <Stack align="center" gap={8} p="xl">
                            <Package
                                size={32}
                                color={neutralColors.textMuted}
                            />
                            <Text
                                style={{
                                    fontSize: 13,
                                    color: neutralColors.textMuted,
                                }}
                            >
                                No SKUs in this channel's universe.
                            </Text>
                        </Stack>
                    }
                />
            </div>

            {/* Approve Plan modal */}
            {plan && (
                <ApprovePlanModal
                    plan={plan}
                    opened={approvePlanOpen}
                    onClose={() => setApprovePlanOpen(false)}
                    onApproved={handlePlanApproved}
                    selectedTransferIds={
                        selectedPersistedTransferIds.size > 0
                            ? selectedPersistedTransferIds
                            : undefined
                    }
                />
            )}

            {/* Create Sale Orders modal — for approved plans without sale orders */}
            {plan && (
                <CreateSaleOrdersModal
                    planId={plan.id}
                    channelKey={plan.channelAbbreviationKey}
                    opened={createSaleOrdersOpen}
                    onClose={() => setCreateSaleOrdersOpen(false)}
                    onCreated={() => {
                        getPlanShipments(plan.id)
                            .then(setShipments)
                            .catch(() => {});
                    }}
                />
            )}

            {/* Email approved plan modal — one templated email per shipment */}
            <Modal
                opened={sendPlanOpen}
                onClose={() => setSendPlanOpen(false)}
                title="Email this plan"
                size="xl"
                centered
            >
                <Stack gap="md">
                    <Group gap="md" align="flex-end">
                        <TextInput
                            type="date"
                            label="Warehouse dispatch date"
                            value={emailDispatchDate}
                            onChange={(e) =>
                                setEmailDispatchDate(e.currentTarget.value)
                            }
                            w={220}
                        />
                        <TextInput
                            type="date"
                            label="Store receiving date"
                            value={emailReceivingDate}
                            onChange={(e) =>
                                setEmailReceivingDate(e.currentTarget.value)
                            }
                            w={220}
                        />
                    </Group>
                    <Text size="xs" c="dimmed">
                        Used in every email below. Changing a date regenerates
                        the drafts and resets edits.
                    </Text>

                    {shipmentCountForEmail > 1 && (
                        <div>
                            <Switch
                                label="Collapse into a single email"
                                description={`This plan has ${shipmentCountForEmail} sale orders. Collapsing sends one email covering all of them — To/CC are the union of every shipment's recipients, and each sale order still gets its own CSV attached.`}
                                checked={emailCombined}
                                onChange={(e) =>
                                    setEmailCombined(e.currentTarget.checked)
                                }
                            />
                        </div>
                    )}

                    {emailPreviewLoading && <Loader size="sm" />}
                    {emailPreviewError && (
                        <Text size="sm" c="red">
                            {emailPreviewError}
                        </Text>
                    )}
                    {!emailPreviewLoading &&
                        !emailPreviewError &&
                        emailDrafts.length === 0 && (
                            <Text size="sm" c="dimmed">
                                No shipments to email yet. Push the plan to the
                                WMS first.
                            </Text>
                        )}

                    {emailDrafts.map((d, i) => {
                        const updateDraft = (patch: Partial<EmailDraft>) =>
                            setEmailDrafts((prev) =>
                                prev.map((x, idx) =>
                                    idx === i ? { ...x, ...patch } : x,
                                ),
                            );
                        const sendOne = async () => {
                            if (!plan || d.to.length === 0) return;
                            updateDraft({ sending: true });
                            try {
                                await sendPlanEmail(plan.id, {
                                    saleOrderCodes: d.saleOrderCodes,
                                    to: d.to,
                                    cc: d.cc,
                                    subject: d.subject,
                                    body: d.body,
                                });
                                updateDraft({ sending: false, sent: true });
                                showSuccess(
                                    `Email sent for ${d.orderId || d.destinationWarehouseName || d.saleOrderCode}.`,
                                );
                            } catch (e: any) {
                                updateDraft({ sending: false });
                                showError(
                                    e?.error?.message ||
                                        e?.message ||
                                        'Could not send the email.',
                                );
                            }
                        };
                        const meta = [
                            d.sourceWarehouseName,
                            d.orderId ? `Order ${d.orderId}` : null,
                            `${d.skuCount} SKUs`,
                            `Qty ${d.qty}`,
                            d.measurement || null,
                        ]
                            .filter(Boolean)
                            .join(' · ');
                        const title =
                            d.saleOrderCodes.length > 1
                                ? `${d.saleOrderCodes.length} shipments — ${d.destinationWarehouseName ?? 'all destinations'}`
                                : (d.destinationWarehouseName ??
                                  d.saleOrderCode);
                        return (
                            <Paper
                                key={d.saleOrderCodes.join('|')}
                                withBorder
                                p="md"
                                radius="md"
                            >
                                <Group justify="space-between" mb="xs">
                                    <div>
                                        <Text fw={600} size="sm">
                                            {title}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                            {meta}
                                        </Text>
                                    </div>
                                    {d.sent && (
                                        <Badge color="green" variant="light">
                                            Sent
                                        </Badge>
                                    )}
                                </Group>
                                <Stack gap="xs">
                                    <TagsInput
                                        label="To"
                                        value={d.to}
                                        onChange={(v) => updateDraft({ to: v })}
                                        placeholder="name@company.com"
                                    />
                                    <TagsInput
                                        label="CC"
                                        value={d.cc}
                                        onChange={(v) => updateDraft({ cc: v })}
                                        placeholder="name@company.com"
                                    />
                                    <TextInput
                                        label="Subject"
                                        value={d.subject}
                                        onChange={(e) =>
                                            updateDraft({
                                                subject: e.currentTarget.value,
                                            })
                                        }
                                    />
                                    <Textarea
                                        label="Body"
                                        autosize
                                        minRows={6}
                                        value={d.body}
                                        onChange={(e) =>
                                            updateDraft({
                                                body: e.currentTarget.value,
                                            })
                                        }
                                    />
                                    <Group justify="flex-end">
                                        <Button
                                            size="xs"
                                            onClick={sendOne}
                                            loading={d.sending}
                                            disabled={
                                                d.to.length === 0 || d.sent
                                            }
                                        >
                                            {d.sent ? 'Sent' : 'Send'}
                                        </Button>
                                    </Group>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            </Modal>
        </div>
    );
};

export default DistributionPlanView;
